import { useState, useEffect, useRef, createContext, useContext } from "react";
import { usePrerenderReady } from "./utils/prerenderReady";
import { navigateTo } from "./utils/navigate";
import { getDefaultMode } from "./theme/tokens";
import GCardMobile from "./components/cards/GCardMobile";
import SliderNav from "./components/ui/SliderNav";
import { fetchListingBySlug, fetchListingById, fetchListingForPreview, fetchSimilarVenues } from './services/listings';
import { trackTasteSignal } from './services/auraClient';
import { fetchApprovedReviews } from './services/reviewService';
import { fetchEditsMentioningListing } from './services/relatedContentService';
import { listingProfileUrl } from './utils/listingUrl';
import { socialProfileUrl } from './utils/socialUrl';
import { useAdminAuth } from "./context/AdminAuthContext";
import { buildCardImgs, mapMediaItemToGalleryPhoto, buildVenueVideos } from './utils/mediaMappers';
import ReviewSubmitForm from './components/reviews/ReviewSubmitForm';
import VenueEnquiryForm from './components/enquiry/VenueEnquiryForm';
import LuxeEnquiryModal from './components/enquiry/LuxeEnquiryModal';
import PublicationModule from './components/publication/PublicationModule';
import SeoHead from './components/seo/SeoHead';
import JsonLd from './components/seo/JsonLd';
import { buildVenueSchema, buildBreadcrumbSchema, buildFaqSchema } from './utils/structuredData';
import { imgSrc } from "./utils/transformListing";
import { imgUrl } from "./utils/imgUrl";

// Listing pages were rendering RAW /object/public/ originals (18 per page), full
// size, no WebP. Route every listing image through the transform. resize:'contain'
// scales proportionally without cropping, so the delivered frame is visually
// identical to the original; CSS object-fit still does the cropping. Returns
// undefined for a missing src so the attribute is omitted rather than set to "".
// Breadcrumb crumb names. "north-yorkshire" -> "North Yorkshire".
//
// Applied to the STORED value too, not just the slug fallback: Leeds Castle stores
// country as "england", lowercase, so the trail read "Home > england > Kent". Only
// touches strings that contain no uppercase at all, so genuine display values are
// left alone ("USA", "United Arab Emirates", "Provence-Alpes-Cote d'Azur").
const titleCaseSlug = (raw) => {
  const v = raw ? String(raw).trim() : '';
  if (!v) return '';
  if (/[A-Z]/.test(v)) return v;                 // already a display string
  return v.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const vpImg = (src, width = 1200) =>
  (src ? imgUrl(src, { width, quality: 75, resize: "contain" }) : undefined);

import HomeNav from "./components/nav/HomeNav";
import { useAura } from "./stores/auraCore";
import { createLead } from "./services/leadEngineService";
import ExternalLinkModal from "./components/ExternalLinkModal";
import { trackExternalClick, hasSeenModalThisSession, markModalSeen } from "./services/outboundClickService";
import { trackProfileView, trackCompareAdd, trackCompareRemove, trackCompareView, trackComparePair, trackEvent } from "./services/userEventService";
import { setListingContext, clearListingContext } from "./lib/tracker";
import { fetchUpcomingEventsForVenue, formatEventDate, formatEventTime } from './services/eventService';
import ReviewsSection from './components/reviews/ReviewsSection';
import VideoPlayModal from './components/video/VideoPlayModal';
import EventDrawer from './components/EventDrawer';
import { ShowcaseAtAGlance, ShowcasePricing, ShowcaseVerified } from './components/showcase';
import { fetchVenueIntelligence } from './services/venueIntelligenceService';
import { getQualityTier } from './services/listings';
import TierBadge from './components/editorial/TierBadge';
import ApprovalIndicators from './components/editorial/ApprovalIndicators';
import FreshnessText from './components/editorial/FreshnessText';
import { safeHtml } from './utils/safeHtml.js';
import { splitDescriptionBlocks } from './utils/proseHtml';
import { isPubliclyRoutable } from './utils/publicRoutability.js';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://luxuryweddingdirectory.com';

// Ensures external URLs always have a protocol, without doubling it up
// e.g. "auberge.com/..." → "https://auberge.com/..."
//      "https://auberge.com/..." → "https://auberge.com/..." (unchanged)
const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

// Parse a YouTube or Vimeo URL into the { type, heroId } shape that
// HeroVideo expects. Returns null for unrecognised URLs.
const parseHeroVideoUrl = (url) => {
  if (!url) return null;
  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (ytMatch) return { type: 'youtube', heroId: ytMatch[1] };
  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  const vmMatch = url.match(
    /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/
  );
  if (vmMatch) return { type: 'vimeo', heroId: vmMatch[1] };
  return null;
};

// When set (e.g. the client's mobile preview), forces useIsMobile regardless of
// the real window width, so the mobile layout renders inside a narrow container.
export const ForcedViewportContext = createContext(null);
function useIsMobile(bp = 768) {
  const forced = useContext(ForcedViewportContext);
  const [mobile, setMobile] = useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setMobile(e.matches);
    mql.addEventListener("change", fn);
    return () => mql.removeEventListener("change", fn);
  }, [bp]);
  return forced != null ? forced : mobile;
}

// ─── DESIGN SYSTEM ────────────────────────────────────────────────────────────
const LIGHT = {
  bg:         "#ffffff",
  bgAlt:      "#f7f7f5",
  surface:    "#ffffff",
  border:     "#ebebeb",
  border2:    "#d8d8d8",
  gold:       "#9d873e",
  goldLight:  "rgba(157,135,62,0.07)",
  goldBorder: "rgba(157,135,62,0.2)",
  green:      "#748172",
  greenLight: "rgba(116,129,114,0.07)",
  text:       "#1a1a18",
  textMid:    "#4a4844",
  textLight:  "#6b6560",
  textMuted:  "#9c9690",
  navBg:      "rgba(255,255,255,0.96)",
  shadow:     "0 2px 16px rgba(0,0,0,0.05)",
  shadowMd:   "0 8px 40px rgba(0,0,0,0.08)",
  shadowLg:   "0 24px 64px rgba(0,0,0,0.1)",
};
const DARK = {
  bg:         "#0f0f0d",
  bgAlt:      "#141412",
  surface:    "#1a1a18",
  border:     "#2e2e2a",
  border2:    "#3c3c38",
  gold:       "#b8a05a",
  goldLight:  "rgba(184,160,90,0.1)",
  goldBorder: "rgba(184,160,90,0.25)",
  green:      "#8fa08c",
  greenLight: "rgba(143,160,140,0.1)",
  text:       "#f5f2ec",
  textMid:    "#c8c4bc",
  textLight:  "#9c9890",
  textMuted:  "#6b6860",
  navBg:      "rgba(15,15,13,0.94)",
  shadow:     "0 2px 16px rgba(0,0,0,0.4)",
  shadowMd:   "0 8px 40px rgba(0,0,0,0.5)",
  shadowLg:   "0 24px 64px rgba(0,0,0,0.6)",
};
const Theme = createContext(LIGHT);
const useT = () => useContext(Theme);
// Font stacks, resolved via CSS custom properties set by ThemeLoader
const FD = "var(--font-heading-primary)"; // display
const FB = "var(--font-body)";            // body

// ─── Sealed review header ─────────────────────────────────────────────────────
// Replaces the full HomeNav when the listing is viewed inside a private-review
// preview session. LWD wordmark + a single "Back to Review" link, no outbound
// links to the public site, no membership/pricing. Keeps the prospect inside the
// curated experience.
function SealedReviewHeader() {
  const mob = typeof window !== 'undefined' && window.innerWidth < 640;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 10, height: 54, padding: mob ? '0 12px' : '0 18px',
      background: 'rgba(10,8,6,0.92)', backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.07)',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, minWidth: 0, flexShrink: 1 }}>
        <span style={{ fontFamily: FD, fontSize: mob ? 20 : 17, letterSpacing: '0.04em',
          color: 'rgba(245,240,232,0.95)', whiteSpace: 'nowrap' }}>
          {mob ? 'LWD' : 'Luxury Wedding Directory'}
        </span>
        <span style={{ fontFamily: FB, fontSize: 8, fontWeight: 700, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#c9a96e', marginTop: 3, whiteSpace: 'nowrap' }}>
          Private Review
        </span>
      </div>
      <button
        onClick={() => { if (typeof window !== 'undefined') window.history.back(); }}
        style={{
          fontFamily: FB, fontSize: mob ? 10 : 11, fontWeight: 700, letterSpacing: '0.05em',
          textTransform: 'uppercase', padding: mob ? '7px 11px' : '8px 15px', borderRadius: 6, cursor: 'pointer',
          background: 'transparent', color: 'rgba(245,240,232,0.9)', whiteSpace: 'nowrap',
          border: '1px solid rgba(255,255,255,0.18)', flexShrink: 0,
        }}>
        ← Back to Review
      </button>
    </div>
  );
}

// ─── COUNTRY → FLAG LOOKUP ────────────────────────────────────────────────────
const COUNTRY_FLAG = {
  'Italy': '🇮🇹', 'Austria': '🇦🇹', 'France': '🇫🇷', 'Spain': '🇪🇸',
  'Portugal': '🇵🇹', 'Greece': '🇬🇷', 'United Kingdom': '🇬🇧', 'UK': '🇬🇧',
  'United States': '🇺🇸', 'USA': '🇺🇸', 'Switzerland': '🇨🇭', 'Germany': '🇩🇪',
  'Croatia': '🇭🇷', 'Mexico': '🇲🇽', 'Maldives': '🇲🇻', 'Turkey': '🇹🇷',
  'Cyprus': '🇨🇾', 'Morocco': '🇲🇦', 'South Africa': '🇿🇦', 'Thailand': '🇹🇭',
  'Bali': '🇮🇩', 'Indonesia': '🇮🇩', 'Ireland': '🇮🇪', 'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿', 'Wales': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
};

// ─── PRICE FORMATTER ──────────────────────────────────────────────────────────
const CCY_SYM = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'C$' };
const fmtPrice = (amount, currency) => {
  if (!amount && amount !== 0) return '';
  const sym = (currency && CCY_SYM[currency]) || currency || '';
  const num = Number(amount);
  if (isNaN(num)) return `${sym}${amount}`;
  return `${sym}${num.toLocaleString()}`;
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const VENUE = {
  // ── MINIMAL FALLBACK: ID ONLY ──
  // All other fields MUST come from database. Do not add venue-specific defaults.
  id: 1,
  name: null,
  tagline: null,
  location: null,
  country: null,
  flag: null,
  rating: null,
  reviews: null,
  responseTime: null,
  responseRate: null,
  established: null,
  weddingsHosted: null,
  priceFrom: null,
  capacity: null,
  verified: null,
  featured: null,
  accommodation: {
    type: null,
    totalRooms: null,
    totalSuites: null,
    maxOvernightGuests: null,
    exclusiveUse: null,
    minNightStay: null,
    description: null,
    images: [],
  },
  dining: {
    style: null,
    chefName: null,
    inHouseCatering: null,
    externalCateringAllowed: null,
    menuStyles: [],
    dietaryOptions: [],
    drinksOptions: [],
    description: null,
    menuImages: [],
  },
  venueType: {
    primaryType: null,
    styles: [],
    architecture: null,
    built: null,
    description: null,
    features: [],
  },
  categories: [],
  awards: [],
  press: [],
  imgs: [],
  gallery: [],
  videos: [],
  // ── Engagement data is removed from fallback. Load from database only. ──
  engagement: {
    photos: {},
    videos: {},
  },
  // ── Notices: Load from database only. Do not provide hardcoded fallbacks. ──
  notices: [],
  exclusiveUse: {
    enabled: false,
    title: null,
    subtitle: null,
    from: null,
    subline: null,
    description: null,
    ctaText: null,
    includes: [],
    minNights: null,
  },
  catering: {
    enabled: true,
    cards: [
      {
        id: 'c1',
        icon: 'dining',
        title: 'In-house catering',
        description: 'Our culinary team sources produce from the estate and surrounding farms. Seasonal menus designed around your wedding day.',
        subtext: '',
        sortOrder: 0,
      },
      {
        id: 'c2',
        icon: 'cooking',
        title: 'External caterers',
        description: 'External caterers welcome. Corkage fee £18 per bottle.',
        subtext: '',
        sortOrder: 1,
      },
      {
        id: 'c3',
        icon: 'wine',
        title: 'Sommelier service',
        description: 'Our sommelier will curate a bespoke wine journey from our private cellar. Private cellar with over 800 labels.',
        subtext: '',
        sortOrder: 2,
      },
    ],
    styles: ["Fine dining", "Banquet", "Family style", "Food stations", "Late-night snacks"],
    dietary: ["Vegan", "Vegetarian", "Halal", "Kosher", "Gluten-free"],
  },
  // ── Spaces: Load from database only. Hardcoded spaces (Grand Salon, Cypress Garden, etc.) removed. ──
  spaces: [],
  experiences: [
    { id: "e1", label: "Private wine cellar tasting",       category: "estate", kind: "wine",    isIncluded: true, season: "all-year" },
    { id: "e2", label: "Full spa and wellness suite",        category: "estate", kind: "spa",     isIncluded: true, season: "all-year" },
    { id: "e3", label: "Heated infinity pool and sun terrace", category: "estate", kind: "pool",  isIncluded: true, season: "summer" },
    { id: "e4", label: "Tuscan cooking class with our chef", category: "estate", kind: "cooking", isPrivate: true, season: "all-year" },
    { id: "e5", label: "Vintage Alfa Romeo estate collection", category: "estate", kind: "car",  isPrivate: true },
    { id: "e6", label: "Olive oil and truffle estate tour",  category: "estate", kind: "truffle", season: "autumn" },
    { id: "e7", label: "Antinori vineyard tours",            category: "nearby", kind: "vineyard", distanceMinutes: 12 },
    { id: "e8", label: "Private Florence gallery experience", category: "nearby", kind: "museum",  distanceMinutes: 30 },
    { id: "e9", label: "Golf Club Ugolino",                  category: "nearby", kind: "golf",    distanceMinutes: 15 },
    { id: "e10", label: "Truffle hunting with local guide",  category: "nearby", kind: "truffle", distanceMinutes: 20 },
    { id: "e11", label: "Arno river private boat",           category: "nearby", kind: "boat",   distanceMinutes: 35 },
    { id: "e12", label: "Private Uffizi after-hours visit",  category: "nearby", kind: "culture", distanceMinutes: 30 },
  ],
  access: {
    helicopterTransferAvailable: false,
    helicopterTransferMinutesFromAirport: null,
    airports: [],
    primaryAirport: null,
  },
  // ── Testimonials: Load from database only. Do not provide hardcoded fallbacks. ──
  testimonials: [],
  // ── Similar Venues: Load from database only. Do not provide hardcoded fallbacks. ──
  similar: [],
  contact: {
    address: {
      line1: null,
      city: null,
      region: null,
      postcode: null,
      country: null,
      latitude: null,
      longitude: null,
    },
    phone: null,
    email: null,
    website: null,
    responseMetrics: {
      averageResponseHours: null,
      responseRatePercent: null,
      sameDayTypical: null,
    },
  },
  video: {
    type: null,
    heroId: null,
    filmId: null,
  },
  // ── Owner: Load from database only. Do not provide hardcoded fallbacks (Isabella Rosanova removed). ──
  owner: {
    name: null,
    title: null,
    photo: null,
    bio: null,
    memberSince: null,
  },
  // ── Wedding Weekend: Load from database only. Hardcoded Villa descriptions removed. ──
  weddingWeekend: {
    enabled: false,
    subtitle: null,
    days: [],
  },
  estateEnabled: false,
  nearbyEnabled: false,
  // ── FAQ: Load from database only. All hardcoded Villa Rosanova FAQs removed. ──
  faq: {
    enabled: false,
    title: null,
    subtitle: null,
    ctaEnabled: false,
    ctaHeadline: null,
    ctaSubtext: null,
    ctaButtonText: null,
    categories: [],
  },
  similarVenuesEnabled: true,
  recentlyViewedEnabled: true,
};

// ─── COMPUTED BACKWARD-COMPAT FIELDS ─────────────────────────────────────────
// These derive from structured data so older references (sidebar, chat, etc.) keep working.
VENUE.responseTime = VENUE.contact.responseMetrics.averageResponseHours
  ? `${VENUE.contact.responseMetrics.averageResponseHours} hrs`
  : null;
VENUE.responseRate = VENUE.contact.responseMetrics.responseRatePercent;
VENUE.contact.mapQuery = `${VENUE.contact.address.city},+${VENUE.contact.address.region},+${VENUE.contact.address.country}`.replace(/ /g, "+");
VENUE.contact.addressFormatted = [
  VENUE.contact.address.line1,
  VENUE.contact.address.city,
  `${VENUE.contact.address.postcode} ${VENUE.contact.address.region}`,
  VENUE.contact.address.country,
].join(", ");

// ─── Canonical URL builder ─────────────────────────────────────────────────────
function buildListingUrl(v) {
  // Canonical URL service (src/utils/listingUrl) is the single source of truth.
  // Stored canonicalPath is a fallback for recently-viewed entries that may lack
  // split slug fields; '#' is the last resort so navigation never dead-links.
  return listingProfileUrl(v) || v.canonicalPath || '#';
}

// ─── RECENTLY VIEWED, localStorage helpers ────────────────────────────────────
// v2: bumped to invalidate stale entries stored before canonical-path sanitization
const RV_KEY = 'ldw_recently_viewed_v2';
// Display cap is 3. Store 4 so that filtering out the current venue still
// leaves 3 cards to display (current venue is always the freshest entry).
const MAX_RV_STORED = 4;

// A valid canonical listing URL is /<country>/<region>/<category>/<slug>
//, 4 lowercase segments. Anything shorter routes through stateToPath which
// returns '/' when country/region are missing, dumping the user on the home page.
function isCanonicalListingPath(path) {
  if (typeof path !== 'string' || !path) return false;
  return /^\/[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+\/?$/.test(path);
}

function sanitizeCanonicalPath(path) {
  return isCanonicalListingPath(path) ? path.replace(/\/$/, '') : null;
}

function slugToLabel(slug) {
  if (!slug) return 'Venues';
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getRVList() {
  try {
    const raw = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    // Defensively drop any entry whose canonicalPath is missing or non-canonical,
    // click handler falls back to `/${slug}` which routes to home via stateToPath.
    return raw.filter((x) => x && isCanonicalListingPath(x.canonicalPath));
  } catch { return []; }
}

function recordVenueView(v, slug, canonicalPathArg, locationFallback) {
  try {
    // Try the caller-computed canonical path first (built from rawListing). If
    // that's missing or not yet computable, fall back to window.location.pathname
    //, but ONLY when the URL bar is itself already canonical, since stateToPath
    // returns '/' for non-canonical paths and would dump the user on home.
    const canonicalPath =
      sanitizeCanonicalPath(canonicalPathArg) ||
      sanitizeCanonicalPath(typeof window !== 'undefined' ? window.location.pathname : null);
    if (!canonicalPath) return;

    // Prefer the dedicated hero image (what the user actually sees when the
    // profile loads). Fall back to imgs[0]/gallery only when heroImgs is empty.
    // This keeps the Recently Viewed thumbnail visually consistent with the
    // profile hero, so a stale gallery URL can't leave a broken tile behind.
    let img = '';
    if (Array.isArray(v.heroImgs) && v.heroImgs.length > 0) {
      const h = v.heroImgs[0];
      img = typeof h === 'string' ? h : (h?.url || h?.src || '');
    }
    if (!img && v.imgs && Array.isArray(v.imgs) && v.imgs.length > 0) {
      img = typeof v.imgs[0] === 'string' ? v.imgs[0] : (v.imgs[0]?.src || v.imgs[0]?.url || '');
    }
    if (!img && v.gallery && typeof v.gallery === 'object') {
      // If gallery is an object (from DB), extract src from first item
      if (Array.isArray(v.gallery) && v.gallery.length > 0) {
        img = v.gallery[0].src || v.gallery[0];
      } else if (v.gallery.src) {
        img = v.gallery.src;
      }
    }

    // Location is cosmetic but the RecentlyViewed filter has historically
    // required it. Provide a sensible URL-slug fallback so entries are never
    // silently dropped just because the DB row lacks city/region.
    const location = v.location || locationFallback || '';

    const entry = {
      id: v.id,
      name: v.name,
      location,
      rating: v.rating,
      price: v.priceFrom,
      currency: v.priceCurrency || '£',
      img: img,
      slug: slug,
      canonicalPath,
      viewedAt: Date.now(),
    };
    const updated = [entry, ...getRVList().filter(x => x.id !== entry.id)].slice(0, MAX_RV_STORED);
    localStorage.setItem(RV_KEY, JSON.stringify(updated));
  } catch { /* localStorage unavailable */ }
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; scrollbar-gutter: stable; }
      body { font-family: ${FB}; -webkit-font-smoothing: antialiased; }
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: rgba(157,135,62,0.3); border-radius: 3px; }
      @keyframes fadeUp      { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      @keyframes fadeIn      { from { opacity:0; } to { opacity:1; } }
      @keyframes shimmer     { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      @keyframes slideUp     { from { transform:translateY(100%); opacity:0; } to { transform:translateY(0); opacity:1; } }
      @keyframes kenBurns    { 0%{transform:scale(1)} 100%{transform:scale(1.06)} }
      @keyframes pulse       { 0%,100%{box-shadow:0 0 0 0 rgba(74,222,128,0.5)} 50%{box-shadow:0 0 0 5px rgba(74,222,128,0)} }
      @keyframes chatModalIn { from { opacity:0; transform:translate(-50%,-50%) scale(0.93); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }
      @keyframes dotPulse    { 0%,80%,100% { transform:scale(0); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }
      @keyframes lightbox-progress { from { width:0 } to { width:100% } }
      .lwd-thumb-strip::-webkit-scrollbar { display:none; }
      @keyframes barPulse    { 0%,100% { box-shadow:0 4px 32px rgba(0,0,0,0.08),0 0 0 1px rgba(211,154,33,0.18); } 50% { box-shadow:0 6px 40px rgba(211,154,33,0.15),0 0 0 1.5px rgba(211,154,33,0.38); } }
      @keyframes lwd-modal-in { from { opacity:0; transform:translateY(18px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
.lwd-img-zoom { transition: transform 0.7s ease; }
      .lwd-img-zoom:hover { transform: scale(1.04); }
      *:hover > .lwd-tag-overlay { opacity: 1 !important; }
      .lwd-fade-up { animation: fadeUp 0.6s ease both; }
      @media (max-width: 900px) { .lwd-sidebar { display: none !important; } .lwd-mobile-bar { display: flex !important; } .lwd-pub-inline { display: block !important; } }
      @media (min-width: 901px) { .lwd-mobile-bar { display: none !important; } }
    `}</style>
  );
}

// ─── EXPERIENCE TAXONOMY ────────────────────────────────────────────────────
const EXPERIENCE_KINDS = Object.freeze([
  "vineyard", "wine", "truffle", "cooking", "dining", "spa", "pool", "golf",
  "boat", "beach", "mountain", "museum", "culture", "tour", "helicopter",
  "airport", "car", "wellness", "nature", "hiking", "safari", "ski", "island", "rail",
]);
const EXPERIENCE_KIND_SET = new Set(EXPERIENCE_KINDS);

// ─── ICON MAP (SVG paths, stroke-based, 24×24 viewBox) ─────────────────────
const ICON_PATHS = Object.freeze({
  vineyard:   "M12 2C12 2 7 8 7 13a5 5 0 0010 0c0-5-5-11-5-11zM12 22v-6",
  wine:       "M8 2h8l-1 8a5 5 0 01-6 0L8 2zM12 14v8M8 22h8M12 10v0",
  truffle:    "M12 6a6 6 0 100 12 6 6 0 000-12zM9 10.5a1.5 1.5 0 110 .01M15 10.5a1.5 1.5 0 110 .01M11 14a1 1 0 110 .01M12 2v4",
  cooking:    "M12 2v4M6 6h12v3a6 6 0 01-12 0V6zM12 15v5M8 22h8M4 6h16",
  dining:     "M3 6h18M7 6v-2a2 2 0 014 0v2M13 6v-2a2 2 0 014 0v2M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14",
  spa:        "M12 8c-3 0-6 2-6 6v2h12v-2c0-4-3-6-6-6zM12 2v2M8 4l1 2M16 4l-1 2M6 18c0 2 2.7 4 6 4s6-2 6-4",
  pool:       "M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 17c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 7c2-2 4-2 6 0s4 2 6 0 4-2 6 0",
  golf:       "M12 2v14M12 16a4 4 0 01-4 4h8a4 4 0 01-4-4zM12 6l6-2",
  boat:       "M2 16l2-2h16l2 2M4 14V8l4-4h8l4 4v6M2 20c2-2 4-2 6 0s4 2 6 0 4-2 6 0",
  beach:      "M2 20h20M6 20v-4M18 20v-4M6 16h12M9 8a3 3 0 106 0M12 2v3",
  mountain:   "M2 20L8 8l4 6 4-6 6 12H2z",
  museum:     "M3 21h18M5 21V10M19 21V10M3 10l9-7 9 7M9 21v-5h6v5",
  culture:    "M4 20h16M12 4v2M7 9a5 5 0 0010 0M5 14h14M8 14v6M16 14v6",
  tour:       "M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zM12 12a3 3 0 110-6 3 3 0 010 6z",
  helicopter: "M12 14l-4 6h8l-4-6zM2 6h20M12 6v8M7 2v8M17 2v8",
  airport:    "M12 2L2 8h20L12 2zM4 8v8h16V8M8 16v4M16 16v4M2 22h20",
  car:        "M3 14h18v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zM5 14l2-6h10l2 6M6.5 17.5a1 1 0 110-2 1 1 0 010 2zM17.5 17.5a1 1 0 110-2 1 1 0 010 2z",
  wellness:   "M12 2a4 4 0 014 4c0 2-2 6-4 6s-4-4-4-6a4 4 0 014-4zM12 12c-4 0-8 2-8 6v2h16v-2c0-4-4-6-8-6z",
  nature:     "M12 2l-2 6h4l-2-6zM12 8c-5 0-8 3-8 7h16c0-4-3-7-8-7zM12 15c-6 0-10 2-10 5h20c0-3-4-5-10-5zM12 20v2",
  hiking:     "M13 4a2 2 0 110-4 2 2 0 010 4zM10 8l-4 8h3l2 6h4l2-6h3l-4-8h-6z",
  safari:     "M12 2c-4 0-8 3.6-8 8s8 12 8 12 8-7.6 8-12-4-8-8-8zM9 9h0M15 9h0M9 13c1.5 2 4.5 2 6 0",
  ski:        "M2 20l6-6M10 14l8-8M14 4l4 4M12 2l-2 6h4l-2-6z",
  island:     "M2 16c3-4 6-6 10-6s7 2 10 6M7 16c1-2 2-6 5-6s4 4 5 6M12 10V6M10 6h4",
  rail:       "M5 4h14v12a2 2 0 01-2 2H7a2 2 0 01-2-2V4zM5 4a2 2 0 012-2h10a2 2 0 012 2M8 20l-2 2M16 20l2 2M5 12h14M8.5 15.5a1 1 0 110-2M15.5 15.5a1 1 0 110-2",
  // ── Utility icons (for Contact, Access, UI) ──
  pin:        "M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zM12 12a3 3 0 110-6 3 3 0 010 6z",
  phone:      "M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z",
  email:      "M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zM22 6l-10 7L2 6",
  globe:      "M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h20M12 2a15 15 0 014 10 15 15 0 01-4 10 15 15 0 01-4-10A15 15 0 0112 2z",
  clock:      "M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2",
  check:      "M20 6L9 17l-5-5",
  zap:        "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
});

// Curated luxury room-facility icons (line style, 24 viewBox) + labels. Single
// source of truth: rendered on the public page and offered in the client editor.
export const ROOM_FACILITIES = Object.freeze([
  { key: "four_poster", label: "Four-poster bed",   icon: "M3 18v-5h18v5M3 13V9a2 2 0 012-2h14a2 2 0 012 2v4M3 18v2M21 18v2M7 13v-2h4v2" },
  { key: "ensuite",     label: "En-suite bathroom", icon: "M6 12V8a4 4 0 018 0M4 12h16v2a8 8 0 01-16 0v-2zM8 19l-1 2M16 19l1 2M12 20v2" },
  { key: "bath",        label: "Freestanding bath", icon: "M4 13h16M6 13V6a2 2 0 014 0M4 13v3a4 4 0 004 4h8a4 4 0 004-4v-3M7 20l-1 2M17 20l1 2" },
  { key: "fireplace",   label: "Fireplace",         icon: "M4 21V3h16v18M8 21v-5a4 4 0 018 0v5M12 8c1 1 1.5 2 .5 3.5" },
  { key: "terrace",     label: "Private terrace",   icon: "M3 8l9-5 9 5M5 8v13M19 8v13M9 8v13M15 8v13M5 14h14M3 8h18" },
  { key: "views",       label: "Estate views",      icon: "M4 3h16v18H4zM12 3v18M4 12h16M6 9l2-1 2 1" },
  { key: "wifi",        label: "Wi-Fi",             icon: "M2 8.5a15 15 0 0120 0M5 12a10 10 0 0114 0M8 15.5a5 5 0 018 0M12 19h.01" },
  { key: "aircon",      label: "Air conditioning",  icon: "M2 7h20v6H2zM6 16v3M10 16v3M14 16v3M18 16v3M5 10h3M11 10h2M16 10h3" },
  { key: "breakfast",   label: "Breakfast",         icon: "M5 8h12v4a5 5 0 01-5 5h-2a5 5 0 01-5-5V8zM17 9h2a2 2 0 010 4h-2M9 3v2M13 3v2M5 21h14" },
  { key: "dressing",    label: "Dressing room",     icon: "M5 2h14v20H5zM12 2v20M9 11h.01M15 11h.01M3 22h18" },
  { key: "tv",          label: "Smart TV",          icon: "M2 4h20v13H2zM7 21h10M12 17v4" },
  { key: "sea_view",    label: "Sea view",          icon: "M2 8c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 13c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0" },
  { key: "balcony",     label: "Balcony",           icon: "M3 10h18M5 10v11M12 10v11M19 10v11M3 15h18M6 4l6-2 6 2v6H6z" },
  { key: "minibar",     label: "Mini bar",          icon: "M9 2h6v3l-1 2v13a2 2 0 01-2 2 2 2 0 01-2-2V7L9 5V2zM9 5h6M10 13h4" },
  { key: "pillow_menu", label: "Pillow menu",       icon: "M3 9a3 3 0 013-3h12a3 3 0 013 3v6a3 3 0 01-3 3H6a3 3 0 01-3-3V9zM7 8c-1 2-1 6 0 8M17 8c1 2 1 6 0 8" },
  { key: "bathrobes",   label: "Robes & slippers",  icon: "M12 3a2 2 0 012 2c0 1-1 2-2 2M12 7l-6 4v10h12V11l-6-4zM12 7v14" },
  { key: "safe",        label: "In-room safe",      icon: "M4 4h16v16H4zM8 8h8v8H8zM12 11a1 1 0 100 2M17 10v4" },
  { key: "accessible",  label: "Step-free access",  icon: "M12 5a2 2 0 100-4 2 2 0 000 4zM12 7v6M8 10h8M12 13l-2 8M12 13l2 8" },
  { key: "dog_friendly",label: "Dog friendly",      icon: "M5 11a2 2 0 100-4 2 2 0 000 4zM19 11a2 2 0 100-4 2 2 0 000 4zM9 7a2 2 0 100-4 2 2 0 000 4zM15 7a2 2 0 100-4 2 2 0 000 4zM12 22a5 5 0 01-5-5c0-3 2-4 5-4s5 1 5 4a5 5 0 01-5 5z" },
]);
export const ROOM_FACILITY_MAP = Object.freeze(Object.fromEntries(ROOM_FACILITIES.map(f => [f.key, f])));

function Icon({ name, size = 18, color, style: extraStyle }) {
  const C = useT();
  const d = ICON_PATHS[name];
  if (!d) {
    if (typeof window !== "undefined" && window.__LWD_DEV) console.warn(`[LWD] Unknown icon: "${name}"`);
    return null;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color || C.textMid}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle", ...extraStyle }}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

// ─── STARS ───────────────────────────────────────────────────────────────────
function Stars({ rating, size = 13 }) {
  const C = useT();
  return (
    <span style={{ display: "inline-flex", gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ fontSize: size, color: i <= Math.floor(rating) ? C.gold : i - 0.5 <= rating ? C.gold : C.border2 }}>★</span>
      ))}
    </span>
  );
}

// ─── SECTION HEADING ─────────────────────────────────────────────────────────
function SectionHeading({ title, subtitle, mb }) {
  const C = useT();
  return (
    <div style={{ marginBottom: mb ?? 40, textAlign: 'left', width: '100%' }}>
      <h2 style={{ fontFamily: FD, fontSize: 34, fontWeight: 400, color: C.text, letterSpacing: "-0.4px", lineHeight: 1.1, marginBottom: 12 }}>{title}</h2>
      <div style={{ width: 48, height: 2, backgroundImage: `linear-gradient(90deg, ${C.gold}, ${C.green})` }} />
      {subtitle && <p style={{ fontFamily: FB, fontSize: 14, color: C.textMuted, marginTop: 12, lineHeight: 1.65, letterSpacing: '0.1px' }}>{subtitle}</p>}
    </div>
  );
}

// ─── PILL / BADGE ─────────────────────────────────────────────────────────────
function Pill({ children, color }) {
  const C = useT();
  const clr = color === "gold" ? C.gold : color === "green" ? C.green : C.textLight;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", border: `1px solid ${clr}22`, background: `${clr}10`, color: clr, fontFamily: FB, fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase" }}>
      {children}
    </span>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav({ venue, darkMode, setDarkMode, saved, setSaved, compareList, onAddCompare, onBack }) {
  const C = useT();
  const catLabel = slugToLabel(venue.categorySlug || venue.category_slug);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // ── colour tokens that change based on scroll + theme ──
  const navBg     = scrolled ? C.navBg    : "transparent";
  const navBorder = scrolled ? (darkMode ? `1px solid ${C.goldBorder}` : `1px solid ${C.border}`) : "none";
  const logoColor = scrolled ? C.gold     : "#ffffff";
  const wordColor = scrolled ? C.textMuted : "rgba(255,255,255,0.45)";
  const crumbColor = scrolled ? C.textMuted : "rgba(255,255,255,0.5)";
  const crumbActive = scrolled ? C.gold   : "rgba(255,255,255,0.9)";
  const btnBorder = (active) => active
    ? C.gold
    : scrolled ? C.border2 : "rgba(255,255,255,0.28)";
  const btnColor  = (active) => active
    ? C.gold
    : scrolled ? C.textMid : "rgba(255,255,255,0.82)";
  const btnBg     = (active) => active ? C.goldLight : "transparent";

  return (
    <nav className="vp-nav" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: navBg,
      backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
      borderBottom: navBorder,
      padding: "0 40px", height: 72,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      transition: "background 0.35s ease, border-color 0.35s ease",
    }}>

      {/* ── Logo + breadcrumb ── */}
      <div className="vp-nav-left" style={{ display: "flex", alignItems: "center", gap: 24 }}>
        {/* Wordmark */}
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <div style={{
            fontFamily: FD, fontSize: 21, fontWeight: 400,
            color: logoColor, letterSpacing: "2.5px",
            lineHeight: 1, transition: "color 0.35s",
          }}>
            LUXURY WEDDING DIRECTORY
          </div>
          <div style={{
            width: "100%", height: 1,
            background: scrolled
              ? `linear-gradient(90deg, ${C.gold}, ${C.green})`
              : "rgba(255,255,255,0.22)",
            transition: "opacity 0.35s",
          }} />
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 28, background: scrolled ? C.border : "rgba(255,255,255,0.2)" }} />

        {/* Breadcrumb */}
        <div className="vp-breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FB, fontSize: 12, letterSpacing: "0.2px" }}>
          {[catLabel, venue.country, venue.location?.split(', ').pop()].filter(Boolean).map((crumb) => (
            <span key={crumb} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{ color: crumbColor, cursor: "pointer", transition: "color 0.2s" }}
                onClick={crumb === catLabel ? () => { const s = venue.categorySlug || venue.category_slug; if (s) { window.history.pushState(null, '', `/${s}`); window.dispatchEvent(new PopStateEvent('popstate')); } else if (onBack) onBack(); } : undefined}
                onMouseEnter={e => e.currentTarget.style.color = crumbActive}
                onMouseLeave={e => e.currentTarget.style.color = crumbColor}
              >{crumb}</span>
              <span style={{ color: scrolled ? C.border2 : "rgba(255,255,255,0.25)", fontSize: 10 }}>›</span>
            </span>
          ))}
          <span style={{ color: crumbActive, fontWeight: 600 }}>{venue.name}</span>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="vp-nav-actions" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {[
          { label: saved ? "♥  Saved" : "♡  Save",  action: () => setSaved(s => !s), active: saved },
          { label: "⊕  Compare", action: onAddCompare,   active: compareList.length > 0 },
          { label: "↗  Share",   action: () => {},        active: false },
        ].map(btn => (
          <button key={btn.label} onClick={btn.action}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = btnBorder(btn.active); e.currentTarget.style.color = btnColor(btn.active); }}
            style={{
              padding: "8px 16px", fontFamily: FB, fontSize: 11, fontWeight: 700, letterSpacing: "0.6px",
              textTransform: "uppercase",
              background: btnBg(btn.active),
              border: `1px solid ${btnBorder(btn.active)}`,
              borderRadius: "var(--lwd-radius-input)",
              color: btnColor(btn.active),
              cursor: "pointer", transition: "all 0.2s",
            }}>{btn.label}
          </button>
        ))}

        {/* Dark / light toggle */}
        <button onClick={() => setDarkMode(d => !d)}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = scrolled ? C.border2 : "rgba(255,255,255,0.28)"; e.currentTarget.style.color = scrolled ? C.textMid : "rgba(255,255,255,0.82)"; }}
          style={{
            width: 38, height: 38, marginLeft: 4,
            border: `1px solid ${scrolled ? C.border2 : "rgba(255,255,255,0.28)"}`,
            borderRadius: "var(--lwd-radius-input)",
            background: "none", color: scrolled ? C.textMid : "rgba(255,255,255,0.82)",
            fontSize: 16, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
          }}>{darkMode ? "☀" : "☽"}
        </button>
      </div>
    </nav>
  );
}

// ─── HERO SLIDER (shared) ────────────────────────────────────────────────────
// LCP NOTE (2026-07-31). This slider was the platform's worst measured mobile
// LCP — 15.1s — for two compounding reasons, neither of them the images' size:
//
//   1. Every slide was a CSS background-image. The browser's preload scanner reads
//      the raw HTML and starts fetching <img src> before any JS runs; it does NOT
//      read url() out of a style attribute. So the hero could not begin loading
//      until the bundle had downloaded, parsed and rendered — on a phone, seconds
//      of dead time before the fetch was even issued. Prerendering did not help,
//      because a prerendered background-image is still invisible to the scanner.
//
//   2. Every slide mounted at once. heroImgs is capped at 5 (10 on the gallery
//      fallback path), each requested at width=1600, so the single image that
//      determines LCP was competing for mobile bandwidth with four more that nobody
//      would see for at least five seconds.
//
// Now: real <img> elements, the first marked high priority so the scanner finds it
// in the prerendered HTML, and only the current slide plus the next one are mounted.
// Initial load drops from 5 full-size images to 2. Visually identical — object-fit and
// object-position reproduce background-size/position exactly, and kenBurns still
// animates the wrapper.
function HeroSlider({ imgs, height, children }) {
  const C = useT();
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  // Highest slide reached. Mounting up to reached+1 keeps the crossfade instant
  // (the next image is already decoded) without paying for the whole set up front.
  const [reached, setReached] = useState(0);
  useEffect(() => { setReached(r => Math.max(r, idx)); }, [idx]);
  useEffect(() => {
    if (hovered) return;
    const t = setInterval(() => setIdx(i => (i + 1) % imgs.length), 5000);
    return () => clearInterval(t);
  }, [hovered, imgs.length]);
  return (
    <div style={{ position: "relative", height, overflow: "hidden" }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {imgs.map((img, i) => {
        // Wrapping is cheap; the <img> inside is what the scanner needs. Slides past
        // the frontier render as empty boxes and cost nothing until they are reached.
        const mounted = i <= reached + 1;
        return (
          <div key={i} style={{
            position: "absolute", inset: 0,
            opacity: i === idx ? 1 : 0,
            animation: i === idx ? "kenBurns 8s ease forwards" : "none",
            transition: "opacity 1.2s ease",
          }}>
            {mounted && (
              <img
                src={vpImg(img, 1800)}
                alt=""
                aria-hidden="true"
                // The first slide IS the LCP element on every listing page. eager +
                // high priority is the whole point of the change; anything else here
                // hands the browser a reason to defer it behind the bundle again.
                // Priority, not laziness, is what separates these. Every mounted slide
                // is eager: loading="lazy" on a slide sitting at opacity 0 made Chrome
                // defer the fetch until it became visible, so the crossfade at 5s
                // revealed a white box — caught on screen, invisible in the network
                // numbers, which is why this needed looking at rather than measuring.
                // fetchpriority="low" gets the same restraint safely: the browser still
                // queues them, just behind the one that decides LCP.
                fetchpriority={i === 0 ? "high" : "low"}
                loading="eager"
                // decoding is left at the default for the LCP image on purpose:
                // decoding="async" can push the first paint back, which is the one
                // thing this change exists to prevent.
                {...(i === 0 ? {} : { decoding: "async" })}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
              />
            )}
          </div>
        );
      })}
      {children}
      {/* Arrows */}
      {imgs.length > 1 && [{ dir: "←", l: 16, r: "auto" }, { dir: "→", l: "auto", r: 16 }].map(a => (
        <button key={a.dir}
          onClick={() => setIdx(i => a.dir === "←" ? (i - 1 + imgs.length) % imgs.length : (i + 1) % imgs.length)}
          style={{
            position: "absolute", top: "50%", left: a.l, right: a.r,
            transform: "translateY(-50%)", width: 40, height: 40,
            border: "1px solid rgba(255,255,255,0.4)", borderRadius: "50%",
            background: "rgba(0,0,0,0.22)",
            backdropFilter: "blur(8px)", color: "#fff", fontSize: 16, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.25s ease",
          }}>{a.dir}</button>
      ))}
      {/* Dots, max 3 shown: prev · active · next */}
      {imgs.length > 1 && (
        <div style={{ position: "absolute", bottom: 16, right: 40, display: "flex", gap: 5, alignItems: "center" }}>
          {[
            (idx - 1 + imgs.length) % imgs.length,
            idx,
            (idx + 1) % imgs.length,
          ].filter((v, i, a) => imgs.length >= 3 || a.indexOf(v) === i).map((dotIdx, i) => (
            <button key={dotIdx} onClick={() => setIdx(dotIdx)} style={{
              width: dotIdx === idx ? 20 : 6, height: 6, border: "none", cursor: "pointer",
              background: dotIdx === idx ? C.gold : "rgba(255,255,255,0.5)",
              transition: "all 0.3s ease",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Location path helper ─────────────────────────────────────────────────────
// Returns a valid internal path to navigate to, or null if data is insufficient.
// null → render plain text; never fall back to '/'.
function getLocationPath(venue) {
  const cs  = venue.countrySlug  || venue.country_slug  || '';
  const rs  = venue.regionSlug   || venue.region_slug   || '';
  const cat = venue.categorySlug || venue.category_slug || '';
  if (!cs) return null;
  if (cat === 'wedding-planners') return `/${cs}/wedding-planners`;
  if (rs) return `/${cs}/${rs}`;
  return `/${cs}`;
}

// ─── HERO STYLE 1: CINEMATIC ─────────────────────────────────────────────────
function HeroCinematic({ venue, onEnquire, onBack }) {
  const C = useT();
  const isMobile = useIsMobile();
  const catLabel = slugToLabel(venue.categorySlug || venue.category_slug);
  const heroMaxGuests = venue.capacity?.ceremony || venue.capacity?.max ||
    (venue.spaces?.length
      ? Math.max(...venue.spaces.map(s =>
          Math.max(s.capacityCeremony || 0, s.capacityReception || 0, s.capacityDining || 0, s.capacityStanding || 0)
        ))
      : 0) || null;
  return (
    <div style={{ position: "relative" }}>
      <HeroSlider imgs={venue.heroImgs?.length ? venue.heroImgs : venue.imgs} height="62vh">
        {/* Gradient, mobile is lighter so the photography breathes */}
        <div style={{ position: "absolute", inset: 0, background: isMobile
          ? "linear-gradient(to bottom, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.01) 28%, rgba(0,0,0,0.60) 100%)"
          : "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.75) 100%)" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMobile ? "0 24px 36px" : "0 40px 52px", animation: "fadeUp 0.8s ease both" }}>

          {/* Editor's Pick, desktop only */}
          {!isMobile && venue.featured && (
            <div style={{ display: "inline-flex", marginBottom: 12, padding: "4px 12px", border: `1px solid ${C.gold}`, background: "rgba(157,135,62,0.18)" }}>
              <span style={{ fontFamily: FB, fontSize: 10, color: "#fff", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>✦ Editor's Pick</span>
            </div>
          )}

          <h1 style={{ fontFamily: FD, fontSize: "clamp(38px, 5.3vw, 68px)", fontWeight: 400, color: "#fff", letterSpacing: "-0.5px", lineHeight: 1.05, marginBottom: 10 }}>{venue.name}</h1>

          {/* Tagline, desktop only */}
          {!isMobile && venue.tagline && (
            <p style={{ fontFamily: FB, fontSize: "clamp(13px, 1.4vw, 16px)", color: "rgba(255,255,255,0.72)", lineHeight: 1.6, marginBottom: 14, maxWidth: 560, letterSpacing: "0.1px" }}>{venue.tagline}</p>
          )}

          {/* Location + stars row, always shown; review count desktop only */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, flexWrap: "wrap", marginBottom: isMobile ? 18 : 22 }}>
            <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{(() => {
                const _path = getLocationPath(venue);
                if (!_path) return <span>{venue.flag} {venue.location}</span>;
                const _nav = () => { window.history.pushState(null, '', _path); window.dispatchEvent(new PopStateEvent('popstate')); };
                return (
                  <span onClick={_nav} style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    title={`Browse in ${venue.location}`}>
                    {venue.flag} {venue.location}
                  </span>
                );
              })()}</span>
            {venue.rating != null && venue.rating > 0 && (
              <>
                <span style={{ width: 1, height: 12, background: "rgba(255,255,255,0.3)" }} />
                <Stars rating={venue.rating} size={13} />
                <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>{venue.rating}</span>
                {!isMobile && venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.55)" }}>({venue.reviews} reviews)</span>}
              </>
            )}
            {/* Desktop-only metadata */}
            {!isMobile && venue.verified && <span style={{ fontFamily: FB, fontSize: 11, color: "#4ade80", fontWeight: 700 }}>✓ Verified</span>}
            {!isMobile && venue.contentScore != null && <TierBadge tier={getQualityTier(venue.contentScore)} showLabel={true} size="sm" />}
            {!isMobile && heroMaxGuests > 0 && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.72)",
                background: "rgba(255,255,255,0.09)", border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 20, padding: "3px 10px", letterSpacing: "0.2px",
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                Up to {heroMaxGuests} guests
              </span>
            )}
          </div>

          {/* Editorial approval + freshness, desktop only */}
          {!isMobile && (venue.editorialApproved || venue.editorialFactChecked) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <ApprovalIndicators approved={venue.editorialApproved} factChecked={venue.editorialFactChecked} layout="horizontal" />
              {venue.editorialApproved && venue.editorialLastReviewedAt && (
                <FreshnessText lastReviewedAt={venue.editorialLastReviewedAt} color="rgba(255,255,255,0.55)" fontSize={11} />
              )}
            </div>
          )}

          {/* Published / Updated dates, desktop only */}
          {!isMobile && venue.publishedAt && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.45)", letterSpacing: "0.3px" }}>
                Published {new Date(venue.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {venue.updatedAt && new Date(venue.updatedAt) > new Date(venue.publishedAt || 0) && (
                <>
                  <span style={{ width: 1, height: 10, background: "rgba(255,255,255,0.15)" }} />
                  <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.3px" }}>
                    Updated {new Date(venue.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Urgency signal, desktop only */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 11px",
                background: "rgba(0,0,0,0.32)", backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.13)",
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%", background: "#4ade80", flexShrink: 0,
                  boxShadow: "0 0 0 0 rgba(74,222,128,0.7)",
                  animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
                }} />
                <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.82)", letterSpacing: "0.35px" }}>
                  {venue.weddingsHosted
                    ? `${venue.weddingsHosted}+ weddings hosted · Popular this season`
                    : 'Popular this season · Limited peak dates remaining'}
                </span>
              </span>
            </div>
          )}

          {/* Hero CTA */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={onEnquire} style={{
              padding: isMobile ? "13px 26px" : "14px 28px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
              color: "#ffffff", fontFamily: FB, fontSize: 13, fontWeight: 800,
              letterSpacing: "1.2px", textTransform: "uppercase", cursor: "pointer",
              transition: "opacity 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >Begin Enquiry</button>

            {/* Showcase button */}
            {venue.showcaseUrl && (
              <a href={venue.showcaseUrl}
                onClick={e => { e.preventDefault(); window.history.pushState(null, "", venue.showcaseUrl); window.dispatchEvent(new PopStateEvent('popstate')); }}
                style={{
                padding: "13px 22px",
                background: "#C47B3A",
                border: "1px solid #C47B3A",
                borderRadius: "var(--lwd-radius-input)",
                color: "#fff",
                fontFamily: FB, fontSize: 13, fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase",
                textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: 6,
                transition: "all 0.2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#A8672E"; e.currentTarget.style.borderColor = "#A8672E"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#C47B3A"; e.currentTarget.style.borderColor = "#C47B3A"; }}
              >✦ Showcase</a>
            )}

            {/* Price + response time, desktop only */}
            {!isMobile && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {venue.priceFrom && (
                  <span style={{ fontFamily: FD, fontSize: 26, fontWeight: 400, color: "#fff", lineHeight: 1 }}>
                    From {fmtPrice(venue.priceFrom, venue.priceCurrency)}
                  </span>
                )}
                {venue.priceFrom && venue.responseTime && (
                  <span style={{ width: 1, height: 11, background: "rgba(255,255,255,0.2)", display: "inline-block" }} />
                )}
                {venue.responseTime && (
                  <span style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.6)", letterSpacing: "0.2px" }}>
                    Replies in {venue.responseTime}
                  </span>
                )}
              </div>
            )}
          </div>

        </div>

      </HeroSlider>
    </div>
  );
}

// ─── HERO STYLE 2: EDITORIAL SPLIT ───────────────────────────────────────────
function HeroSplit({ venue, onEnquire }) {
  const C = useT();
  const isMobile = useIsMobile();
  return (
    <div className="vp-hero-grid" style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "58% 42%",
      height: isMobile ? "auto" : "62vh",
      marginTop: 64,
    }}>
      {/* Image, top on mobile, left on desktop */}
      <HeroSlider imgs={venue.heroImgs?.length ? venue.heroImgs : venue.imgs} height={isMobile ? "44vh" : "100%"}>
        {venue.featured && (
          <div style={{ position: "absolute", top: 20, left: 20, display: "inline-flex", padding: "4px 12px", border: `1px solid ${C.gold}`, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
            <span style={{ fontFamily: FB, fontSize: 10, color: "#fff", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>✦ Editor's Pick</span>
          </div>
        )}
      </HeroSlider>
      {/* Info, below on mobile, right on desktop */}
      <div style={{
        background: C.surface,
        borderLeft: isMobile ? "none" : `1px solid ${C.border}`,
        borderTop: isMobile ? `1px solid ${C.border}` : "none",
        padding: isMobile ? "24px 20px 28px" : "40px 44px",
        display: "flex", flexDirection: "column", justifyContent: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: isMobile ? 14 : 20, flexWrap: "wrap" }}>
          {venue.categories.map(cat => <Pill key={cat} color="gold">{cat}</Pill>)}
        </div>
        <h1 style={{ fontFamily: FD, fontSize: isMobile ? 32 : "clamp(30px, 3.5vw, 52px)", fontWeight: 400, color: C.text, lineHeight: 1.05, marginBottom: 8 }}>{venue.name}</h1>
        <p style={{ fontFamily: FB, fontSize: 13, color: C.textLight, marginBottom: 8, lineHeight: 1.6 }}>{(() => {
                const _path = getLocationPath(venue);
                if (!_path) return <span>{venue.flag} {venue.location}</span>;
                const _nav = () => { window.history.pushState(null, '', _path); window.dispatchEvent(new PopStateEvent('popstate')); };
                return (
                  <span onClick={_nav} style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    title={`Browse in ${venue.location}`}>
                    {venue.flag} {venue.location}
                  </span>
                );
              })()}</p>
        {venue.publishedAt && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: isMobile ? 14 : 20 }}>
            <span style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.3px" }}>
              Published {new Date(venue.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            {venue.updatedAt && new Date(venue.updatedAt) > new Date(venue.publishedAt) && (
              <>
                <span style={{ width: 1, height: 10, background: C.border }} />
                <span style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.3px" }}>
                  Updated {new Date(venue.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </>
            )}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: isMobile ? 16 : 24, flexWrap: "wrap" }}>
          {venue.rating != null && venue.rating > 0 && <><Stars rating={venue.rating} size={14} />
          <span style={{ fontFamily: FB, fontSize: 14, fontWeight: 700, color: C.text }}>{venue.rating}</span>
          {venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>· {venue.reviews} reviews</span>}</> }
          {venue.verified && <span style={{ fontFamily: FB, fontSize: 11, color: C.green, fontWeight: 700 }}>✓ Verified</span>}
          {venue.contentScore != null && <TierBadge tier={getQualityTier(venue.contentScore)} showLabel={true} size="sm" />}
        </div>
        {(venue.editorialApproved || venue.editorialFactChecked) && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <ApprovalIndicators approved={venue.editorialApproved} factChecked={venue.editorialFactChecked} layout="horizontal" />
            {venue.editorialApproved && venue.editorialLastReviewedAt && (
              <FreshnessText lastReviewedAt={venue.editorialLastReviewedAt} color={C.textMuted} fontSize={11} />
            )}
          </div>
        )}
        <div style={{ height: 1, background: C.border, marginBottom: isMobile ? 12 : 16 }} />
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: isMobile ? 12 : 16, padding: "4px 10px", background: `${C.green}14`, border: `1px solid ${C.green}33` }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, flexShrink: 0, animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite" }} />
          <span style={{ fontFamily: FB, fontSize: 11, color: C.text, letterSpacing: "0.35px" }}>
            {venue.weddingsHosted ? `${venue.weddingsHosted}+ weddings hosted · Popular this season` : 'Popular this season · Limited peak dates remaining'}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 16 : 24, gap: 12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {venue.priceFrom && (
            <div>
              <div style={{ fontFamily: FD, fontSize: isMobile ? 24 : 28, color: C.gold }}>From {fmtPrice(venue.priceFrom, venue.priceCurrency)}</div>
              <div style={{ fontFamily: FB, fontSize: 12, color: C.textMuted, marginTop: 2 }}>per event · up to {venue.capacity.ceremony} guests</div>
            </div>
          )}
          {venue.responseTime && (
            <div style={{ fontFamily: FB, fontSize: 12, color: C.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><Icon name="zap" size={13} color={C.green} /> Replies in {venue.responseTime}</div>
          )}
        </div>
        <button onClick={onEnquire} style={{ width: "100%", padding: isMobile ? "13px" : "14px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)", color: "#ffffff", fontFamily: FB, fontSize: 13, fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", cursor: "pointer", transition: "opacity 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
          Begin Enquiry
        </button>
        {venue.showcaseUrl && (
          <a href={venue.showcaseUrl}
            onClick={e => { e.preventDefault(); window.history.pushState(null, "", venue.showcaseUrl); window.dispatchEvent(new PopStateEvent('popstate')); }}
            style={{
            display: "block", textAlign: "center", marginTop: 10,
            padding: "11px", background: "#C47B3A", border: "1px solid #C47B3A",
            borderRadius: "var(--lwd-radius-input)",
            color: "#fff", fontFamily: FB, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.8px", textTransform: "uppercase",
            textDecoration: "none", transition: "all 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#A8672E"; e.currentTarget.style.borderColor = "#A8672E"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#C47B3A"; e.currentTarget.style.borderColor = "#C47B3A"; }}
          >✦ View Showcase</a>
        )}
      </div>
    </div>
  );
}

// ─── HERO STYLE 3: MAGAZINE BANNER ───────────────────────────────────────────
function HeroMagazine({ venue, onEnquire }) {
  const C = useT();
  return (
    <div>
      <HeroSlider imgs={venue.heroImgs?.length ? venue.heroImgs : venue.imgs} height="48vh">
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.28)" }} />
        {venue.featured && (
          <div style={{ position: "absolute", top: 80, left: 40 }}>
            <div style={{ display: "inline-flex", padding: "4px 12px", border: `1px solid ${C.gold}`, background: "rgba(0,0,0,0.45)" }}>
              <span style={{ fontFamily: FB, fontSize: 10, color: "#fff", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>✦ Editor's Pick</span>
            </div>
          </div>
        )}
      </HeroSlider>
      {/* Title below image */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "28px 40px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ fontFamily: FD, fontSize: "clamp(28px, 4vw, 54px)", fontWeight: 400, color: C.text, lineHeight: 1.05, marginBottom: 8 }}>{venue.name}</h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>{(() => {
                const _path = getLocationPath(venue);
                if (!_path) return <span>{venue.flag} {venue.location}</span>;
                const _nav = () => { window.history.pushState(null, '', _path); window.dispatchEvent(new PopStateEvent('popstate')); };
                return (
                  <span onClick={_nav} style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    title={`Browse in ${venue.location}`}>
                    {venue.flag} {venue.location}
                  </span>
                );
              })()}</span>
              <span style={{ width: 1, height: 12, background: C.border2 }} />
              {venue.rating != null && venue.rating > 0 && <><Stars rating={venue.rating} size={13} />
              <span style={{ fontFamily: FB, fontSize: 13, fontWeight: 700, color: C.text }}>{venue.rating}</span>
              {venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>({venue.reviews} reviews)</span>}</> }
              {venue.verified && <span style={{ fontFamily: FB, fontSize: 11, color: C.green, fontWeight: 700 }}>✓ Verified</span>}
              {venue.contentScore != null && <TierBadge tier={getQualityTier(venue.contentScore)} showLabel={true} size="sm" />}
              {venue.publishedAt && (
                <>
                  <span style={{ width: 1, height: 12, background: C.border2 }} />
                  <span style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.3px" }}>
                    Published {new Date(venue.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </>
              )}
              {(venue.editorialApproved || venue.editorialFactChecked) && (
                <>
                  <span style={{ width: 1, height: 12, background: C.border2 }} />
                  <ApprovalIndicators approved={venue.editorialApproved} factChecked={venue.editorialFactChecked} layout="horizontal" />
                </>
              )}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "4px 10px", background: `${C.green}14`, border: `1px solid ${C.green}33` }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, flexShrink: 0, animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite" }} />
              <span style={{ fontFamily: FB, fontSize: 11, color: C.text, letterSpacing: "0.35px" }}>
                {venue.weddingsHosted ? `${venue.weddingsHosted}+ weddings hosted · Popular this season` : 'Popular this season · Limited peak dates remaining'}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {venue.responseTime && (
              <div style={{ fontFamily: FB, fontSize: 12, color: C.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name="zap" size={13} color={C.green} /> Replies in {venue.responseTime}
              </div>
            )}
            {venue.priceFrom && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: FD, fontSize: 26, color: C.gold }}>From {fmtPrice(venue.priceFrom, venue.priceCurrency)}</div>
                <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted }}>per event</div>
              </div>
            )}
            {venue.showcaseUrl && (
              <a href={venue.showcaseUrl}
                onClick={e => { e.preventDefault(); window.history.pushState(null, "", venue.showcaseUrl); window.dispatchEvent(new PopStateEvent('popstate')); }}
                style={{
                padding: "12px 18px", background: "#C47B3A", border: "1px solid #C47B3A",
                borderRadius: "var(--lwd-radius-input)",
                color: "#fff", fontFamily: FB, fontSize: 11, fontWeight: 700,
                letterSpacing: "0.8px", textTransform: "uppercase",
                textDecoration: "none", whiteSpace: "nowrap", transition: "all 0.2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#A8672E"; e.currentTarget.style.borderColor = "#A8672E"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#C47B3A"; e.currentTarget.style.borderColor = "#C47B3A"; }}
              >✦ Showcase</a>
            )}
            <button onClick={onEnquire} style={{
              padding: "13px 22px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
              color: "#ffffff", fontFamily: FB, fontSize: 12, fontWeight: 800,
              letterSpacing: "1.2px", textTransform: "uppercase", cursor: "pointer",
              whiteSpace: "nowrap", transition: "opacity 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >Begin Enquiry</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HERO STYLE 4: VIDEO (YouTube / Vimeo) ───────────────────────────────────
function HeroVideo({ venue, onEnquire }) {
  const C = useT();
  const [muted, setMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const { type, heroId, credit: videoCredit } = venue.video || {};
  const isMobile = useIsMobile();

  // No valid video → render Cinematic as fallback
  if (!type || !heroId) return <HeroCinematic venue={venue} onEnquire={onEnquire} />;

  // Hero image shown first while the video loads, prefer dedicated hero image
  const posterImg = venue.heroImgs?.[0] || venue.imgs?.[0] || '';

  // Sound is toggled by REMOUNTING the iframe with mute baked into the src (not by
  // calling the live player's mute()/unMute() over postMessage) — on this hero that API
  // call is a silent no-op (verified: isMuted() stays true even called directly on the
  // player), while a fresh src with mute=0 reliably works. Vimeo already used this
  // pattern; YouTube now matches it via the `key` on the iframe below.
  const embedUrl = type === "youtube"
    ? `https://www.youtube-nocookie.com/embed/${heroId}?autoplay=1&mute=${muted ? 1 : 0}&controls=0&rel=0&modestbranding=1&loop=1&playlist=${heroId}&showinfo=0&iv_load_policy=3&cc_load_policy=0&cc_lang_pref=none&disablekb=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
    : muted
      ? `https://player.vimeo.com/video/${heroId}?autoplay=1&loop=1&muted=1&background=1&texttrack=false`
      : `https://player.vimeo.com/video/${heroId}?autoplay=1&loop=1&muted=0&controls=0&background=0&transparent=0&texttrack=false`;

  // Listen for YouTube API "onReady" / first state-change so we know playback
  // has started. For Vimeo, the iframe onLoad is close enough.
  useEffect(() => {
    if (type !== "youtube" || !heroId) return;
    // Drive the hero through the real YouTube IFrame API. A raw enablejsapi=1
    // iframe IGNORES postMessage "command" calls until it receives the API's
    // "listening" handshake, so unloadModule was being silently dropped and
    // auto-captions leaked through on videos that have them. The API guarantees
    // the player is ready before we call unloadModule, which reliably removes
    // caption/cc modules, even auto-generated tracks, and re-kills on loop.
    let capTimer = null;
    let cancelled = false;
    const killCaps = (p) => {
      try {
        p.unloadModule("captions"); p.unloadModule("cc");
        p.setOption("captions", "track", {}); p.setOption("cc", "track", {});
      } catch { /* player not ready yet */ }
    };
    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { try { prev && prev(); } catch {} resolve(window.YT); };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement("script");
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
    });
    ensureApi().then((YT) => {
      if (cancelled || !iframeRef.current || !YT?.Player) return;
      try {
        playerRef.current = new YT.Player(iframeRef.current, {
          events: {
            onReady: (e) => {
              // Enforce the intended state defensively; the src already encodes it
              // (mute=${muted?1:0}), this just guards against autoplay-policy edge cases.
              try { if (muted) e.target.mute(); else e.target.unMute(); } catch {}
              killCaps(e.target);
              setVideoReady(true);
              if (!capTimer) capTimer = setInterval(() => killCaps(e.target), 1500);
            },
            onStateChange: (e) => {
              if (e.data === 1) { setVideoReady(true); killCaps(e.target); }
            },
          },
        });
      } catch { /* attach failed; poster fallback timers still reveal */ }
    });
    return () => {
      cancelled = true;
      if (capTimer) clearInterval(capTimer);
      try { playerRef.current?.destroy?.(); } catch {}
      playerRef.current = null;
    };
  }, [type, heroId, muted]); // re-attach after each remount (key={embedUrl} below recreates the iframe)

  // Vimeo hero: the &texttrack=false URL param disables captions, but also call
  // the Player API disableTextTrack once the iframe loads, for stubborn videos.
  useEffect(() => {
    if (type !== "vimeo") return;
    const disable = () => {
      const win = iframeRef.current?.contentWindow;
      if (win) win.postMessage(JSON.stringify({ method: "disableTextTrack" }), "*");
    };
    const t1 = setTimeout(disable, 1500);
    const t2 = setTimeout(disable, 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [type]);

  // Fallback: if YouTube never fires the playing message, reveal after 3s.
  // If nothing loads at all after 8s, mark as failed so poster stays.
  useEffect(() => {
    if (videoReady || videoFailed) return;
    const reveal = setTimeout(() => setVideoReady(true), 3000);
    const fail   = setTimeout(() => { if (!videoReady) setVideoFailed(true); }, 8000);
    return () => { clearTimeout(reveal); clearTimeout(fail); };
  }, [videoReady, videoFailed]);

  const toggleSound = () => {
    // YouTube: no live player.mute()/unMute() call here — verified that no-ops on this
    // hero. Sound is controlled by the mute=${muted?1:0} param in embedUrl (above) plus
    // the key={embedUrl} on the iframe, which remounts it with the correct state.
    if (type === "vimeo" && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ method: muted ? "setVolume" : "setVolume", value: muted ? 1 : 0 }), "*"
      );
    }
    setMuted(m => !m);
  };

  return (
    <div style={{ position: "relative", height: "62vh", overflow: "hidden", background: "#111" }}>
      {/* Poster image, visible immediately, fades out once video plays */}
      {posterImg && (
        <img
          src={vpImg(posterImg, 1800)}
          alt={venue.name || ""}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
            opacity: videoReady ? 0 : 1,
            transition: "opacity 1.2s ease",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}

      {/* iframe scaled 120% to hide YouTube edge UI */}
      <div style={{
        position: "absolute",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "120%", height: "120%",
        pointerEvents: "none",
      }}>
        <iframe
          key={embedUrl}
          ref={iframeRef}
          src={embedUrl}
          style={{ width: "100%", height: "100%", border: "none" }}
          allow="autoplay; fullscreen; picture-in-picture"
          title="Venue hero video"
          onLoad={() => { if (type !== "youtube") setVideoReady(true); }}
        />
      </div>

      {/* Gradient overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.72) 100%)",
        pointerEvents: "none",
      }} />

      {/* Sound on/off toggle, only visible when video is playing */}
      {videoReady && !videoFailed && <button
        onClick={toggleSound}
        title={muted ? "Turn sound on" : "Turn sound off"}
        style={{
          position: "absolute", bottom: 56, right: 40, zIndex: 10,
          width: 44, height: 44, borderRadius: "50%",
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.2s, transform 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,0,0,0.65)"; e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.45)"; e.currentTarget.style.transform = "scale(1)"; }}
      >
        {muted ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        )}
      </button>}

      {/* Videographer credit, subtle, bottom-right corner of the hero */}
      {videoReady && !videoFailed && videoCredit && (
        <div style={{
          position: "absolute", bottom: 14, right: 18, zIndex: 9,
          fontSize: 10.5, letterSpacing: "0.06em",
          color: "rgba(255,255,255,0.72)",
          textShadow: "0 1px 3px rgba(0,0,0,0.55)",
          fontStyle: "italic", pointerEvents: "none", userSelect: "none",
        }}>
          Film by {videoCredit}
        </div>
      )}

      {/* Content, same layout as Cinematic */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "0 40px 52px", animation: "fadeUp 0.8s ease both" }}>
        {venue.featured && (
          <div style={{ display: "inline-flex", marginBottom: 12, padding: "4px 12px", border: `1px solid ${C.gold}`, background: "rgba(157,135,62,0.18)" }}>
            <span style={{ fontFamily: FB, fontSize: 10, color: "#fff", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>✦ Editor's Pick</span>
          </div>
        )}
        <h1 style={{ fontFamily: FD, fontSize: "clamp(38px, 5.3vw, 68px)", fontWeight: 400, color: "#fff", letterSpacing: "-0.5px", lineHeight: 1.05, marginBottom: 14 }}>{venue.name}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 22 }}>
          <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{(() => {
                const _path = getLocationPath(venue);
                if (!_path) return <span>{venue.flag} {venue.location}</span>;
                const _nav = () => { window.history.pushState(null, '', _path); window.dispatchEvent(new PopStateEvent('popstate')); };
                return (
                  <span onClick={_nav} style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    title={`Browse in ${venue.location}`}>
                    {venue.flag} {venue.location}
                  </span>
                );
              })()}</span>
          <span style={{ width: 1, height: 12, background: "rgba(255,255,255,0.3)" }} />
          <Stars rating={venue.rating} size={13} />
          <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>{venue.rating}</span>
          {venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.5)" }}>({venue.reviews} reviews)</span>}
          {venue.verified && <span style={{ fontFamily: FB, fontSize: 11, color: "#4ade80", fontWeight: 700 }}>✓ Verified</span>}
          {venue.contentScore != null && <TierBadge tier={getQualityTier(venue.contentScore)} showLabel={true} size="sm" />}
        </div>
        {(venue.editorialApproved || venue.editorialFactChecked) && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <ApprovalIndicators approved={venue.editorialApproved} factChecked={venue.editorialFactChecked} layout="horizontal" />
            {venue.editorialApproved && venue.editorialLastReviewedAt && (
              <FreshnessText lastReviewedAt={venue.editorialLastReviewedAt} color="rgba(255,255,255,0.55)" fontSize={11} />
            )}
          </div>
        )}
        {/* Published / Updated date, WordPress-style editorial timestamp */}
        {venue.publishedAt && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.45)", letterSpacing: "0.3px" }}>
              Published {new Date(venue.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            {venue.updatedAt && new Date(venue.updatedAt) > new Date(venue.publishedAt) && (
              <>
                <span style={{ width: 1, height: 10, background: "rgba(255,255,255,0.15)" }} />
                <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.3px" }}>
                  Updated {new Date(venue.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </>
            )}
          </div>
        )}
        {/* Urgency signal, desktop only */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", background: "rgba(0,0,0,0.32)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.13)" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", flexShrink: 0, boxShadow: "0 0 0 0 rgba(74,222,128,0.7)", animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite" }} />
              <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.82)", letterSpacing: "0.35px" }}>
                {venue.weddingsHosted ? `${venue.weddingsHosted}+ weddings hosted · Popular this season` : 'Popular this season · Limited peak dates remaining'}
              </span>
            </span>
          </div>
        )}
        {/* Hero CTAs */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={onEnquire} style={{
            padding: "14px 28px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
            color: "#ffffff", fontFamily: FB, fontSize: 13, fontWeight: 800,
            letterSpacing: "1.2px", textTransform: "uppercase", cursor: "pointer", transition: "opacity 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >Begin Enquiry</button>
          {venue.showcaseUrl && (
            <a href={venue.showcaseUrl}
              onClick={e => { e.preventDefault(); window.history.pushState(null, "", venue.showcaseUrl); window.dispatchEvent(new PopStateEvent('popstate')); }}
              style={{
                padding: "13px 22px", background: "#C47B3A", border: "1px solid #C47B3A",
                borderRadius: "var(--lwd-radius-input)", color: "#fff",
                fontFamily: FB, fontSize: 13, fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase",
                textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#A8672E"; e.currentTarget.style.borderColor = "#A8672E"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#C47B3A"; e.currentTarget.style.borderColor = "#C47B3A"; }}
            >✦ Showcase</a>
          )}
          {/* Price + response time, desktop only */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {venue.priceFrom && (
                <span style={{ fontFamily: FD, fontSize: 26, fontWeight: 400, color: "#fff", lineHeight: 1 }}>
                  From {fmtPrice(venue.priceFrom, venue.priceCurrency)}
                </span>
              )}
              {venue.priceFrom && venue.responseTime && (
                <span style={{ width: 1, height: 11, background: "rgba(255,255,255,0.2)", display: "inline-block" }} />
              )}
              {venue.responseTime && (
                <span style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.6)", letterSpacing: "0.2px" }}>
                  Replies in {venue.responseTime}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── STICKY TAB NAV ───────────────────────────────────────────────────────────
const IS_VENUE = (v) => v.categorySlug === 'wedding-venues';
const TABS = [
  { key: 'overview',     label: 'Overview',       show: (v) => true },
  { key: 'gallery',      label: 'Gallery',         show: (v) => (v.gallery?.length || 0) > 0 },
  { key: 'capacity',     label: 'Spaces',          show: (v) => IS_VENUE(v) && (v.spaces?.length || 0) > 0 },
  { key: 'rooms',        label: 'Rooms',           show: (v) => IS_VENUE(v) && (v.accommodation?.totalRooms > 0 || v.accommodation?.description) },
  { key: 'dining',       label: 'Dining',          show: (v) => IS_VENUE(v) && (v.dining?.description || v.dining?.style) },
  { key: 'pricing',      label: 'Pricing',         show: (v) => (v.exclusiveUse?.enabled !== false && v.exclusiveUse) || v.priceFrom || v.pricingBody },
  { key: 'availability', label: 'Availability',    show: (v) => (v.notices?.length || 0) > 0 || v.availabilityBody },
  { key: 'virtual',      label: 'Virtual Try-On',  gold: true, show: (v) => v.categorySlug === 'bridal-boutiques' && v.bridal_virtual_tryon },
  { key: 'reviews',      label: 'Reviews',         show: (v) => true },
  { key: 'faqs',         label: 'FAQs',            show: (v) => true },
  { key: 'venue-type',   label: 'Venue Type',      show: (v) => IS_VENUE(v) && (v.venueType?.primaryType || (v.categories?.length || 0) > 0) },
  { key: 'things-to-do', label: 'Things to Do',   show: (v) => (v.experiences?.length || 0) > 0 },
];

// ─── BREADCRUMB BAR ───────────────────────────────────────────────────────────
function BreadcrumbBar({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const cs  = venue.countrySlug  || venue.country_slug  || '';
  const rs  = venue.regionSlug   || venue.region_slug   || '';
  const cat = venue.categorySlug || venue.category_slug || '';
  if (isMobile) return null;

  const navigate = (path) => {
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const crumbs = [
    { label: 'Home', path: '/' },
    cs && venue.country ? { label: venue.country, path: `/${cs}` } : null,
    cs && rs && venue.region ? { label: venue.region, path: `/${cs}/${rs}` } : null,
    cat ? { label: slugToLabel(cat), path: cs && rs ? `/${cs}/${rs}/${cat}` : `/${cat}` } : null,
  ].filter(Boolean);

  const linkStyle = {
    color: 'rgba(255,255,255,0.7)', cursor: 'pointer', transition: 'color 0.15s',
    textDecoration: 'none',
  };

  return (
    <div style={{
      background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%)',
      padding: '24px 40px 14px',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
        fontFamily: "'Nunito','Inter',sans-serif", fontSize: 12,
      }}>
        {crumbs.map((crumb) => (
          <span key={crumb.path} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              style={linkStyle}
              onClick={() => navigate(crumb.path)}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            >
              {crumb.label}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, userSelect: 'none' }}>›</span>
          </span>
        ))}
        <span style={{ color: '#fff', fontWeight: 600 }}>{venue.name}</span>
      </div>
    </div>
  );
}

function StickyTabNav({ venue, activeTab, onTabClick, saved, setSaved, onAddCompare, compareList = [] }) {
  const C = useT();
  const isMobile = useIsMobile();
  const visibleTabs = TABS.filter(t => t.show(venue));

  if (isMobile) {
    return (
      <div data-tab-nav style={{
        position: 'sticky', top: 60, zIndex: 50,
        backgroundColor: C.navBg || C.bg, borderBottom: `1px solid ${C.border}`,
        padding: '10px 20px',
        backdropFilter: 'blur(12px)',
      }}>
        <select
          value={activeTab}
          onChange={e => onTabClick(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px',
            fontSize: 14, fontFamily: 'inherit',
            border: `1px solid ${C.border}`,
            borderRadius: 'var(--lwd-radius-input, 3px)',
            backgroundColor: C.surface,
            color: C.text,
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23999' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 14px center',
            paddingRight: 40,
            cursor: 'pointer',
          }}
        >
          {visibleTabs.map(t => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div data-tab-nav style={{
      position: 'sticky', top: 60, zIndex: 50,
      backgroundColor: C.navBg || C.bg,
      backdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '0 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', flex: 1 }}>
        {visibleTabs.map((t, i) => {
          const active = activeTab === t.key;
          const baseColor = t.gold ? C.gold : C.textLight;
          return (
            <div key={t.key} style={{ display: 'flex', alignItems: 'stretch' }}>
              <button
                onClick={() => onTabClick(t.key)}
                style={{
                  flexShrink: 0,
                  padding: '0 18px',
                  height: 48,
                  border: 'none',
                  borderBottom: `2px solid ${active ? C.gold : 'transparent'}`,
                  backgroundColor: 'transparent',
                  color: active ? C.gold : baseColor,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: active || t.gold ? 700 : 500,
                  letterSpacing: '0.01em',
                  cursor: 'pointer',
                  transition: 'color 0.15s, border-bottom-color 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.color = C.gold; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.color = baseColor; }}
              >
                {t.label}
              </button>
              {i < visibleTabs.length - 1 && (
                <div style={{ width: '1px', backgroundColor: `${C.border}`, opacity: 0.5, alignSelf: 'center', height: '20px' }} />
              )}
            </div>
          );
        })}
        </div>
        {/* Venue actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, paddingLeft: 16 }}>
          {[
            { label: saved ? '♥  Saved' : '♡  Save', action: () => setSaved(s => !s), active: saved },
            { label: '⊕  Compare', action: onAddCompare, active: compareList.length > 0 },
            { label: '↗  Share', action: () => {}, active: false },
          ].map(btn => (
            <button key={btn.label} onClick={btn.action}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d39a21'; e.currentTarget.style.color = '#d39a21'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = btn.active ? '#d39a21' : 'rgba(0,0,0,0.15)'; e.currentTarget.style.color = btn.active ? '#d39a21' : '#666'; }}
              style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontFamily: "'Nunito','Inter',sans-serif",
                background: btn.active ? 'rgba(211,154,33,0.08)' : 'none',
                border: `1px solid ${btn.active ? '#d39a21' : 'rgba(0,0,0,0.15)'}`,
                borderRadius: 'var(--lwd-radius-input)',
                color: btn.active ? '#d39a21' : '#666',
                cursor: 'pointer', transition: 'all 0.2s',
              }}>{btn.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SECTION SIDE IMAGE ──────────────────────────────────────────────────────
// Consistent 3:4 portrait block, same size for ALL sections. No layout shift.
function SectionSideImage({ src, alt = '' }) {
  if (!src) return null;
  return (
    <div style={{ width: 240, flexShrink: 0 }}>
      <img
        src={vpImg(src, 500)}
        alt={alt}
        loading="lazy"
        style={{
          display: 'block',
          width: 240,
          aspectRatio: '3/4',
          objectFit: 'cover',
          borderRadius: 2,
        }}
      />
    </div>
  );
}

// Wrapper that puts content + side image side by side on desktop
function SectionLayout({ children, sideImg, isMobile }) {
  return (
    <div style={{
      display: 'flex',
      gap: 48,
      alignItems: 'flex-start',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {!isMobile && <SectionSideImage src={sideImg} />}
    </div>
  );
}

// ─── HERO WRAPPER, style switcher ───────────────────────────────────────────
function Hero({ venue, heroStyle, setHeroStyle, onEnquire, onBack }) {
  const C = useT();
  // Only show Video option if the venue has video configured
  const styles = [
    { key: "cinematic", label: "Cinematic" },
    { key: "split",     label: "Split" },
    { key: "magazine",  label: "Magazine" },
    ...(venue.video?.type ? [{ key: "video", label: "▶ Video" }] : []),
  ];
  return (
    <div>
      {heroStyle === "cinematic" && <HeroCinematic venue={venue} onEnquire={onEnquire} onBack={onBack} />}
      {heroStyle === "split"     && <HeroSplit venue={venue} onEnquire={onEnquire} />}
      {heroStyle === "magazine"  && <HeroMagazine venue={venue} onEnquire={onEnquire} />}
      {heroStyle === "video"     && <HeroVideo venue={venue} onEnquire={onEnquire} />}
    </div>
  );
}

// ─── STATS STRIP ─────────────────────────────────────────────────────────────
function StatsStrip({ venue, nextEvent, onEventClick }) {
  const C = useT();

  // Capacity, explicit venue-level max, then fall back to largest per-space figure
  const maxCeremony  = venue.capacity?.ceremony || venue.capacity?.standing || null;
  const maxDinner    = venue.capacity?.dinner || venue.capacity?.seated || null;
  const spaceMax     = venue.spaces?.length
    ? Math.max(...venue.spaces.map(s =>
        Math.max(s.capacityCeremony || 0, s.capacityReception || 0, s.capacityDining || 0, s.capacityStanding || 0)
      ))
    : 0;
  const maxGuests    = maxCeremony || maxDinner || (spaceMax > 0 ? spaceMax : null);

  // Accommodation
  const sleepsValue  = venue.accommodation?.maxOvernightGuests ?? venue.accommodation?.maxGuests ?? null;
  const totalRooms   = venue.accommodation?.totalRooms ?? null;

  // Spaces
  const spacesCount  = venue.spaces?.length || null;

  // Ceremony styles, derived from spaces tags or venue.ceremonyStyles
  const ceremonyTypes = (() => {
    const from = venue.ceremonyStyles || [];
    if (from.length) return from.slice(0, 2).join(" & ");
    if (!venue.spaces?.length) return null;
    const tags = new Set();
    venue.spaces.forEach(s => {
      if (s.setting?.toLowerCase().includes("beach"))   tags.add("Beach");
      if (s.setting?.toLowerCase().includes("garden"))  tags.add("Garden");
      if (s.setting?.toLowerCase().includes("chapel"))  tags.add("Chapel");
      if (s.setting?.toLowerCase().includes("terrace")) tags.add("Terrace");
      if (s.indoor !== undefined) tags.add(s.indoor ? "Indoor" : "Outdoor");
    });
    const arr = [...tags].slice(0, 2);
    return arr.length ? arr.join(" & ") : null;
  })();

  // Prestige tier label
  const TIER_LABELS = {
    'ultra-luxury': 'Ultra Luxury', 'ultra_luxury': 'Ultra Luxury', 'ultraluxury': 'Ultra Luxury',
    'luxury':   'Luxury',
    'premium':  'Premium',
    'boutique': 'Boutique',
  };
  const tierRaw   = venue.luxuryTier || '';
  const tierLabel = TIER_LABELS[tierRaw?.toLowerCase?.().replace(/\s+/g, '-')] || tierRaw || null;

  // Member-since year (lives on owner sub-object)
  const memberSinceYear = venue.owner?.memberSince || null;

  const isVenue   = venue.categorySlug === 'wedding-venues';
  const isPlanner = venue.categorySlug === 'wedding-planners';

  // Planner pricing model label
  const PLANNER_PRICING = {
    flat_fee: 'Flat Fee', percentage: '% of Budget',
    tiered: 'Tiered Packages', hybrid: 'Flat Fee + %', day_rate: 'Day Rate',
  };
  const plannerPricingLabel = PLANNER_PRICING[venue.plannerPricingModel] || venue.plannerPricingModel || null;

  // Format planner starting-from: if the stored value is a plain number,
  // prepend the listing's currency symbol. If it already contains a symbol
  // or non-numeric text, use it as-is.
  const formatPlannerPrice = (raw, currency) => {
    if (!raw) return null;
    const stripped = String(raw).replace(/[,\s]/g, '');
    if (/^\d+(\.\d+)?$/.test(stripped)) {
      const sym = currency || '€';
      return `${sym}${Number(stripped).toLocaleString()}`;
    }
    return String(raw);
  };
  const plannerStartingFromFormatted = formatPlannerPrice(venue.plannerStartingFrom, venue.priceCurrency);

  const stats = [
    // ── Planner stats (Starting From first) ──────────────────────────────────
    {
      label: "Starting From",
      value: plannerStartingFromFormatted,
      sub: "per wedding",
      hide: !isPlanner || !venue.plannerStartingFrom,
    },
    {
      label: "Team",
      value: venue.plannerTeamSize || null,
      sub: "",
      hide: !isPlanner || !venue.plannerTeamSize,
      small: true,
    },
    {
      label: "Years Active",
      value: venue.plannerYearsActive || null,
      sub: "in the industry",
      hide: !isPlanner || !venue.plannerYearsActive,
    },
    {
      label: "Per Year",
      value: venue.plannerWeddingsYear ? `${venue.plannerWeddingsYear} weddings` : null,
      sub: "",
      hide: !isPlanner || !venue.plannerWeddingsYear,
      small: true,
    },
    {
      label: "Pricing Model",
      value: plannerPricingLabel,
      sub: "",
      hide: !isPlanner || !plannerPricingLabel,
      small: true,
    },
    // ── Venue stats ───────────────────────────────────────────────────────────
    {
      label: "Starting From",
      value: venue.priceFrom ? fmtPrice(venue.priceFrom, venue.priceCurrency) : null,
      sub: "per event",
      hide: !isVenue || !venue.priceFrom,
    },
    {
      label: "Up to",
      value: maxGuests ? `${maxGuests}` : null,
      sub: "guests",
      hide: !isVenue || !maxGuests,
    },
    {
      label: "Rooms",
      value: totalRooms ? totalRooms.toString() : null,
      sub: sleepsValue ? `sleeps ${sleepsValue}` : "for guests",
      hide: !isVenue || !totalRooms,
    },
    {
      label: "Event Spaces",
      value: spacesCount ? spacesCount.toString() : null,
      sub: "ceremony settings",
      hide: !isVenue || !spacesCount,
    },
    {
      label: "Ceremony Style",
      value: ceremonyTypes,
      sub: "settings available",
      hide: !isVenue || !ceremonyTypes,
      small: true,
    },
    {
      label: "Exclusive Use",
      value: venue.exclusiveUse ? "Available" : null,
      sub: "full venue buyout",
      hide: !isVenue || !venue.exclusiveUse,
      small: true,
    },
    // ── Universal stats ───────────────────────────────────────────────────────
    {
      label: "Prestige Level",
      value: tierLabel,
      sub: isVenue ? "positioning" : null,
      hide: !tierLabel,
      small: true,
    },
    {
      label: "Est.",
      value: memberSinceYear,
      sub: "years of excellence",
      hide: !memberSinceYear,
      small: true,
    },
    {
      label: "Rating",
      value: venue.rating ? `${venue.rating}★` : null,
      sub: `${venue.reviews || 0} verified reviews`,
      hide: !venue.rating,
    },
  ].filter(s => !s.hide);

  const nextEventDate = nextEvent ? formatEventDate(nextEvent.startDate) : null;

  return (
    <div className="vp-stats-outer" style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 clamp(16px, 5vw, 40px)" }}>
      <div style={{ display: "flex", overflowX: "auto", gap: 0, scrollbarWidth: "none" }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            flex: "0 0 auto", padding: "18px 28px",
            borderRight: `1px solid ${C.border}`,
            minWidth: 120,
          }}>
            <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: 5 }}>{s.label}</div>
            <div style={{ fontFamily: s.small ? FB : FD, fontSize: s.small ? 15 : 22, fontWeight: s.small ? 600 : 500, color: C.gold, lineHeight: 1, letterSpacing: s.small ? "-0.2px" : "normal" }}>{s.value}</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.textLight, marginTop: 4 }}>{s.sub}</div>
          </div>
        ))}

        {/* Next Open Day, only when an upcoming event exists */}
        {nextEvent && nextEventDate && (
          <div
            onClick={() => {
              trackEvent({ eventType: 'event_cta_click', entityType: 'event', entityId: nextEvent.id, metadata: { surface: 'stats_strip', eventTitle: nextEvent.title } })
              onEventClick?.(nextEvent)
            }}
            style={{
              flex: "0 0 auto", padding: "18px 28px", minWidth: 140,
              cursor: "pointer", position: "relative",
              transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.hover || 'rgba(211,154,33,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: 5 }}>
              Next Open Day
            </div>
            <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.gold, lineHeight: 1.2 }}>
              {nextEventDate}
            </div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.gold, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4, opacity: 0.85 }}>
              Register <span style={{ fontSize: 10 }}>→</span>
            </div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
              animation: 'lwd-pulse-bar 2.5s ease-in-out infinite',
            }} />
          </div>
        )}

        {/* Virtual Try-On entry point, bridal boutiques only */}
        {venue.categorySlug === 'bridal-boutiques' && (venue.bridal_virtual_tryon || venue.bridalVirtualTryon) && (
          <div
            onClick={() => { window.location.href = `/fashion-studio/${venue.slug || venue.id}`; }}
            style={{
              flex: "0 0 auto", padding: "18px 28px", minWidth: 160,
              cursor: "pointer", position: "relative",
              borderLeft: `1px solid ${C.border}`,
              transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(211,154,33,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: 5 }}>
              ✦ Available
            </div>
            <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 700, color: C.gold, lineHeight: 1.2 }}>
              Virtual Try-On
            </div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.gold, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4, opacity: 0.85 }}>
              Open Fitting Room <span style={{ fontSize: 10 }}>→</span>
            </div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
              animation: 'lwd-pulse-bar 2.5s ease-in-out infinite',
            }} />
          </div>
        )}
      </div>
      <style>{`
        @keyframes lwd-pulse-bar {
          0%, 100% { opacity: 0.3; }
          50%       { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── Wedding Intelligence panel ──────────────────────────────────────────────
// Surfaces the venue's confirmed wedding-capability signals (the same graph
// Aura scores against) as a public grid of ✓ badges. Reads the camelCased
// capability columns off the venue object (mapListingFromDb snake→camel).
// Rule: show only CONFIRMED capabilities, true booleans / meaningful enum
// values. NULL (unknown) and false are hidden, so a partially-filled venue
// reads as a curated set of strengths rather than an empty form. Empty groups
// are dropped; the whole panel hides below a minimum signal count.
function WeddingIntelligencePanel({ venue: v }) {
  const C = useT();

  const fmtCurfew = (t) => {
    if (!t) return null;
    if (String(t).toLowerCase() === 'extended') return 'Extended music licence';
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(t).trim());
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const lbl = (h === 0 || h === 24) ? 'midnight' : (h === 12 ? 'noon' : (h < 12 ? `${h}am` : `${h - 12}pm`));
    return `Music until ${lbl}`;
  };

  const corkageOK = ['wine', 'full_drinks', 'case_by_case'].includes(v.corkagePolicy);
  const rooms = Number(v.accommodationRoomsCount) || 0;
  const transferMin = Number(v.airportTransferMinutes) || 0;
  const airport = (v.nearestAirport && String(v.nearestAirport).trim()) || '';

  // Each group: { title, items: [label, ...] }. Falsy items filtered out.
  const groups = [
    {
      title: 'Stay & exclusivity',
      items: [
        (v.exclusiveHire === true || v.exclusiveUseEnabled === true) && 'Exclusive use',
        rooms > 0 ? `${rooms} bedrooms on site` : (v.accommodationOnSite === true && 'On-site accommodation'),
      ],
    },
    {
      title: 'Ceremony',
      items: [
        v.ceremonyCivil === true && 'Civil ceremonies',
        v.ceremonyReligious === true && 'Religious ceremonies',
        v.ceremonyOutdoor === true && 'Outdoor ceremonies',
      ],
    },
    {
      title: 'Catering & dining',
      items: [
        v.cateringInHouse === true && 'In-house catering',
        v.cateringExternalAllowed === true && 'External caterers welcome',
        v.cateringHalal === true && 'Halal catering',
        v.cateringKosher === true && 'Kosher catering',
      ],
    },
    {
      title: 'Drinks',
      items: [
        corkageOK && 'Corkage allowed',
        v.byoAlcoholAllowed === true && 'Bring your own wine',
        v.barPolicy === 'open' && 'Open bar',
      ],
    },
    {
      title: 'Music & entertainment',
      items: [
        v.liveBandAllowed === true && 'Live bands welcome',
        v.djAllowed === true && 'DJs welcome',
        v.outdoorMusicAllowed === true && 'Outdoor music',
        fmtCurfew(v.musicCurfewIndoor),
        v.afterPartyAvailable === true && 'After-party spaces',
        v.fireworksPolicy === 'allowed' && 'Fireworks allowed',
        v.fireworksPolicy === 'silent_only' && 'Silent fireworks',
        v.dronePolicy === 'allowed' && 'Drone shows',
      ],
    },
    {
      title: 'Wedding planning',
      items: [
        v.inHousePlanner === true && 'In-house wedding planner',
        v.coordinatorOnSite === true && 'On-site coordinator',
        v.externalPlannerAllowed === true && 'Your own planner welcome',
      ],
    },
    {
      title: 'Wedding programme',
      items: [
        v.weddingFormat === 'three_day' && 'Three-day weddings',
        v.weddingFormat === 'multi_day' && 'Multi-day celebrations',
        v.weddingFormat === 'weekend' && 'Weekend weddings',
        v.weddingFormat === 'full_buyout' && 'Full venue buyout',
        v.welcomeEventCapability === true && 'Welcome events',
        v.poolPartyCapability === true && 'Pool parties',
        v.beachPartyCapability === true && 'Beach parties',
        v.weddingBreakfastSpace === true && 'Wedding breakfast',
        v.farewellBrunchCapability === true && 'Farewell brunch',
      ],
    },
    {
      title: 'Arrival & logistics',
      items: [
        transferMin > 0 ? `${transferMin} min from ${airport || 'the airport'}` : (airport && `Near ${airport}`),
        v.privateJetAirportNearby === true && 'Private jet access',
        v.helicopterTransferAvailable === true && 'Helicopter transfers',
        v.boatTransferAvailable === true && 'Boat access',
        v.shuttleServiceAvailable === true && 'Guest shuttles',
        v.valetParking === true && 'Valet parking',
      ],
    },
  ]
    .map(g => ({ ...g, items: g.items.filter(Boolean) }))
    .filter(g => g.items.length > 0);

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  // Curation floor: a venue with almost nothing confirmed shouldn't show a
  // near-empty panel. Below the floor it reads as missing, not premium.
  if (total < 3) return null;

  return (
    <div className="vp-wi-outer" style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "26px 0 30px" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <span style={{ fontFamily: FB, fontSize: 10, letterSpacing: "1.4px", textTransform: "uppercase", color: C.gold }}>✦ Wedding Intelligence</span>
          <span style={{ fontFamily: FD, fontSize: 20, color: C.text, lineHeight: 1.1 }}>
            {total} confirmed {total === 1 ? 'capability' : 'capabilities'} for your celebration
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "18px 28px" }}>
          {groups.map((g, gi) => (
            <div key={gi}>
              <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 9 }}>{g.title}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {g.items.map((label, ii) => (
                  <span key={ii} style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontFamily: FB, fontSize: 12, color: C.text,
                    background: C.goldLight || 'rgba(157,135,62,0.07)',
                    border: `1px solid ${C.goldBorder || 'rgba(157,135,62,0.2)'}`,
                    borderRadius: 6, padding: "5px 11px", lineHeight: 1.3,
                  }}>
                    <span style={{ color: C.gold, fontSize: 11 }}>✓</span>{label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR: OWNER CARD ─────────────────────────────────────────────────────
function OwnerCard({ owner, venue }) {
  const C = useT();

  // Only render if owner data exists
  if (!owner || !owner.name) return null;

  return (
    <div style={{
      border: `1px solid ${C.border}`,
      background: C.surface,
      overflow: "hidden",
      marginBottom: 0,
    }}>
      {/* Gold top accent */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${C.gold}, ${C.green})` }} />

      <div style={{ padding: "20px 22px" }}>
        {/* Owner header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            {owner.photo ? (
              <img
                src={vpImg(owner.photo, 400)}
                alt={owner.name}
                style={{
                  width: 58, height: 58, borderRadius: "50%", objectFit: "cover",
                  border: `2px solid ${C.gold}`, display: "block",
                }}
              />
            ) : (
              <div style={{
                width: 58, height: 58, borderRadius: "50%",
                border: `2px solid ${C.gold}`, background: C.goldLight,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: FD, fontSize: 20, color: C.gold, letterSpacing: "-0.5px",
              }}>
                {(owner.name || "V").split(" ").map(w => w[0]).slice(0, 2).join("")}
              </div>
            )}
            {/* LWD verified dot, on by default; an editor can switch it off */}
            {owner.verified !== false && (
            <div style={{
              position: "absolute", bottom: 0, right: 0,
              width: 17, height: 17, borderRadius: "50%",
              background: C.gold, border: `2px solid ${C.surface}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9, color: "#fff", fontWeight: 700,
            }}>✓</div>
            )}
          </div>
          <div>
            <div style={{ fontFamily: FD, fontSize: 16, color: C.text, lineHeight: 1.2, marginBottom: 2 }}>{owner.name}</div>
            {owner.title && (
              <div style={{ fontFamily: FB, fontSize: 12, color: C.textLight, marginBottom: 1 }}>{owner.title}</div>
            )}
            {owner.memberSince && (
              <div style={{ fontFamily: FB, fontSize: 11, color: C.gold, fontWeight: 600, letterSpacing: "0.2px" }}>
                ✦ LWD Partner since {owner.memberSince}
              </div>
            )}
          </div>
        </div>

        {/* Quote, only render when bio has content */}
        {owner.bio && (
          <div style={{
            borderLeft: `2px solid ${C.goldBorder}`,
            paddingLeft: 14, marginBottom: 16,
          }}>
            <p style={{
              fontFamily: FD, fontSize: 13, fontStyle: "italic",
              color: C.textMid, lineHeight: 1.75, margin: 0,
            }}>"{owner.bio}"</p>
          </div>
        )}

        {/* Quick contact strip, visible whenever email / phone is known.
            Especially useful when no named contact has been added and the card
            is using the venue name as a fallback. */}
        {(owner.email || owner.phone || owner.whatsapp) && (
          <div style={{
            borderTop: `1px solid ${C.border}`,
            paddingTop: 14, marginBottom: 14,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {owner.phone && (
              <a href={`tel:${owner.phone}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: FB, fontSize: 12, color: C.text,
                textDecoration: 'none', lineHeight: 1.3,
              }}>
                <Icon name="phone" size={12} color={C.gold} />
                <span>{owner.phone}</span>
              </a>
            )}
            {owner.email && (
              <a href={`mailto:${owner.email}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: FB, fontSize: 12, color: C.text,
                textDecoration: 'none', lineHeight: 1.3, wordBreak: 'break-all',
              }}>
                <Icon name="mail" size={12} color={C.gold} />
                <span>{owner.email}</span>
              </a>
            )}
            {owner.whatsapp && (
              <a
                href={`https://wa.me/${String(owner.whatsapp).replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: FB, fontSize: 12, color: C.text,
                  textDecoration: 'none', lineHeight: 1.3,
                }}
              >
                <Icon name="phone" size={12} color={C.gold} />
                <span>WhatsApp</span>
              </a>
            )}
            {owner.website && (
              <a
                href={owner.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: FB, fontSize: 12, color: C.text,
                  textDecoration: 'none', lineHeight: 1.3, wordBreak: 'break-all',
                }}
              >
                <Icon name="globe" size={12} color={C.gold} />
                <span>Website</span>
              </a>
            )}
          </div>
        )}

        {/* Social links, same icon set/pattern as the Contact & Location section */}
        {owner.social && Object.keys(owner.social).length > 0 && (() => {
          const SOCIAL_ICONS = {
            instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
            facebook:  'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
            linkedin:  'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
            tiktok:    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
            twitter:   'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
            pinterest: 'M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z',
            youtube:   'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
          };
          const links = Object.keys(SOCIAL_ICONS).filter(k => owner.social[k]);
          if (!links.length) return null;
          return (
            <div style={{
              borderTop: `1px solid ${C.border}`, paddingTop: 12, marginBottom: 14,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.7px", textTransform: "uppercase" }}>Follow</span>
              {links.map(key => (
                <a
                  key={key}
                  href={socialProfileUrl(key, owner.social[key])}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={key}
                  style={{ color: C.textMuted, transition: "color 0.2s", display: "flex" }}
                  onMouseEnter={e => e.currentTarget.style.color = C.gold}
                  onMouseLeave={e => e.currentTarget.style.color = C.textMuted}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
                    <path d={SOCIAL_ICONS[key]} />
                  </svg>
                </a>
              ))}
            </div>
          );
        })()}

        {/* Stats grid */}
        <div className="vp-stats-strip" style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: 0, borderTop: `1px solid ${C.border}`,
          paddingTop: 14,
        }}>
          {[
            { label: "Responds in",   value: venue.responseTime || owner.responseTime || null                },
            { label: "Response rate", value: (venue.responseRate ? `${venue.responseRate}%` : owner.responseRate) || null },
            { label: "Weddings held", value: venue.weddingsHosted != null ? `${venue.weddingsHosted}+` : (owner.weddings ? `${owner.weddings}+` : null) },
            { label: "Experience",    value: owner.yearsExperience ? `${owner.yearsExperience} yrs` : null     },
            { label: "Partner since", value: owner.memberSince || null                                       },
          ].filter(s => s.value).map((s, i) => (
            <div key={s.label} style={{
              padding: "10px 0",
              borderRight: i % 2 === 0 ? `1px solid ${C.border}` : "none",
              paddingRight: i % 2 === 0 ? 16 : 0,
              paddingLeft: i % 2 === 1 ? 16 : 0,
            }}>
              <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontFamily: FD, fontSize: 17, color: C.text }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Languages spoken */}
        {owner.languages && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.5px", textTransform: "uppercase" }}>Languages</span>
            <span style={{ fontFamily: FB, fontSize: 12, color: C.text }}>{owner.languages}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SIDEBAR: MINI CONTACT ───────────────────────────────────────────────────
// ─── OPENING HOURS WIDGET ─────────────────────────────────────────────────────
function OpeningHoursWidget({ openingHours }) {
  const C = useT();
  if (!openingHours?.enabled || !openingHours?.hours) return null;
  const DAYS = [
    { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
  ];
  // Group consecutive days with same hours
  const rows = DAYS.map(d => ({ ...d, ...(openingHours.hours[d.key] || { type: 'closed' }) }));
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 16px' }}>
      <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 10 }}>Opening Hours</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map(r => (
          <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FB, fontSize: 12 }}>
            <span style={{ color: (r.type === 'closed' && !r.from) ? C.textMuted : C.text, width: 32 }}>{r.label}</span>
            <span style={{ color: r.type === 'closed' ? C.textMuted : (r.type === 'appointment' || r.type === 'by_appointment') ? C.gold : C.text }}>
              {r.type === 'closed'
                ? 'Closed'
                : (r.type === 'appointment' || r.type === 'by_appointment')
                  ? 'By appt.'
                  : (r.from && r.to)
                    ? `${r.from} – ${r.to}`
                    : 'Open'}
            </span>
          </div>
        ))}
      </div>
      {openingHours.note && (
        <div style={{ marginTop: 8, fontFamily: FB, fontSize: 11, color: C.textLight, lineHeight: 1.5 }}>{openingHours.note}</div>
      )}
    </div>
  );
}

// ─── PRESS FEATURES SIDEBAR CARD ──────────────────────────────────────────────
function PressSidebarCard({ items }) {
  const C = useT();
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      border: `1px solid ${C.border}`,
      background: C.surface,
      padding: '18px 20px',
    }}>
      <div style={{
        fontFamily: FB, fontSize: 9, color: C.textMuted,
        letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 14,
      }}>
        As Featured In
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.slice(0, 6).map((p, i) => {
          const headline = p.outlet || p.title || '';
          const body     = p.outlet && p.title ? p.title : '';
          const inner = (
            <div key={i} style={{
              borderLeft: `2px solid ${C.gold}40`,
              paddingLeft: 12,
            }}>
              <div style={{
                fontFamily: FD, fontSize: 14, color: C.text,
                lineHeight: 1.3, marginBottom: body || p.year ? 3 : 0,
              }}>
                {headline}
              </div>
              {body && (
                <div style={{
                  fontFamily: FB, fontSize: 11, color: C.textLight,
                  lineHeight: 1.4, fontStyle: 'italic',
                }}>
                  {body}
                </div>
              )}
              {p.year && (
                <div style={{
                  fontFamily: FB, fontSize: 10, color: C.gold,
                  letterSpacing: '0.5px', marginTop: 3, fontWeight: 600,
                }}>
                  {p.year}
                </div>
              )}
            </div>
          );
          return p.url
            ? (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', display: 'block' }}
              >
                {inner}
              </a>
            )
            : inner;
        })}
      </div>
    </div>
  );
}

// ─── AWARDS SIDEBAR CARD ──────────────────────────────────────────────────────
function AwardsSidebarCard({ items }) {
  const C = useT();
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      border: `1px solid ${C.border}`,
      background: C.surface,
      padding: '18px 20px',
    }}>
      <div style={{
        fontFamily: FB, fontSize: 9, color: C.textMuted,
        letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 14,
      }}>
        Awards & Recognition
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.slice(0, 8).map((a, i) => (
          <div key={i} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <div style={{
              flexShrink: 0, fontSize: 18, lineHeight: 1.2,
              filter: 'grayscale(0.1)',
            }}>
              {a.icon || '🏆'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FD, fontSize: 13, color: C.text,
                lineHeight: 1.35, marginBottom: a.issuer || a.year ? 3 : 0,
              }}>
                {a.award}
              </div>
              {(a.issuer || a.year) && (
                <div style={{
                  fontFamily: FB, fontSize: 11, color: C.textLight,
                  lineHeight: 1.4,
                }}>
                  {[a.issuer, a.year].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SidebarContact({ venue }) {
  const C = useT();
  const [exitConfig, setExitConfig] = useState(null);

  const handleWebsiteClick = () => {
    const url = toAbsoluteUrl(venue.contact.website);
    const trackData = { entityType: 'venue', entityId: venue.id, venueId: venue.id, linkType: 'website', url };
    if (!hasSeenModalThisSession(url)) {
      setExitConfig({ url, name: venue.name });
    } else {
      trackExternalClick(trackData);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface }}>
      {/* Mini map */}
      <div style={{ height: 160, overflow: "hidden", borderBottom: `1px solid ${C.border}` }}>
        <iframe
          title="Mini map"
          width="100%" height="160"
          style={{ display: "block", border: "none", marginTop: -40 }}
          loading="lazy"
          src={`https://maps.google.com/maps?q=${venue.contact.mapQuery}&output=embed&z=11`}
        />
      </div>
      {/* Address + quick actions */}
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontFamily: FB, fontSize: 12, color: C.textMid, lineHeight: 1.6, marginBottom: 12 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="pin" size={13} color={C.textMid} /> {venue.contact.addressFormatted}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <a href={`tel:${venue.contact.phone}`} style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "9px 8px", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)",
            fontFamily: FB, fontSize: 11, fontWeight: 700, color: C.text,
            textDecoration: "none", letterSpacing: "0.4px", textTransform: "uppercase",
            transition: "all 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border2; e.currentTarget.style.color = C.text; }}
          ><Icon name="phone" size={12} /> Call</a>
          <button onClick={venue.contact.website ? handleWebsiteClick : undefined} style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "9px 8px", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)",
            fontFamily: FB, fontSize: 11, fontWeight: 700, color: C.text,
            background: "none", cursor: "pointer",
            letterSpacing: "0.4px", textTransform: "uppercase",
            transition: "all 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border2; e.currentTarget.style.color = C.text; }}
          ><Icon name="globe" size={12} /> Website</button>
        </div>
      </div>
      {/* Opening hours */}
      <OpeningHoursWidget openingHours={venue.openingHours} />

      {exitConfig && (
        <ExternalLinkModal
          name={exitConfig.name}
          url={exitConfig.url}
          onClose={() => setExitConfig(null)}
          onContinue={() => {
            markModalSeen(exitConfig.url);
            trackExternalClick({ entityType: 'venue', entityId: venue.id, venueId: venue.id, linkType: 'website', url: exitConfig.url });
          }}
        />
      )}
    </div>
  );
}

// ─── NOTICE ENQUIRY MODAL ─────────────────────────────────────────────────────
const NOTICE_FORM_CONFIG = {
  "open-day": {
    heading: "Reserve Your Place",
    getContext: (n) => n.title,
    fields: [
      { key: "name",    label: "Full name",        type: "text",     required: true  },
      { key: "email",   label: "Email address",    type: "email",    required: true  },
      { key: "phone",   label: "Phone number",     type: "tel",      required: false },
      { key: "guests",  label: "Number attending", type: "select",   required: true,
        options: ["1 person", "2 people", "3 people", "4 people"] },
      { key: "message", label: "Any questions",    type: "textarea", required: false },
    ],
    submitLabel: "Reserve My Place",
    confirmHeading: "You're reserved.",
    confirmDetail: "We'll send confirmation to your email and look forward to welcoming you.",
  },
  "offer": {
    heading: "Enquire About This Offer",
    getContext: (n) => n.title,
    fields: [
      { key: "name",        label: "Full name",    type: "text",     required: true  },
      { key: "email",       label: "Email address",type: "email",    required: true  },
      { key: "phone",       label: "Phone number", type: "tel",      required: false },
      { key: "weddingDate", label: "Wedding date", type: "text",     required: false, placeholder: "e.g. September 2026" },
      { key: "guests",      label: "Guest count",  type: "text",     required: false, placeholder: "e.g. 120 guests" },
      { key: "message",     label: "Message",      type: "textarea", required: false },
    ],
    submitLabel: "Send Enquiry",
    confirmHeading: "Enquiry received.",
    confirmDetail: "Our team will be in touch within 2 hours to discuss your 2026 booking.",
  },
  "availability": {
    heading: "Enquire About This Date",
    getContext: (n) => `${n.title} (exclusive use inquiry)`,
    fields: [
      { key: "name",  label: "Full name",    type: "text",  required: true  },
      { key: "email", label: "Email address",type: "email", required: true  },
      { key: "phone", label: "Phone number", type: "tel",   required: true  },
      { key: "guests",label: "Guest count",  type: "text",  required: false, placeholder: "e.g. 80 guests" },
    ],
    note: "We respond within 2 hours. This date will not be held without a confirmed enquiry.",
    submitLabel: "Enquire About This Date",
    confirmHeading: "We're on it.",
    confirmDetail: "Our availability team will contact you within 2 hours. Please keep your phone nearby.",
  },
  "news": {
    heading: "Request Sample Menus",
    getContext: () => "Curated by Lorenzo Conti, Executive Chef",
    fields: [
      { key: "name",       label: "Full name",            type: "text",     required: true  },
      { key: "email",      label: "Email address",        type: "email",    required: true  },
      { key: "weddingDate",label: "Wedding date",         type: "text",     required: false, placeholder: "e.g. June 2026" },
      { key: "dietary",    label: "Dietary requirements", type: "textarea", required: false, placeholder: "Any allergies or preferences to note" },
    ],
    submitLabel: "Request Menus",
    confirmHeading: "Sample menus on their way.",
    confirmDetail: "Lorenzo's current seasonal menu will be sent to your inbox shortly.",
  },
};

function NoticeEnquiryModal({ notice, venueName, onClose }) {
  const C = useT();
  const cfg      = NOTICE_FORM_CONFIG[notice.type] || NOTICE_FORM_CONFIG["offer"];
  const noticeCfg = NOTICE_CONFIG[notice.type]    || NOTICE_CONFIG["news"];
  const [formData,   setFormData]   = useState({});
  const [sent,       setSent]       = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleChange = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => { setSent(true); setSubmitting(false); }, 900);
  };

  const inputBase = {
    width: "100%", background: C.bgAlt, border: `1px solid ${C.border}`,
    color: C.text, fontFamily: FB, fontSize: 13, padding: "10px 12px",
    borderRadius: "var(--lwd-radius-input)", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 4000,
        background: "rgba(0,0,0,0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(92vw, 480px)",
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderTop: `3px solid ${noticeCfg.accent}`,
          maxHeight: "90vh", overflowY: "auto",
          animation: "lwd-modal-in 0.25s ease",
        }}
      >
        {/* ── Image header ── */}
        {notice.img && (
          <div style={{ position: "relative", height: 170, overflow: "hidden", flexShrink: 0 }}>
            <img src={vpImg(notice.img)} alt={notice.title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)",
            }} />
            {/* Type badge */}
            <div style={{
              position: "absolute", top: 14, left: 18,
              fontSize: 9, fontFamily: FB, fontWeight: 700,
              letterSpacing: "1.4px", textTransform: "uppercase",
              color: noticeCfg.accent, borderLeft: `2px solid ${noticeCfg.accent}`, paddingLeft: 7,
            }}>{noticeCfg.label}</div>
            {/* Close */}
            <button onClick={onClose} style={{
              position: "absolute", top: 12, right: 14,
              background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "var(--lwd-radius-input)", color: "rgba(255,255,255,0.8)",
              width: 30, height: 30, cursor: "pointer",
              fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
            {/* Context line */}
            <div style={{
              position: "absolute", bottom: 14, left: 18, right: 50,
              fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.72)",
              fontStyle: "italic", letterSpacing: "0.3px",
            }}>{cfg.getContext(notice)}</div>
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ padding: "26px 26px 30px" }}>
          {!sent ? (
            <>
              <h3 style={{
                fontFamily: FD, fontSize: 21, fontWeight: 400,
                color: C.text, margin: "0 0 5px", lineHeight: 1.2,
              }}>{cfg.heading}</h3>
              <p style={{
                fontFamily: FB, fontSize: 11, color: C.textMuted,
                letterSpacing: "0.3px", margin: "0 0 22px",
              }}>{venueName} · {cfg.getContext(notice)}</p>

              {/* Urgency note (availability only) */}
              {cfg.note && (
                <div style={{
                  background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.22)",
                  borderRadius: "var(--lwd-radius-card)", padding: "10px 13px", marginBottom: 20,
                  fontFamily: FB, fontSize: 11, color: C.textLight, lineHeight: 1.55,
                }}>{cfg.note}</div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                {cfg.fields.map(f => (
                  <div key={f.key}>
                    <label style={{
                      display: "block", fontFamily: FB, fontSize: 10, fontWeight: 700,
                      letterSpacing: "1.2px", textTransform: "uppercase",
                      color: C.textMuted, marginBottom: 6,
                    }}>
                      {f.label}
                      {f.required && <span style={{ color: noticeCfg.accent, marginLeft: 3 }}>*</span>}
                    </label>
                    {f.type === "textarea" ? (
                      <textarea rows={3} required={f.required} placeholder={f.placeholder || ""}
                        value={formData[f.key] || ""}
                        onChange={e => handleChange(f.key, e.target.value)}
                        style={{ ...inputBase, resize: "vertical", minHeight: 76 }}
                        onFocus={e => e.target.style.borderColor = noticeCfg.accent}
                        onBlur={e  => e.target.style.borderColor = C.border}
                      />
                    ) : f.type === "select" ? (
                      <select required={f.required} value={formData[f.key] || ""}
                        onChange={e => handleChange(f.key, e.target.value)}
                        style={inputBase}
                        onFocus={e => e.target.style.borderColor = noticeCfg.accent}
                        onBlur={e  => e.target.style.borderColor = C.border}
                      >
                        <option value="">Select…</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} required={f.required}
                        placeholder={f.placeholder || ""}
                        value={formData[f.key] || ""}
                        onChange={e => handleChange(f.key, e.target.value)}
                        style={inputBase}
                        onFocus={e => e.target.style.borderColor = noticeCfg.accent}
                        onBlur={e  => e.target.style.borderColor = C.border}
                      />
                    )}
                  </div>
                ))}

                <button type="submit" disabled={submitting} style={{
                  marginTop: 4,
                  background: noticeCfg.accent, border: "none", borderRadius: "var(--lwd-radius-input)",
                  color: "#0f0d0a", padding: "13px 24px",
                  fontFamily: FB, fontSize: 11, fontWeight: 700,
                  letterSpacing: "1.2px", textTransform: "uppercase",
                  cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.7 : 1, transition: "opacity 0.2s", width: "100%",
                }}>{submitting ? "Sending…" : `${cfg.submitLabel} →`}</button>
              </form>
            </>
          ) : (
            /* ── Confirmation ── */
            <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
              <div style={{
                width: 54, height: 54, borderRadius: "50%",
                border: `2px solid ${noticeCfg.accent}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px", fontSize: 24, color: noticeCfg.accent,
              }}>✓</div>
              <h3 style={{
                fontFamily: FD, fontSize: 22, fontWeight: 400,
                color: C.text, margin: "0 0 10px",
              }}>{cfg.confirmHeading}</h3>
              <p style={{
                fontFamily: FB, fontSize: 13, color: C.textLight,
                lineHeight: 1.65, margin: "0 auto 26px", maxWidth: 300,
              }}>{cfg.confirmDetail}</p>
              <button onClick={onClose} style={{
                background: "none", border: `1px solid ${C.border}`,
                borderRadius: "var(--lwd-radius-input)", color: C.textMuted,
                padding: "10px 28px", fontFamily: FB, fontSize: 10,
                fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase",
                cursor: "pointer",
              }}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR: VENUE NOTICES ───────────────────────────────────────────────────
const NOTICE_CONFIG = {
  "open-day":     { label: "Open Day",         accent: "#d39a21",              bg: "rgba(211,154,33,0.07)",  icon: "🗓" },
  "offer":        { label: "Special Offer",     accent: "#d97706",              bg: "rgba(217,119,6,0.07)",   icon: "🏷" },
  "availability": { label: "Late Availability", accent: "#22c55e",              bg: "rgba(34,197,94,0.07)",   icon: "📅" },
  "news":         { label: "Estate News",        accent: "#d39a21",              bg: "rgba(211,154,33,0.07)",  icon: "📢" },
};

function SidebarNotices({ notices, venueName }) {
  const C = useT();
  // Only first notice open by default
  const [openIds,       setOpenIds]       = useState(() => new Set(notices?.length ? [notices[0].id] : []));
  const [enquiryNotice, setEnquiryNotice] = useState(null);
  if (!notices?.length) return null;

  const toggle = (id) => setOpenIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div style={{
      border: `1px solid ${C.border}`,
      background: C.surface,
    }}>
      {/* Header */}
      <div style={{
        padding: "14px 18px 12px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 10, color: C.gold }}>✦</span>
        <span style={{
          fontFamily: FB, fontSize: 10, fontWeight: 700,
          letterSpacing: "2px", textTransform: "uppercase", color: C.textMuted,
        }}>From {venueName}</span>
      </div>

      {/* Notices */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {notices.map((n, idx) => {
          const cfg = NOTICE_CONFIG[n.type] || NOTICE_CONFIG["news"];
          const isOpen = openIds.has(n.id);
          return (
            <div
              key={n.id}
              style={{
                borderBottom: idx < notices.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              {/* Notice row */}
              <button
                onClick={() => toggle(n.id)}
                style={{
                  width: "100%", textAlign: "left", background: "none", border: "none",
                  padding: "14px 18px", cursor: "pointer", display: "flex",
                  alignItems: "flex-start", gap: 12, transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = cfg.bg}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                {/* Type badge */}
                <span style={{
                  flexShrink: 0, marginTop: 1,
                  fontSize: 9, fontFamily: FB, fontWeight: 700,
                  letterSpacing: "1.2px", textTransform: "uppercase",
                  color: cfg.accent, whiteSpace: "nowrap",
                  borderLeft: `2px solid ${cfg.accent}`,
                  paddingLeft: 6,
                }}>
                  {cfg.label}
                </span>

                {/* Title + chevron */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: FB, fontSize: 12, fontWeight: 600,
                    color: C.text, lineHeight: 1.35,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{n.title}</div>
                  {n.expires && (
                    <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                      Expires {n.expires}
                    </div>
                  )}
                </div>

                <span style={{
                  flexShrink: 0, fontSize: 10, color: C.textMuted,
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.25s", lineHeight: 1, marginTop: 2,
                }}>▾</span>
              </button>

              {/* Expanded detail */}
              <div style={{
                overflow: "hidden",
                maxHeight: isOpen ? 420 : 0,
                transition: "max-height 0.4s ease",
              }}>
                <div style={{
                  borderTop: `1px solid ${C.border}`,
                }}>
                  {/* Image, only for news type */}
                  {n.img && (
                    <div style={{ position: "relative", overflow: "hidden" }}>
                      <img
                        src={vpImg(n.img, 800)}
                        alt={n.imgCaption || n.title}
                        style={{ width: "100%", display: "block", maxHeight: 160, objectFit: "cover" }}
                      />
                      {n.imgCaption && (
                        <div style={{
                          position: "absolute", bottom: 0, left: 0, right: 0,
                          padding: "16px 12px 8px",
                          background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                          fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.75)",
                          fontStyle: "italic", letterSpacing: "0.3px",
                        }}>{n.imgCaption}</div>
                      )}
                    </div>
                  )}
                  <div style={{ padding: "12px 18px 16px" }}>
                  {n.type === "news" && (
                    <p style={{
                      fontFamily: FD, fontSize: 15, fontWeight: 600,
                      color: C.text, lineHeight: 1.35, margin: "0 0 8px",
                    }}>{n.title}</p>
                  )}
                  <p style={{
                    fontFamily: FB, fontSize: 12, color: C.textLight,
                    lineHeight: 1.65, margin: "0 0 12px",
                  }}>{n.detail}</p>
                  <button
                    onClick={e => { e.stopPropagation(); setEnquiryNotice(n); }}
                    style={{
                      background: cfg.accent, border: "none", borderRadius: "var(--lwd-radius-input)",
                      color: "#0f0d0a",
                      padding: "8px 16px", fontFamily: FB, fontSize: 10,
                      fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase",
                      cursor: "pointer", transition: "opacity 0.2s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                  >{n.cta} →</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Notice enquiry modal */}
      {enquiryNotice && (
        <NoticeEnquiryModal
          notice={enquiryNotice}
          venueName={venueName}
          onClose={() => setEnquiryNotice(null)}
        />
      )}
    </div>
  );
}

// ─── SIDEBAR: INSTAGRAM TEASER ───────────────────────────────────────────────
function SidebarInstagram({ venue }) {
  const C = useT();
  // Placeholder tiles, real impl pulls from Instagram Basic Display API
  const posts = [
    { id: 0, src: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=240&q=75" },
    { id: 1, src: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=240&q=75" },
    { id: 2, src: "https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=240&q=75" },
    { id: 3, src: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=240&q=75" },
    { id: 4, src: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=240&q=75" },
    { id: 5, src: "https://images.unsplash.com/photo-1464808322410-1a934aab61e5?w=240&q=75" },
  ];
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, padding: "16px 16px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>◈</span>
          <span style={{ fontFamily: FB, fontSize: 12, fontWeight: 700, color: C.text }}>@{venue.contact.website.replace("www.", "").split(".")[0]}</span>
        </div>
        <a href="#" style={{ fontFamily: FB, fontSize: 11, color: C.gold, textDecoration: "none", fontWeight: 600 }}>Follow →</a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
        {posts.map(p => (
          <div key={p.id} style={{ overflow: "hidden", aspectRatio: "1", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.querySelector("img").style.transform = "scale(1.06)"}
            onMouseLeave={e => e.currentTarget.querySelector("img").style.transform = "scale(1)"}
          >
            <img src={vpImg(p.src)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.4s ease" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LEAD FORM (SIDEBAR) ──────────────────────────────────────────────────────
function LeadForm({ venue }) {
  const C = useT();
  const [step, setStep] = useState(0); // 0=idle, 1=date, 2=guests, 3=details, 4=done
  const [form, setForm] = useState({ date: "", guests: 80, name: "", email: "", message: "" });

  const steps = [
    {
      key: "date", label: "When is your wedding?",
      content: (
        <div>
          <label style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Wedding date</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            style={{ width: "100%", padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt || C.bg, color: C.text, fontFamily: FB, fontSize: 14, outline: "none" }} />
        </div>
      )
    },
    {
      key: "guests", label: "How many guests?",
      content: (
        <div>
          <label style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", display: "block", marginBottom: 8 }}>
            Guests, <span style={{ color: C.gold, fontWeight: 700 }}>{form.guests}</span>
          </label>
          <input type="range" min={20} max={200} value={form.guests} onChange={e => setForm(f => ({ ...f, guests: +e.target.value }))}
            style={{ width: "100%", accentColor: C.gold }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontFamily: FB, fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            <span>20</span><span>200</span>
          </div>
        </div>
      )
    },
    {
      key: "details", label: "Your details",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[{ key: "name", ph: "Your name", type: "text" }, { key: "email", ph: "Email address", type: "email" }].map(f => (
            <input key={f.key} type={f.type} placeholder={f.ph} value={form[f.key]} onChange={e => setForm(fm => ({ ...fm, [f.key]: e.target.value }))}
              style={{ width: "100%", padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt || C.bg, color: C.text, fontFamily: FB, fontSize: 14, outline: "none" }} />
          ))}
          <textarea placeholder="Anything specific you'd like to know? (optional)" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={3}
            style={{ width: "100%", padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt || C.bg, color: C.text, fontFamily: FB, fontSize: 13, outline: "none", resize: "none" }} />
        </div>
      )
    },
  ];

  const canAdvance = () => {
    if (step === 1) return !!form.date;
    if (step === 3) return !!(form.name && form.email);
    return true;
  };

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      boxShadow: C.shadowLg,
      padding: 28,
    }}>
      {/* Price + rating */}
      <div style={{ marginBottom: 20 }}>
        {venue.priceFrom && (
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
            <span style={{ fontFamily: FD, fontSize: 29, fontWeight: 700, color: C.gold }}>From {fmtPrice(venue.priceFrom, venue.priceCurrency)}</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Stars rating={venue.rating} size={12} />
          <span style={{ fontFamily: FB, fontSize: 13, fontWeight: 700, color: C.text }}>{venue.rating}</span>
          {venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>· {venue.reviews} reviews</span>}
        </div>
        {venue.responseTime && (
          <div style={{ marginTop: 8, fontFamily: FB, fontSize: 12, color: C.green, fontWeight: 600 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="zap" size={13} color={C.green} /> Responds within {venue.responseTime}</span>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: C.border, marginBottom: 20 }} />

      {/* Step progress */}
      {step > 0 && step < 4 && (
        <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{ flex: 1, height: 3, background: s <= step ? C.gold : C.border, transition: "background 0.3s" }} />
          ))}
        </div>
      )}

      {/* Step content */}
      {step === 0 && (
        <>
          <button onClick={() => setStep(1)} style={{
            width: "100%", padding: "15px 20px",
            background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)", color: "#fff",
            fontFamily: FB, fontSize: 14, fontWeight: 800, letterSpacing: "1.2px",
            textTransform: "uppercase", cursor: "pointer",
            transition: "all 0.2s",
          }}>Begin Enquiry</button>
          {venue.showcaseUrl && (
            <a href={venue.showcaseUrl}
              onClick={e => { e.preventDefault(); window.history.pushState(null, "", venue.showcaseUrl); window.dispatchEvent(new PopStateEvent('popstate')); }}
              style={{
              display: "block", textAlign: "center", marginTop: 12,
              padding: "11px", background: "#C47B3A", border: "1px solid #C47B3A",
              borderRadius: "var(--lwd-radius-input)",
              fontFamily: FB, fontSize: 12, fontWeight: 700,
              color: "#fff", textDecoration: "none",
              letterSpacing: "0.8px", textTransform: "uppercase", transition: "all 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "#A8672E"; e.currentTarget.style.borderColor = "#A8672E"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#C47B3A"; e.currentTarget.style.borderColor = "#C47B3A"; }}
            >✦ View Full Showcase →</a>
          )}
        </>
      )}

      {step > 0 && step < 4 && (
        <div style={{ animation: "fadeUp 0.3s ease" }}>
          <div style={{ fontFamily: FD, fontSize: 18, fontWeight: 500, color: C.text, marginBottom: 16 }}>{steps[step - 1].label}</div>
          {steps[step - 1].content}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} style={{
                flex: 1, padding: "11px", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)", background: "none",
                color: C.textLight, fontFamily: FB, fontSize: 13, cursor: "pointer",
              }}>← Back</button>
            )}
            <button onClick={() => step < 3 ? setStep(s => s + 1) : setStep(4)}
              disabled={!canAdvance()}
              style={{
                flex: 2, padding: "11px",
                background: canAdvance() ? C.gold : C.border,
                border: "none", borderRadius: "var(--lwd-radius-input)",
                color: canAdvance() ? "#fff" : C.textMuted,
                fontFamily: FB, fontSize: 13, fontWeight: 700, cursor: canAdvance() ? "pointer" : "not-allowed",
                letterSpacing: "0.5px", transition: "all 0.2s",
              }}>{step < 3 ? "Continue →" : "Send Enquiry →"}</button>
          </div>
          <div style={{ textAlign: "center", marginTop: 12, fontFamily: FB, fontSize: 11, color: C.textMuted }}>
            Step {step} of 3
          </div>
        </div>
      )}

      {step === 4 && (
        <div style={{ textAlign: "center", animation: "fadeUp 0.4s ease", padding: "8px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
          <div style={{ fontFamily: FD, fontSize: 20, color: C.text, marginBottom: 8 }}>Enquiry Sent</div>
          <div style={{ fontFamily: FB, fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>
            Your message has been sent. The venue typically replies within {venue.responseTime}.
          </div>
          <button onClick={() => setStep(0)} style={{
            marginTop: 16, padding: "9px 20px",
            border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)", background: "none",
            color: C.textLight, fontFamily: FB, fontSize: 12, cursor: "pointer",
          }}>Make another enquiry</button>
        </div>
      )}

      {/* Save + Compare */}
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        {["♡ Save", "⊕ Compare"].map(a => (
          <button key={a} style={{
            flex: 1, padding: "9px", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)", background: "none",
            color: C.textLight, fontFamily: FB, fontSize: 12, cursor: "pointer",
            transition: "all 0.2s",
          }}>{a}</button>
        ))}
      </div>
    </div>
  );
}

// ─── IMAGE GALLERY, Coco-style 3-photo preview ──────────────────────────────
function ImageGallery({ gallery, onOpenLight }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [allOpen, setAllOpen] = useState(false);
  const scrollRef = useRef(null);
  const preview = gallery.slice(0, 6);
  const remaining = gallery.length - 5;

  return (
    <section id="gallery" style={{ marginBottom: 56 }}>
      <SectionHeading title="Gallery" subtitle={`${gallery.length} curated photograph${gallery.length === 1 ? "" : "s"}`} />

      {/* ── Mobile: horizontal photo slider ── */}
      {isMobile && !allOpen && (
        <div>
          <div ref={scrollRef} className="vp-gallery-slider" style={{
            display: "flex", gap: 8, overflowX: "auto", scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
            marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
          }}>
            {preview.map((img, i) => (
              <div key={img.id} onClick={() => onOpenLight(i)} style={{
                flex: "0 0 280px", scrollSnapAlign: "start",
                overflow: "hidden", cursor: "pointer", position: "relative",
                borderRadius: 3, height: 340,
              }}>
                <img src={vpImg(img.src)} alt={img.alt || ""} loading="lazy" style={{
                  width: "100%", height: "100%", objectFit: "cover", display: "block",
                }} />
                {/* Photo number badge */}
                <div style={{
                  position: "absolute", bottom: 10, left: 10,
                  background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)",
                  borderRadius: 20, padding: "3px 10px",
                  fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.8)",
                  letterSpacing: "0.5px",
                }}>{i + 1} / {gallery.length}</div>
              </div>
            ))}
            {/* "View all" final card */}
            <div onClick={() => setAllOpen(true)} style={{
              flex: "0 0 180px", scrollSnapAlign: "start",
              borderRadius: 3, height: 340, cursor: "pointer",
              background: C.surface, border: `1px solid ${C.border}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <div style={{ fontFamily: FD, fontSize: 36, color: C.gold, fontWeight: 400, lineHeight: 1 }}>+{remaining}</div>
              <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "1px", textTransform: "uppercase" }}>View all</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Desktop: grid preview ── */}
      {!isMobile && !allOpen && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "auto auto", gap: 6 }}>
            <div onClick={() => onOpenLight(0)} style={{
              gridRow: "1 / 3", overflow: "hidden", cursor: "pointer",
              position: "relative", minHeight: 360,
            }}
              onMouseEnter={e => e.currentTarget.querySelector("img").style.transform = "scale(1.04)"}
              onMouseLeave={e => e.currentTarget.querySelector("img").style.transform = "scale(1)"}>
              <img src={vpImg(gallery[0]?.src)} alt={gallery[0]?.alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.7s ease" }} />
            </div>
            <div onClick={() => onOpenLight(1)} style={{ overflow: "hidden", cursor: "pointer", position: "relative", aspectRatio: "4/3" }}
              onMouseEnter={e => e.currentTarget.querySelector("img").style.transform = "scale(1.04)"}
              onMouseLeave={e => e.currentTarget.querySelector("img").style.transform = "scale(1)"}>
              <img src={vpImg(gallery[1]?.src)} alt={gallery[1]?.alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.7s ease" }} />
            </div>
            <div onClick={() => setAllOpen(true)} style={{ overflow: "hidden", cursor: "pointer", position: "relative", aspectRatio: "4/3" }}
              onMouseEnter={e => e.currentTarget.querySelector("img").style.transform = "scale(1.04)"}
              onMouseLeave={e => e.currentTarget.querySelector("img").style.transform = "scale(1)"}>
              <img src={vpImg(gallery[2]?.src)} alt={gallery[2]?.alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.7s ease" }} />
              <div style={{
                position: "absolute", inset: 0, background: "rgba(0,0,0,0.52)",
                display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6,
              }}>
                <span style={{ fontFamily: FD, fontSize: 32, color: "#fff", fontWeight: 400 }}>+{gallery.length - 3}</span>
                <span style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.85)", letterSpacing: "1px", textTransform: "uppercase" }}>View all photos</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={() => setAllOpen(true)} style={{
              background: "none", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)",
              color: C.textLight, fontFamily: FB, fontSize: 12, fontWeight: 600,
              padding: "8px 18px", cursor: "pointer", letterSpacing: "0.3px",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border2; e.currentTarget.style.color = C.textLight; }}>
              View all {gallery.length} photographs →
            </button>
          </div>
        </div>
      )}

      {/* ── Full gallery, masonry ── */}
      {allOpen && (
        <div style={{ animation: "fadeUp 0.4s ease" }}>
          <div style={{ columns: isMobile ? 2 : 3, columnGap: 6, marginBottom: 16 }}>
            {gallery.map((img, i) => (
              <div key={img.id} onClick={() => onOpenLight(i)} style={{
                breakInside: "avoid", marginBottom: 6, overflow: "hidden", cursor: "pointer",
                borderRadius: 2,
              }}
                onMouseEnter={e => e.currentTarget.querySelector("img").style.transform = "scale(1.04)"}
                onMouseLeave={e => e.currentTarget.querySelector("img").style.transform = "scale(1)"}>
                <img src={vpImg(img.src)} alt="" loading="lazy" style={{ width: "100%", height: "auto", display: "block", transition: "transform 0.7s ease" }} />
              </div>
            ))}
          </div>
          <button onClick={() => setAllOpen(false)} style={{
            background: "none", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)",
            color: C.textLight, fontFamily: FB, fontSize: 12,
            padding: "8px 18px", cursor: "pointer",
          }}>← Show less</button>
        </div>
      )}
    </section>
  );
}

// ─── LIGHTBOX ────────────────────────────────────────────────────────────────
function Lightbox({ gallery, idx, onClose, onPrev, onNext, setLightIdx, engagement }) {
  const isMobile = useIsMobile();
  const [autoPlay, setAutoPlay] = useState(false);
  const [hovPrev, setHovPrev]   = useState(false);
  const [hovNext, setHovNext]   = useState(false);
  const [viewAll, setViewAll]   = useState(false);
  const [copied, setCopied]     = useState(false);
  const [likedMap, setLikedMap] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [commentText, setCommentText] = useState("");
  const thumbRef = useRef(null);

  const photo = gallery[idx];
  const pg = photo?.photographer;
  const eng = engagement?.[photo?.id] || { likes: 0, comments: [] };
  const isLiked = likedMap[photo?.id] || false;
  const likeCount = eng.likes + (isLiked ? 1 : 0);
  const allComments = [...(eng.comments || []), ...(commentsMap[photo?.id] || [])];

  // Keyboard nav
  useEffect(() => {
    const fn = (e) => {
      const tag = e.target?.tagName;
      const isFormField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable;
      if (isFormField) return;
      if (e.key === "Escape") { if (viewAll) setViewAll(false); else onClose(); }
      if (e.key === "ArrowLeft" && !viewAll) onPrev();
      if (e.key === "ArrowRight" && !viewAll) onNext();
      if (e.key === " ") { e.preventDefault(); setAutoPlay((a) => !a); }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose, onPrev, onNext, viewAll]);

  // Auto-play slideshow, stop if idx is null (lightbox closed)
  useEffect(() => {
    if (!autoPlay || idx === null) return;
    const timer = setInterval(() => onNext(), 3000);
    return () => clearInterval(timer);
  }, [autoPlay, idx, onNext]);

  // Reset internal state when lightbox is closed
  useEffect(() => {
    if (idx === null) {
      setAutoPlay(false);
      setViewAll(false);
    }
  }, [idx]);

  // Scroll active thumbnail into view, scroll the strip container, not the page
  useEffect(() => {
    if (!thumbRef.current) return;
    const strip = thumbRef.current;
    const active = strip.children[idx];
    if (!active) return;
    const stripRect  = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const offset = activeRect.left - stripRect.left - (stripRect.width / 2) + (activeRect.width / 2);
    strip.scrollBy({ left: offset, behavior: "smooth" });
  }, [idx]);

  // Copy link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href + `#photo-${photo.id}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Share via email
  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Photo, ${venue.name || 'Wedding Venue'}`);
    const body = encodeURIComponent(`Check out this photo:\n${photo.alt || ""}\n\nPhotographer: ${pg?.name || "Unknown"}\n${window.location.href}#photo-${photo.id}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  // Share via Pinterest
  const handlePinterestShare = () => {
    const url = encodeURIComponent(window.location.href);
    const media = encodeURIComponent(photo.src);
    const desc = encodeURIComponent(`${photo.alt || "Villa Rosanova"}, Photo by ${pg?.name || ""}`);
    window.open(`https://pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${desc}`, "_blank", "width=600,height=400");
  };

  // Share via Facebook
  const handleFacebookShare = () => {
    const url = encodeURIComponent(window.location.href + `#photo-${photo.id}`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank", "width=600,height=400");
  };

  // Share via Instagram (copy link + open Instagram)
  const handleInstagramShare = () => {
    navigator.clipboard.writeText(`${photo.alt || "Villa Rosanova"}, Photo by ${pg?.name || ""}\n${window.location.href}#photo-${photo.id}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window.open("https://www.instagram.com/", "_blank");
    });
  };

  if (idx === null) return null;

  const navBtn = (dir, hov, setHov) => (
    <button
      onClick={(e) => { e.stopPropagation(); dir === "prev" ? onPrev() : onNext(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label={dir === "prev" ? "Previous photo" : "Next photo"}
      style={{
        position: "absolute", top: "50%", transform: "translateY(-50%)",
        [dir === "prev" ? "left" : "right"]: 16,
        width: 44, height: 44, borderRadius: "50%",
        background: hov ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.4)",
        border: `1px solid ${hov ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.12)"}`,
        backdropFilter: "blur(4px)",
        color: hov ? "#fff" : "rgba(255,255,255,0.7)",
        cursor: "pointer", fontSize: 18,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s", zIndex: 10,
      }}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );

  // Small share button helper
  const shareBtn = (label, icon, onClick, hov) => (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
        padding: "7px 0", background: "none",
        border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--lwd-radius-input)",
        color: "rgba(255,255,255,0.5)", fontFamily: FB, fontSize: 10,
        fontWeight: 600, letterSpacing: "0.4px", cursor: "pointer",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; e.currentTarget.style.color = "#fff"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span> {label}
    </button>
  );

  // View All grid overlay
  if (viewAll) {
    return (
      <div style={{
        position: "fixed", inset: 0, background: "rgba(5,4,3,0.97)", zIndex: 2000,
        overflow: "auto", padding: "60px 40px 40px",
      }}>
        {/* Header */}
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 10,
          padding: "16px 40px", background: "rgba(5,4,3,0.92)", backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontFamily: FD, fontSize: 18, color: "#f5f2ec" }}>
            All Photos <span style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.4)", marginLeft: 8 }}>{gallery.length} photographs</span>
          </div>
          <button onClick={() => setViewAll(false)} aria-label="Close grid view"
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "var(--lwd-radius-input)",
              color: "rgba(255,255,255,0.55)", width: 34, height: 34, cursor: "pointer",
              fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>
        {/* Grid */}
        <div style={{ columns: 4, columnGap: 8 }}>
          {gallery.map((img, i) => (
            <div key={img.id} onClick={() => { setViewAll(false); setLightIdx?.(i); }}
              style={{ breakInside: "avoid", marginBottom: 8, cursor: "pointer", position: "relative", overflow: "hidden" }}
            >
              <img src={vpImg(img.src)} alt={img.alt || ""} style={{ width: "100%", display: "block", transition: "transform 0.5s, opacity 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.opacity = "0.85"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.opacity = "1"; }}
              />
              {/* Tags overlay on hover */}
              {img.tags && (
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  padding: "24px 8px 6px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  display: "flex", flexWrap: "wrap", gap: 3, opacity: 0, transition: "opacity 0.3s",
                }}
                  className="lwd-tag-overlay"
                >
                  {img.tags.slice(0, 3).map(t => (
                    <span key={t} style={{
                      fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.8)",
                      background: "rgba(255,255,255,0.12)", padding: "2px 6px",
                      borderRadius: "var(--lwd-radius-input)", letterSpacing: "0.3px",
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} onMouseDown={e => { if (e.target.tagName === "BUTTON" || e.target.closest("button")) e.preventDefault(); }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.96)", zIndex: 2000,
      display: "flex", flexDirection: "column",
    }}>
      {/* Top bar */}
      <div onClick={(e) => e.stopPropagation()} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: FD, fontSize: 16, color: "#f5f2ec" }}>
            {idx + 1} <span style={{ color: "rgba(255,255,255,0.3)" }}>/ {gallery.length}</span>
          </span>
          {pg && <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.35)" }}>📷 {pg.name}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Auto-play toggle, hidden on mobile */}
          {!isMobile && (
            <button onClick={() => setAutoPlay((a) => !a)}
              style={{
                background: autoPlay ? "rgba(211,154,33,0.12)" : "none",
                border: `1px solid ${autoPlay ? "rgba(211,154,33,0.4)" : "rgba(255,255,255,0.15)"}`,
                borderRadius: "var(--lwd-radius-input)", color: autoPlay ? "#d39a21" : "rgba(255,255,255,0.5)",
                padding: "6px 14px", cursor: "pointer", fontFamily: FB, fontSize: 11,
                fontWeight: 600, letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: 6,
                transition: "all 0.2s",
              }}
            >
              {autoPlay ? "❚❚" : "▶"} {autoPlay ? "Pause" : "Slideshow"}
            </button>
          )}
          {/* View All, hidden on mobile */}
          {!isMobile && (
            <button onClick={() => setViewAll(true)}
              style={{
                background: "none", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "var(--lwd-radius-input)",
                color: "rgba(255,255,255,0.5)", padding: "6px 14px", cursor: "pointer",
                fontFamily: FB, fontSize: 11, fontWeight: 600, letterSpacing: "0.5px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
            >
              ⊞ View All
            </button>
          )}
          {/* Close */}
          <button onClick={onClose} aria-label="Close gallery"
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "var(--lwd-radius-input)",
              color: "rgba(255,255,255,0.55)", width: 34, height: 34, cursor: "pointer",
              fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d39a21"; e.currentTarget.style.color = "#d39a21"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
          >✕</button>
        </div>
      </div>

      {/* Main content: image + info panel */}
      <div onClick={(e) => e.stopPropagation()} style={{
        flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row",
        minHeight: 0, overflow: isMobile ? "auto" : "hidden",
      }}>
        {/* Image area */}
        <div onClick={onClose} style={{
          flex: isMobile ? "none" : 1, display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "hidden", minWidth: 0,
          minHeight: isMobile ? "50vh" : undefined,
        }}>
          <img
            key={idx}
            src={vpImg(photo.src, 1800)}
            alt={photo.alt || ""}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "100%", maxWidth: "100%", objectFit: "contain",
              animation: "fadeIn 0.3s ease",
            }}
          />
          {/* Nav arrows, on mobile show smaller inline arrows */}
          {isMobile ? (
            <>
              <button onClick={(e) => { e.stopPropagation(); onPrev(); }} style={{
                position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)",
                border: "none", color: "#fff", fontSize: 16, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>‹</button>
              <button onClick={(e) => { e.stopPropagation(); onNext(); }} style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)",
                border: "none", color: "#fff", fontSize: 16, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>›</button>
            </>
          ) : (
            <>
              {navBtn("prev", hovPrev, setHovPrev)}
              {navBtn("next", hovNext, setHovNext)}
            </>
          )}

          {/* Auto-play progress bar */}
          {autoPlay && (
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
              background: "rgba(255,255,255,0.08)",
            }}>
              <div style={{
                height: "100%", background: "#d39a21",
                animation: "lightbox-progress 3s linear infinite",
              }} />
            </div>
          )}
        </div>

        {/* ── Info panel (right on desktop, below on mobile) ── */}
        <div style={{
          width: isMobile ? "100%" : 280, flexShrink: 0,
          borderLeft: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)",
          borderTop: isMobile ? "1px solid rgba(255,255,255,0.06)" : "none",
          background: "rgba(10,8,6,0.6)", backdropFilter: "blur(8px)",
          display: "flex", flexDirection: "column",
          overflowY: "auto", padding: isMobile ? "16px 16px 40px" : "20px 18px",
        }}>
          {/* Image description */}
          {photo.alt && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>Description</div>
              <div style={{ fontFamily: FB, fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.6, fontWeight: 300 }}>{photo.alt}</div>
            </div>
          )}

          {/* Photographer details */}
          {pg && (
            <div style={{
              marginBottom: 20, padding: "14px 12px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "var(--lwd-radius-card)",
            }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>Photographer</div>
              <div style={{ fontFamily: FD, fontSize: 16, color: "#f5f2ec", marginBottom: 3 }}>{pg.name}</div>
              <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}><Icon name="pin" size={11} color="rgba(255,255,255,0.45)" /> {pg.area}</div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pg.instagram && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon name="culture" size={11} color="rgba(255,255,255,0.5)" />
                    <span style={{ fontFamily: FB, fontSize: 11, color: "#d39a21" }}>{pg.instagram}</span>
                  </div>
                )}
                {pg.website && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon name="globe" size={11} color="rgba(255,255,255,0.5)" />
                    <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{pg.website}</span>
                  </div>
                )}
                {pg.camera && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, opacity: 0.5 }}>📷</span>
                    <span style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{pg.camera}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags for AI search */}
          {photo.tags && photo.tags.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Tags</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {photo.tags.map(t => (
                  <span key={t} style={{
                    fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.55)",
                    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)",
                    padding: "3px 8px", borderRadius: "var(--lwd-radius-input)", letterSpacing: "0.3px",
                    transition: "all 0.2s", cursor: "default",
                  }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Sharing options */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Share</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {shareBtn(copied ? "Copied!" : "Copy", "📋", handleCopyLink)}
              {shareBtn("Pin", "📌", handlePinterestShare)}
              {shareBtn("Email", "✉", handleEmailShare)}
              {shareBtn("Facebook", "📘", handleFacebookShare)}
              {shareBtn("Instagram", "📷", handleInstagramShare)}
            </div>
          </div>

          {/* ── Appreciations & Reflections ── */}
          <div style={{ marginBottom: 20 }}>
            {/* Appreciation + reflection count */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <button onClick={() => setLikedMap(m => ({ ...m, [photo.id]: !m[photo.id] }))}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: isLiked ? "rgba(211,154,33,0.08)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isLiked ? "rgba(211,154,33,0.25)" : "rgba(255,255,255,0.06)"}`,
                  borderRadius: "var(--lwd-radius-input)", padding: "6px 12px", cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <span style={{ fontSize: 13, transition: "transform 0.2s", transform: isLiked ? "scale(1.15)" : "scale(1)" }}>{isLiked ? "❤️" : "🤍"}</span>
                <span style={{ fontFamily: FB, fontSize: 10, fontWeight: 600, color: isLiked ? "#d39a21" : "rgba(255,255,255,0.45)", letterSpacing: "0.3px" }}>
                  {likeCount} {likeCount === 1 ? "Appreciation" : "Appreciations"}
                </span>
              </button>
              {allComments.length > 0 && (
                <span style={{ fontFamily: FB, fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: "0.3px" }}>
                  {allComments.length} {allComments.length === 1 ? "Reflection" : "Reflections"}
                </span>
              )}
            </div>

            {/* Reflection input */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Share Your Reflection</div>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your reflection on this moment..."
                style={{
                  width: "100%", minHeight: 52, resize: "vertical",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "var(--lwd-radius-input)", padding: "10px 12px",
                  fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.65)",
                  lineHeight: 1.6, outline: "none", fontStyle: "italic",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(211,154,33,0.3)"; e.currentTarget.style.fontStyle = "normal"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; if (!commentText) e.currentTarget.style.fontStyle = "italic"; }}
              />
              {commentText.trim() && (
                <button
                  onClick={() => {
                    if (!commentText.trim()) return;
                    setCommentsMap(m => ({
                      ...m,
                      [photo.id]: [...(m[photo.id] || []), { name: "You", text: commentText.trim(), date: new Date().toISOString().slice(0, 10) }],
                    }));
                    setCommentText("");
                  }}
                  style={{
                    marginTop: 6, width: "100%", padding: "8px 0",
                    background: "rgba(211,154,33,0.06)", border: "1px solid rgba(211,154,33,0.2)",
                    borderRadius: "var(--lwd-radius-input)", cursor: "pointer",
                    fontFamily: FB, fontSize: 10, fontWeight: 600,
                    color: "#d39a21", letterSpacing: "0.8px", textTransform: "uppercase",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.06)"; }}
                >
                  Share Reflection
                </button>
              )}
            </div>

            {/* Curated Reflections */}
            {allComments.length > 0 && (
              <div>
                <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
                  Curated Reflections
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 180, overflowY: "auto" }}>
                  {allComments.map((c, ci) => (
                    <div key={ci} style={{
                      padding: "10px 12px",
                      borderLeft: "2px solid rgba(211,154,33,0.25)",
                      background: "rgba(255,255,255,0.015)",
                    }}>
                      <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, fontStyle: "italic", fontWeight: 300 }}>
                        "{c.text}"
                      </div>
                      <div style={{ fontFamily: FB, fontSize: 10, color: "rgba(211,154,33,0.5)", marginTop: 6, fontWeight: 600 }}>
                       , {c.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image meta */}
          <div style={{
            marginTop: "auto", paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ fontFamily: FB, fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Image Info</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.7 }}>
              Photo {idx + 1} of {gallery.length}<br />
              {pg ? `© ${pg.name}` : "Villa Rosanova"}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div onClick={(e) => e.stopPropagation()} style={{
        flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.06)",
        padding: "8px 12px", overflow: "hidden",
      }}>
        <div ref={thumbRef} className="lwd-thumb-strip" style={{
          display: "flex", gap: 4, overflowX: "auto",
          scrollbarWidth: "none", msOverflowStyle: "none",
        }}>
          {gallery.map((img, i) => (
            <div
              key={img.id}
              onClick={() => setLightIdx?.(i)}
              style={{
                flexShrink: 0, width: 72, height: 48, cursor: "pointer",
                border: i === idx ? "2px solid #d39a21" : "2px solid transparent",
                opacity: i === idx ? 1 : 0.4,
                transition: "all 0.2s", overflow: "hidden",
              }}
              onMouseEnter={(e) => { if (i !== idx) e.currentTarget.style.opacity = "0.7"; }}
              onMouseLeave={(e) => { if (i !== idx) e.currentTarget.style.opacity = "0.4"; }}
            >
              <img src={vpImg(img.src)} alt={img.alt || img.title || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ABOUT SECTION ────────────────────────────────────────────────────────────
function AboutSection({ venue, isDbVenue = false }) {
  const C = useT();
  const [expanded, setExpanded] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const isMobile = useIsMobile();

  return (
    <section id="overview" style={{ marginBottom: 56 }}>
      <SectionHeading title={venue.name ? `About ${venue.name}` : 'About This Venue'} subtitle={venue?.sectionIntros?.overview || undefined} />

      {/* Single-column editorial layout */}
      <div>

        {/* Intro paragraph, real listing copy only, never a fabricated fallback */}
        {(venue.description || venue.shortDescription || venue.short_description || venue.summary || venue.tagline) && (
          <p style={{ fontFamily: FB, fontSize: isMobile ? 15 : 16, color: C.textMid, lineHeight: 1.9, marginBottom: 28, maxWidth: 780 }}>
            {venue.description || venue.shortDescription || venue.short_description || venue.summary || venue.tagline}
          </p>
        )}

        {/* Full description, DB venues */}
        {isDbVenue && venue.fullDescription && (() => {
          const descStyle = { fontFamily: FB, fontSize: isMobile ? 14 : 15, color: C.textLight, lineHeight: 1.9, maxWidth: 780 };
          const renderBlocks = (blocks, startIdx = 0) => blocks.map((block, i) =>
            block.html
              ? <p key={i} style={{ margin: (startIdx + i) > 0 ? '1.35rem 0 0' : '0' }} dangerouslySetInnerHTML={safeHtml(block.content)} />
              : <p key={i} style={{ margin: (startIdx + i) > 0 ? '1.35rem 0 0' : '0' }}>{block.content}</p>
          );
          const blocks = splitDescriptionBlocks(venue.fullDescription);

          // Most listing descriptions are authored as a single long <p>, not
          // several short paragraphs, so a paragraph-count gate alone misses
          // most long descriptions. Gate on total plain-text length instead;
          // ~600 chars is comfortably above the shortest real listings (~470)
          // and comfortably below the platform median (~980).
          const plainTextLength = blocks.reduce((sum, b) =>
            sum + (b.html ? b.content.replace(/<[^>]+>/g, '') : b.content).length, 0);
          const COLLAPSE_THRESHOLD = 600;

          // Read-more is OFF by default, real listing descriptions run
          // ~150-230 words (verified across all published listings), short
          // enough that collapsing adds friction rather than value. This is
          // an opt-in for the exceptional listing with a genuinely long
          // description; even then it only collapses past the length gate.
          if (!venue.readmoreEnabled || plainTextLength <= COLLAPSE_THRESHOLD) {
            return (
              <div style={{ ...descStyle, marginBottom: 28 }}>
                {renderBlocks(blocks)}
              </div>
            );
          }

          const COLLAPSED_HEIGHT = isMobile ? 260 : 230; // ~first couple of paragraphs
          return (
            <>
              <div style={{ position: 'relative' }}>
                <div style={{ overflow: 'hidden', maxHeight: descExpanded ? 5000 : COLLAPSED_HEIGHT, transition: 'max-height 0.35s ease' }}>
                  <div style={descStyle}>
                    {renderBlocks(blocks)}
                  </div>
                </div>
                {!descExpanded && (
                  <div aria-hidden="true" style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, height: 64,
                    background: `linear-gradient(180deg, transparent, ${C.bg})`,
                    pointerEvents: 'none',
                  }} />
                )}
              </div>
              <button
                onClick={() => setDescExpanded(e => !e)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginTop: 8, marginBottom: 28,
                  background: 'none', border: 'none',
                  fontFamily: FB, fontSize: 13, fontWeight: 700,
                  color: C.gold, cursor: 'pointer', letterSpacing: '0.3px',
                  padding: 0,
                }}
              >
                {descExpanded ? 'Show Less' : 'Continue Reading'}
              </button>
            </>
          );
        })()}

        {/* Second paragraph, static Villa Rosanova content, only on /venue */}
        {!isDbVenue && (
        <p style={{ fontFamily: FB, fontSize: isMobile ? 14 : 15, color: C.textLight, lineHeight: 1.9, marginBottom: 16, maxWidth: 780 }}>
          From the frescoed Grand Salon, with its original parquet floors and three Venetian chandeliers, to the centuries-old cypress garden, every space has been designed to create moments of extraordinary beauty. With accommodation for 58 guests across 24 rooms and 6 suites, Villa Rosanova is the perfect setting for multi-day wedding celebrations.
        </p>
        )}

        {/* Expandable paragraphs, static Villa Rosanova content, only on /venue */}
        {!isDbVenue && (
        <div style={{ overflow: "hidden", maxHeight: expanded ? 500 : 0, transition: "max-height 0.5s ease", maxWidth: 780 }}>
          <p style={{ fontFamily: FB, fontSize: isMobile ? 14 : 15, color: C.textLight, lineHeight: 1.9, marginBottom: 16 }}>
            The estate produces its own Chianti Classico wine, cold-pressed extra virgin olive oil, and seasonal truffles, all of which feature on our exclusively crafted wedding menus. Every detail of your celebration is managed by our dedicated events team, who have hosted over 300 weddings across four decades.
          </p>
          <p style={{ fontFamily: FB, fontSize: isMobile ? 14 : 15, color: C.textLight, lineHeight: 1.9 }}>
            Villa Rosanova has been featured in Vogue, Tatler and Harper's Bazaar, and has received the Luxury Wedding Directory's Best Villa award three years in succession. For couples seeking a truly once-in-a-lifetime setting, where privacy, beauty and impeccable service converge, there is simply nowhere quite like it.
          </p>
        </div>
        )}

        {!isDbVenue && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            marginTop: 4, marginBottom: 32,
            background: "none", border: "none",
            fontFamily: FB, fontSize: 13, fontWeight: 700,
            color: C.gold, cursor: "pointer", letterSpacing: "0.3px",
            padding: 0,
          }}
        >
          {expanded ? "Show less ↑" : "Read the full story →"}
        </button>
        )}

        {/* Awards, horizontal scroll on mobile */}
        {venue.awards?.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 16 }}>
          <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 12 }}>Awards & Recognition</div>
          <div className="vp-awards-scroll" style={{
            display: "flex", gap: 8, overflowX: isMobile ? "auto" : "visible",
            flexWrap: isMobile ? "nowrap" : "wrap",
            scrollbarWidth: "none", msOverflowStyle: "none",
            paddingBottom: isMobile ? 4 : 0,
          }}>
            {venue.awards.map(a => (
              <div key={a} style={{
                flex: "0 0 auto",
                padding: "8px 14px",
                border: `1px solid ${C.gold}30`,
                background: `${C.gold}08`,
                borderRadius: 3,
                fontFamily: FB, fontSize: 11, fontWeight: 600,
                color: C.gold, letterSpacing: "0.3px", whiteSpace: "nowrap",
              }}>
                ✦ {a}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Press */}
        {venue.press?.length > 0 && (
        <div>
          <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 12 }}>As Seen In</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            {venue.press.map(p => (
              <span key={p} style={{
                fontFamily: FD, fontSize: isMobile ? 15 : 17, fontWeight: 400,
                color: C.textLight, letterSpacing: "0.5px",
              }}>{p}</span>
            ))}
          </div>
        </div>
        )}

      </div>
    </section>
  );
}

// ─── CONTACT SECTION ─────────────────────────────────────────────────────────
function ContactSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [mapLoaded, setMapLoaded]         = useState(false);
  const [exitConfig, setExitConfig]       = useState(null);
  if (!venue.contact) return null;

  const handleWebsiteClick = () => {
    const url = toAbsoluteUrl(venue.contact.website);
    const trackData = { entityType: 'venue', entityId: venue.id, venueId: venue.id, linkType: 'website', url };
    if (!hasSeenModalThisSession(url)) {
      setExitConfig({ url, name: venue.name });
    } else {
      trackExternalClick(trackData);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  const addr = venue.contact.address || {};
  const rm   = venue.contact.responseMetrics || {};

  const contactRow = (iconName, label, content, props = {}) => {
    const Tag = props.href ? "a" : "div";
    return (
      <Tag {...(props.href ? { href: props.href, target: props.target, rel: props.rel } : {})}
        style={{
          padding: "18px 20px", display: "flex", gap: 14, alignItems: "center",
          textDecoration: "none", transition: "background 0.2s",
          borderBottom: props.last ? "none" : `1px solid ${C.border}`,
          ...(props.topAlign ? { alignItems: "flex-start" } : {}),
        }}
        onMouseEnter={props.href ? (e => e.currentTarget.style.background = C.bgAlt) : undefined}
        onMouseLeave={props.href ? (e => e.currentTarget.style.background = "transparent") : undefined}
      >
        <Icon name={iconName} size={18} color={C.textMuted} style={props.topAlign ? { marginTop: 2 } : {}} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
          {content}
        </div>
      </Tag>
    );
  };

  const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const addressFormatted = [addr.line1, addr.city, [addr.postcode, addr.region].filter(Boolean).join(' '), cap(addr.country)].filter(Boolean).join(', ');

  // Clean location label (deduplicate city/region if same)
  const locationParts = [addr.city, addr.region, addr.country]
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .filter((v, i, a) => i === 0 || v.toLowerCase() !== a[i - 1].toLowerCase());
  const locationLabel = locationParts.join(", ");

  // Travel context, nearest airport if available
  const nearestAirport = venue.access?.nearestAirport;

  // Google Maps links, embed + external
  const rawQuery  = venue.contact.mapQuery?.replace(/\+/g, " ") || locationLabel;
  const embedSrc  = `https://maps.google.com/maps?q=${encodeURIComponent(rawQuery)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
  const mapsLink  = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rawQuery)}`;

  return (
    <section style={{ marginBottom: 56 }}>
      <SectionHeading title="Contact & Location" subtitle="Find us and plan your journey" />
      <div className="vp-contact-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 32 }}>

        {/* ── Contact details ── */}
        <div style={{ display: "flex", flexDirection: "column", height: 400 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, border: `1px solid ${C.border}`, overflow: "hidden", flex: 1 }}>
            {addressFormatted && contactRow("pin", "Address",
              <div style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.6 }}>{addressFormatted}</div>,
              { topAlign: true }
            )}
            {venue.contact.phone && contactRow("phone", "Phone",
              <div style={{ fontFamily: FB, fontSize: 15, color: C.gold, fontWeight: 600 }}>{venue.contact.phone}</div>,
              { href: `tel:${venue.contact.phone}` }
            )}
            {venue.contact.email && contactRow("email", "Email",
              emailRevealed
                ? <a href={`mailto:${venue.contact.email}`} style={{ fontFamily: FB, fontSize: 14, color: C.gold, textDecoration: "none" }}>{venue.contact.email}</a>
                : <button onClick={() => setEmailRevealed(true)} style={{
                    background: "none", border: `1px solid ${C.border2}`, borderRadius: "var(--lwd-radius-input)",
                    color: C.textLight, fontFamily: FB, fontSize: 12, padding: "5px 12px",
                    cursor: "pointer", letterSpacing: "0.3px",
                  }}>Click to reveal email</button>
            )}
            {venue.contact.website && (
              <div
                onClick={handleWebsiteClick}
                style={{
                  padding: "18px 20px", display: "flex", gap: 14, alignItems: "center",
                  cursor: "pointer", transition: "background 0.2s",
                  borderBottom: `1px solid ${C.border}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.bgAlt}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <Icon name="globe" size={15} color={C.textMuted} />
                <div>
                  <div style={{ fontFamily: FB, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMuted, marginBottom: 3 }}>Website</div>
                  <div style={{ fontFamily: FB, fontSize: 14, color: C.gold, fontWeight: 600 }}>{venue.contact.website}</div>
                </div>
              </div>
            )}

            {/* ── Social media links ── */}
            {(() => {
              const s = venue.contact?.social || {};
              const links = [
                { key: 'instagram', label: 'Instagram', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
                { key: 'facebook', label: 'Facebook', icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                { key: 'linkedin', label: 'LinkedIn', icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
                { key: 'tiktok', label: 'TikTok', icon: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
                { key: 'twitter', label: 'X (Twitter)', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
                { key: 'pinterest', label: 'Pinterest', icon: 'M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z' },
                { key: 'youtube', label: 'YouTube', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
              ].filter(l => s[l.key]);
              if (!links.length) return null;
              return (
                <div style={{ padding: "16px 20px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, letterSpacing: "0.7px", textTransform: "uppercase", marginRight: 4 }}>Follow</span>
                  {links.map(l => (
                    <button key={l.key} onClick={() => {
                      trackExternalClick({ entityType: 'venue', entityId: venue.id, venueId: venue.id, linkType: l.key, url: s[l.key] });
                      window.open(s[l.key], '_blank', 'noopener,noreferrer');
                    }} title={l.label}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.textMuted, transition: "color 0.2s", display: "flex" }}
                      onMouseEnter={e => e.currentTarget.style.color = C.gold}
                      onMouseLeave={e => e.currentTarget.style.color = C.textMuted}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
                        <path d={l.icon} />
                      </svg>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {rm.averageResponseHours && (
            <div style={{ marginTop: 14, padding: "12px 16px", background: C.goldLight, border: `1px solid ${C.goldBorder}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Icon name="zap" size={14} color={C.gold} style={{ marginTop: 1 }} />
              <div>
                <div style={{ fontFamily: FB, fontSize: 12, color: C.gold, fontWeight: 600, marginBottom: 3 }}>Responds within {rm.averageResponseHours} hrs</div>
                <div style={{ fontFamily: FB, fontSize: 12, color: C.textLight }}>
                  {rm.responseRatePercent}% response rate{rm.sameDayTypical ? " · Typically replies same day" : ""}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Map column ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

          {/* Location descriptor */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="pin" size={13} color={C.gold} />
              <span style={{ fontFamily: FB, fontSize: 13, color: C.textMid, fontWeight: 500 }}>
                {locationLabel || venue.location}
              </span>
              {nearestAirport && (
                <span style={{ fontFamily: FB, fontSize: 12, color: C.textMuted }}>
                  · {nearestAirport} nearest airport{venue.access?.transferTime ? ` · ${venue.access.transferTime} transfer` : ''}
                </span>
              )}
            </div>
            <a
              href={mapsLink} target="_blank" rel="noopener noreferrer"
              style={{ fontFamily: FB, fontSize: 11, color: C.gold, textDecoration: "none", letterSpacing: "0.3px", display: "flex", alignItems: "center", gap: 4, transition: "opacity 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.72"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              View in Google Maps ↗
            </a>
          </div>

          {/* Map container, fixed height so iframe always renders */}
          <div style={{
            position: "relative", height: 400, overflow: "hidden",
            border: `1px solid ${C.border}`,
            borderRadius: "var(--lwd-radius-input)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            background: C.bgAlt,
          }}>
            {/* Loading shimmer, hidden once map fires onLoad */}
            {!mapLoaded && (
              <div style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 12,
                background: C.bgAlt,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={C.gold} opacity="0.4"/>
                </svg>
                <span style={{ fontFamily: FB, fontSize: 12, color: C.textMuted, letterSpacing: "0.3px" }}>Loading map…</span>
              </div>
            )}
            <iframe
              title="Venue location"
              width="100%"
              height="400"
              style={{ display: "block", border: "none", opacity: mapLoaded ? 1 : 0, transition: "opacity 0.4s ease" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={embedSrc}
              onLoad={() => setMapLoaded(true)}
            />
          </div>
        </div>

      </div>

      {exitConfig && (
        <ExternalLinkModal
          name={exitConfig.name}
          url={exitConfig.url}
          onClose={() => setExitConfig(null)}
          onContinue={() => {
            markModalSeen(exitConfig.url);
            trackExternalClick({ entityType: 'venue', entityId: venue.id, venueId: venue.id, linkType: 'website', url: exitConfig.url });
          }}
        />
      )}
    </section>
  );
}

// ─── VIDEO PLAY MODAL ─────────────────────────────────────────────────────────
// VideoPlayModal extracted to ./components/video/VideoPlayModal (shared by listing + showcase)

// ─── VIDEO GALLERY ────────────────────────────────────────────────────────────
function VideoGallery({ videos, venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(null);

  // Reset all state when VideoGallery unmounts (route change / navigation away)
  useEffect(() => {
    return () => {
      setPlaying(null);
      setActive(0);
    };
  }, []);
  if (!videos || videos.length === 0) return null;
  const vg = videos[active].videographer;
  const venueName = venue?.name || 'Wedding Venue';

  return (
    <section style={{ marginBottom: 56 }}>
      <SectionHeading title="Films" subtitle={`Real weddings, estate tours and highlights from ${venueName}`} />

      {/* Main featured video */}
      <div
        style={{ marginBottom: 0, position: "relative", background: "#000", cursor: "pointer", aspectRatio: "16/9", overflow: "hidden", borderRadius: isMobile ? 3 : 0 }}
        onClick={() => setPlaying(videos[active])}
        role="button"
        aria-label={`Play ${videos[active].title}`}
      >
        <img src={vpImg(videos[active].thumb, 1200)} alt={videos[active].title}
          onError={e => { if (videos[active].thumbFallback) e.currentTarget.src = videos[active].thumbFallback; }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.85 }} />
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            width: isMobile ? 52 : 64, height: isMobile ? 52 : 64, borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
          }}>
            <span style={{ fontSize: isMobile ? 16 : 20, color: "#fff", marginLeft: 3 }}>▶</span>
          </div>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMobile ? "32px 16px 14px" : "40px 20px 16px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
          <div style={{ fontFamily: FD, fontSize: isMobile ? 17 : 20, color: "#fff" }}>{videos[active].title}</div>
          <div style={{ fontFamily: FB, fontSize: isMobile ? 11 : 12, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{videos[active].duration} · {videos[active].type === "wedding" ? "Wedding Film" : "Estate Tour"}</div>
        </div>
      </div>

      {/* Video info bar, stacked on mobile */}
      <div className="vp-video-info" style={{
        padding: isMobile ? "14px 0" : "16px 20px", background: isMobile ? "transparent" : C.surface,
        border: isMobile ? "none" : `1px solid ${C.border}`, borderTop: "none",
        display: "flex", flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 12 : 24, alignItems: isMobile ? "stretch" : "flex-start",
        marginBottom: 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {videos[active].desc && (
            <p style={{ fontFamily: FB, fontSize: isMobile ? 13 : 13, color: C.textLight, lineHeight: 1.7, marginBottom: 10 }}>{videos[active].desc}</p>
          )}
          {videos[active].tags && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {videos[active].tags.map(t => (
                <span key={t} style={{
                  fontFamily: FB, fontSize: 9, color: C.textMuted,
                  background: C.bgAlt, border: `1px solid ${C.border}`,
                  padding: "2px 7px", borderRadius: "var(--lwd-radius-input)", letterSpacing: "0.3px",
                }}>{t}</span>
              ))}
            </div>
          )}
        </div>
        {vg && (
          <div style={{
            flexShrink: 0, width: isMobile ? "100%" : 200,
            padding: "10px 14px", background: C.bgAlt,
            border: `1px solid ${C.border}`, borderRadius: "var(--lwd-radius-card)",
            display: "flex", flexDirection: isMobile ? "row" : "column",
            alignItems: isMobile ? "center" : "stretch",
            gap: isMobile ? 12 : 0,
          }}>
            <div style={{ flex: isMobile ? 1 : undefined }}>
              <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 4 }}>Videographer</div>
              <div style={{ fontFamily: FD, fontSize: 15, color: C.text }}>{vg.name}</div>
              <div style={{ fontFamily: FB, fontSize: 11, color: C.textLight, display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}><Icon name="pin" size={11} color={C.textLight} /> {vg.area}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {vg.instagram && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 10, opacity: 0.5 }}>📹</span>
                  <span style={{ fontFamily: FB, fontSize: 10, color: C.gold }}>{vg.instagram}</span>
                </div>
              )}
              {vg.camera && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 10, opacity: 0.5 }}>🎥</span>
                  <span style={{ fontFamily: FB, fontSize: 10, color: C.textMuted }}>{vg.camera}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail strip, only shown when there are multiple videos */}
      {videos.length > 1 && (isMobile ? (
        <div className="vp-films-slider" style={{
          display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
          marginTop: 16, marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        }}>
          {videos.map((v, i) => (
            <div key={v.id} style={{ flex: "0 0 200px", scrollSnapAlign: "start", cursor: "pointer" }}
              onClick={() => setActive(i)}>
              <div style={{
                position: "relative", aspectRatio: "16/9", overflow: "hidden", borderRadius: 3,
                border: i === active ? `2px solid ${C.gold}` : "2px solid transparent",
              }}>
                <img src={vpImg(v.thumb, 600)} alt={v.title}
                  onError={e => { if (v.thumbFallback) e.currentTarget.src = v.thumbFallback; }}
                  style={{
                    width: "100%", height: "100%", objectFit: "cover", display: "block",
                    opacity: i === active ? 1 : 0.6,
                  }} />
                {i === active && (
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                    padding: "12px 8px 6px", textAlign: "center",
                    fontFamily: FB, fontSize: 8, fontWeight: 700, letterSpacing: "0.8px",
                    textTransform: "uppercase", color: C.gold,
                  }}>Now Playing</div>
                )}
                <div onClick={(e) => { e.stopPropagation(); setPlaying(v); }} style={{
                  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: 10, color: "#fff", marginLeft: 2 }}>▶</span>
                  </div>
                </div>
              </div>
              <div style={{ padding: "6px 0 0" }}>
                <div style={{ fontFamily: FB, fontSize: 11, fontWeight: 600, color: C.text, lineHeight: 1.3 }}>{v.title}</div>
                <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, marginTop: 2 }}>{v.duration}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="vp-films-slider" style={{
          display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
          marginTop: 20,
        }}>
          {videos.map((v, i) => (
            <div key={v.id} style={{ flex: "0 0 200px", scrollSnapAlign: "start", cursor: "pointer" }} onClick={() => setActive(i)}>
              <div style={{
                position: "relative", aspectRatio: "16/9", overflow: "hidden",
                border: i === active ? `2px solid ${C.gold}` : "2px solid transparent",
              }}>
                <img src={vpImg(v.thumb, 600)} alt={v.title}
                  onError={e => { if (v.thumbFallback) e.currentTarget.src = v.thumbFallback; }}
                  style={{
                    width: "100%", height: "100%", objectFit: "cover", display: "block",
                    opacity: i === active ? 1 : 0.5, transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => { if (i !== active) e.currentTarget.style.opacity = "0.75"; }}
                  onMouseLeave={(e) => { if (i !== active) e.currentTarget.style.opacity = "0.5"; }}
                />
                <div
                  onClick={(e) => { e.stopPropagation(); setPlaying(v); }}
                  style={{
                    position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: 0, transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: 11, color: "#fff", marginLeft: 2 }}>▶</span>
                  </div>
                </div>
                {i === active && (
                  <div style={{
                    position: "absolute", bottom: 4, left: 0, right: 0, textAlign: "center",
                    fontFamily: FB, fontSize: 8, fontWeight: 700, letterSpacing: "0.8px",
                    textTransform: "uppercase", color: C.gold, textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                  }}>Now Playing</div>
                )}
              </div>
              <div style={{ padding: "8px 0 0" }}>
                <div style={{ fontFamily: FB, fontSize: 11, fontWeight: 600, color: C.text, lineHeight: 1.3 }}>{v.title}</div>
                <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, marginTop: 2 }}>{v.duration} · {v.videographer?.name || ""}</div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Video play modal */}
      {playing && (
        <VideoPlayModal
          video={playing}
          videos={videos}
          onSelect={(v) => setPlaying(v)}
          onClose={() => setPlaying(null)}
          engagement={VENUE.engagement?.videos}
        />
      )}
    </section>
  );
}

// ─── EXCLUSIVE USE ────────────────────────────────────────────────────────────
function ExclusiveUse({ venue, onEnquire }) {
  const C = useT();
  const isMobile = useIsMobile();
  const eu = venue.exclusiveUse;

  // Section hidden if disabled or no data
  if (!eu || eu.enabled === false) return null;
  // Don't render empty shell
  if (!eu.from && !eu.description && !(eu.includes?.length)) return null;

  const title    = eu.title    || "Exclusive Use";
  const subtitle = eu.subtitle || "";
  const ctaText  = eu.ctaText  || "Enquire About Exclusive Use";
  const subline  = eu.subline  || (eu.minNights ? `Minimum ${eu.minNights} nights` : "");

  return (
    <section id="pricing" style={{ marginBottom: 56 }}>
      <SectionHeading title={title} subtitle={subtitle} />
      <div style={{ border: `1px solid ${C.goldBorder}`, background: C.goldLight, padding: isMobile ? 24 : 40 }}>
        <div className="vp-exclusive-grid" style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? 32 : 48,
        }}>
          {/* Left: price + description + CTA */}
          <div>
            {eu.from && (
              <div style={{ fontFamily: FD, fontSize: isMobile ? 32 : 40, color: C.gold, marginBottom: 6, lineHeight: 1 }}>
                From {String(eu.from).replace(/^\s*from\s+/i, '')}
              </div>
            )}
            {subline && (
              <div style={{ fontFamily: FB, fontSize: 13, color: C.textLight, marginBottom: 24 }}>
                {subline}
              </div>
            )}
            {eu.description && (
              <div style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.8, marginBottom: 28 }}>
                {splitDescriptionBlocks(eu.description).map((block, i) =>
                  block.html
                    ? <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }} dangerouslySetInnerHTML={safeHtml(block.content)} />
                    : <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }}>{block.content}</p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onEnquire}
              style={{
                padding: "13px 28px", background: C.gold, border: "none",
                borderRadius: "var(--lwd-radius-input)",
                color: "#fff", fontFamily: FB, fontSize: 12, fontWeight: 800,
                letterSpacing: "0.9px", textTransform: "uppercase", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 8,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              {ctaText} →
            </button>
          </div>

          {/* Right: includes list */}
          {eu.includes?.length > 0 && (
            <div>
              <div style={{
                fontFamily: FB, fontSize: 9, color: C.textMuted,
                letterSpacing: "1.4px", textTransform: "uppercase", marginBottom: 16,
              }}>
                Exclusive use includes
              </div>
              {eu.includes.slice(0, 12).map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <span style={{ color: C.gold, fontSize: 14, lineHeight: 1, flexShrink: 0 }}>✓</span>
                  <span style={{ fontFamily: FB, fontSize: 14, color: C.textMid }}>{typeof item === 'string' ? item : item?.text || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── CATERING ────────────────────────────────────────────────────────────────
// ── Wedding Packages ─────────────────────────────────────────────────────────
// Renders the structured wedding-package offerings saved by the Listing
// Studio's PackagesSection (e.g. Orchardleigh's "House Weddings" / "Estate
// Weddings"). Hidden when there are no packages.
function WeddingPackagesSection({ venue, onEnquire }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [hoverIdx, setHoverIdx] = useState(null);

  const packages = (venue?.weddingPackages || [])
    .filter(p => p && (p.name || p.description))
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    .slice(0, 5);

  if (packages.length === 0) return null;

  // Currency symbol, accept either a symbol or an ISO code.
  const currencySymbol = (ccy) => {
    if (!ccy) return '£';
    const map = { GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'C$' };
    if (map[ccy]) return map[ccy];
    return ccy.length <= 2 ? ccy : '£';
  };
  const formatPrice = (n, ccy) => {
    if (!n || n <= 0) return null;
    return `${currencySymbol(ccy)}${Number(n).toLocaleString()}`;
  };

  // Fluid columns: 1 on mobile, auto-fit on desktop so 3 cards never orphan.
  const gridTemplateColumns = isMobile
    ? '1fr'
    : packages.length === 1
      ? '1fr'
      : 'repeat(auto-fit, minmax(320px, 1fr))';

  // Soft palette tokens, fall back to safe luxury defaults if theme is sparse.
  const GOLD       = C.gold || '#d39a21';
  const GOLD_DEEP  = '#9a7416';
  const SURFACE    = C.surface || '#fffdf9';
  const BORDER     = C.border  || '#ece6d8';
  const TEXT       = C.text    || '#1a1510';
  const MUTED      = C.muted   || '#7a7264';

  return (
    <section style={{ marginBottom: 64 }} id="wedding-packages">
      {/* Editorial header, gold eyebrow + serif title + ornament */}
      <div style={{ marginBottom: isMobile ? 24 : 36, textAlign: 'center', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{
          fontFamily: FB,
          fontSize: 10,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          fontWeight: 600,
          marginBottom: 10,
        }}>
          ✦ Curated Offerings ✦
        </div>
        <h2 style={{
          fontFamily: FD,
          fontSize: isMobile ? 28 : 38,
          fontWeight: 400,
          color: TEXT,
          margin: '0 0 14px',
          lineHeight: 1.15,
          letterSpacing: '-0.01em',
        }}>
          Wedding Packages
        </h2>
        {/* Gold ornament */}
        <div aria-hidden style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          margin: '0 auto 14px',
        }}>
          <span style={{ width: 36, height: 1, background: `linear-gradient(90deg, transparent, ${GOLD})` }} />
          <span style={{ color: GOLD, fontSize: 9 }}>◆</span>
          <span style={{ width: 36, height: 1, background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
        </div>
        <p style={{
          fontFamily: FB,
          fontSize: isMobile ? 13 : 14,
          lineHeight: 1.7,
          color: MUTED,
          margin: 0,
          fontStyle: 'italic',
        }}>
          {venue?.sectionIntros?.packages || 'Choose the package that fits your celebration, from intimate house weddings to full-estate buyouts.'}
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns,
        gap: isMobile ? 18 : 24,
        alignItems: 'stretch',
      }}>
        {packages.map((p, idx) => {
          const priceLabel = formatPrice(p.price_from, p.price_currency);
          const isHover = hoverIdx === idx;
          const indexLabel = String(idx + 1).padStart(2, '0');

          return (
            <article
              key={p.id || p.name || idx}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{
                background: `linear-gradient(180deg, ${SURFACE} 0%, ${SURFACE} 70%, rgba(211,154,33,0.025) 100%)`,
                border: `1px solid ${isHover ? `${GOLD}80` : BORDER}`,
                borderRadius: 6,
                padding: isMobile ? '34px 22px 26px' : '42px 32px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: 22,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
                transform: isHover ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow: isHover
                  ? '0 24px 48px -20px rgba(154, 116, 22, 0.18), 0 8px 24px -12px rgba(0,0,0,0.06)'
                  : '0 2px 12px -4px rgba(0,0,0,0.04)',
              }}
            >
              {/* Top gold accent bar */}
              <div aria-hidden style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${GOLD_DEEP}, ${GOLD}, ${GOLD_DEEP})`,
                opacity: isHover ? 1 : 0.65,
                transition: 'opacity 0.3s',
              }} />

              {/* Ghost numeral, top right, editorial */}
              <div aria-hidden style={{
                position: 'absolute',
                top: isMobile ? 14 : 20,
                right: isMobile ? 18 : 26,
                fontFamily: FD,
                fontSize: isMobile ? 32 : 44,
                fontWeight: 300,
                color: GOLD,
                opacity: isHover ? 0.35 : 0.18,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                fontStyle: 'italic',
                transition: 'opacity 0.3s',
                pointerEvents: 'none',
              }}>
                {indexLabel}
              </div>

              {/* Header: name */}
              <header style={{ paddingRight: isMobile ? 50 : 64 }}>
                <h3 style={{
                  fontFamily: FD,
                  fontSize: isMobile ? 24 : 30,
                  fontWeight: 400,
                  color: TEXT,
                  lineHeight: 1.15,
                  margin: '0 0 12px',
                  letterSpacing: '-0.005em',
                }}>
                  {p.name || 'Wedding Package'}
                </h3>

                {/* Meta chips: duration · exclusive use · season */}
                {(p.duration_days > 0 || p.exclusive_use || (p.season && p.season !== 'year-round')) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {p.duration_days > 0 && (
                      <span style={{
                        fontFamily: FB,
                        fontSize: 9,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: GOLD_DEEP,
                        background: 'rgba(211,154,33,0.09)',
                        border: `1px solid ${GOLD}40`,
                        padding: '5px 11px',
                        borderRadius: 2,
                      }}>
                        {p.duration_days} {p.duration_days === 1 ? 'Day' : 'Days'}
                      </span>
                    )}
                    {p.exclusive_use && (
                      <span style={{
                        fontFamily: FB,
                        fontSize: 9,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: GOLD_DEEP,
                        background: 'rgba(211,154,33,0.09)',
                        border: `1px solid ${GOLD}40`,
                        padding: '5px 11px',
                        borderRadius: 2,
                      }}>
                        Exclusive Use
                      </span>
                    )}
                    {p.season && p.season !== 'year-round' && (
                      <span style={{
                        fontFamily: FB,
                        fontSize: 9,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: MUTED,
                        background: 'transparent',
                        border: `1px solid ${BORDER}`,
                        padding: '5px 11px',
                        borderRadius: 2,
                      }}>
                        {p.season === 'winter' ? 'Winter Package' : p.season === 'summer' ? 'Summer Package' : p.season}
                      </span>
                    )}
                  </div>
                )}
              </header>

              {/* Description */}
              {p.description && (
                <p style={{
                  fontFamily: FB,
                  fontSize: isMobile ? 13 : 14,
                  lineHeight: 1.7,
                  color: MUTED,
                  margin: 0,
                  fontStyle: 'italic',
                }}>
                  {p.description}
                </p>
              )}

              {/* Capacity grid, refined */}
              {(p.dining_capacity > 0 || p.accommodation_capacity > 0 || p.min_guests > 0 || p.max_guests > 0) && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                  gap: 16,
                  paddingTop: 18,
                  paddingBottom: 4,
                  borderTop: `1px solid ${BORDER}`,
                }}>
                  {p.dining_capacity > 0 && (
                    <div>
                      <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 4, fontWeight: 600 }}>Dining</div>
                      <div style={{ fontFamily: FD, fontSize: 19, fontWeight: 400, color: TEXT, lineHeight: 1.1 }}>
                        up to <span style={{ color: GOLD_DEEP }}>{p.dining_capacity}</span>
                      </div>
                    </div>
                  )}
                  {p.accommodation_capacity > 0 && (
                    <div>
                      <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 4, fontWeight: 600 }}>Sleeps</div>
                      <div style={{ fontFamily: FD, fontSize: 19, fontWeight: 400, color: TEXT, lineHeight: 1.1 }}>
                        up to <span style={{ color: GOLD_DEEP }}>{p.accommodation_capacity}</span>
                      </div>
                    </div>
                  )}
                  {(p.min_guests > 0 || p.max_guests > 0) && (
                    <div>
                      <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 4, fontWeight: 600 }}>Guests</div>
                      <div style={{ fontFamily: FD, fontSize: 19, fontWeight: 400, color: TEXT, lineHeight: 1.1 }}>
                        {p.min_guests > 0 && p.max_guests > 0 ? (
                          <span style={{ color: GOLD_DEEP }}>{p.min_guests}–{p.max_guests}</span>
                        ) : p.max_guests > 0 ? (
                          <>up to <span style={{ color: GOLD_DEEP }}>{p.max_guests}</span></>
                        ) : (
                          <>min <span style={{ color: GOLD_DEEP }}>{p.min_guests}</span></>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Inclusions, bulleted list with gold ornament marks (cleaner than chips) */}
              {Array.isArray(p.inclusions) && p.inclusions.length > 0 && (
                <div style={{ paddingTop: 4 }}>
                  <div style={{
                    fontFamily: FB,
                    fontSize: 9,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: MUTED,
                    fontWeight: 600,
                    marginBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <span>What's Included</span>
                    <span style={{ flex: 1, height: 1, background: BORDER }} />
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 7,
                  }}>
                    {p.inclusions.map((it, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        fontFamily: FB,
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: TEXT,
                      }}>
                        <span aria-hidden style={{
                          color: GOLD,
                          fontSize: 9,
                          marginTop: 5,
                          flexShrink: 0,
                        }}>
                          ✦
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Footer: price + CTA */}
              <div style={{
                marginTop: 'auto',
                paddingTop: 22,
                borderTop: `1px solid ${BORDER}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}>
                {priceLabel && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}>
                    <div>
                      <div style={{
                        fontFamily: FB,
                        fontSize: 9,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: MUTED,
                        fontWeight: 600,
                        marginBottom: 4,
                      }}>
                        Investment From
                      </div>
                      <div style={{
                        fontFamily: FD,
                        fontSize: isMobile ? 30 : 36,
                        fontWeight: 400,
                        color: TEXT,
                        lineHeight: 1,
                        letterSpacing: '-0.015em',
                      }}>
                        {priceLabel}
                      </div>
                    </div>
                    {p.min_guests > 0 && (
                      <div style={{
                        fontFamily: FB,
                        fontSize: 10,
                        color: MUTED,
                        textAlign: 'right',
                        lineHeight: 1.4,
                        fontStyle: 'italic',
                        paddingBottom: 4,
                      }}>
                        based on<br />{p.min_guests} guests
                      </div>
                    )}
                  </div>
                )}

                {/* CTA, only if onEnquire wired */}
                {typeof onEnquire === 'function' && (
                  <button
                    onClick={onEnquire}
                    style={{
                      width: '100%',
                      padding: '13px 16px',
                      background: isHover ? GOLD : 'transparent',
                      border: `1px solid ${GOLD}`,
                      borderRadius: 2,
                      color: isHover ? '#0f0d0a' : GOLD_DEEP,
                      fontFamily: FB,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    Enquire About This Package
                    <span aria-hidden style={{
                      transition: 'transform 0.25s ease',
                      transform: isHover ? 'translateX(3px)' : 'translateX(0)',
                    }}>→</span>
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function CateringSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const cat = venue.catering;

  // Hide if disabled or missing
  if (!cat || cat.enabled === false) return null;

  // Only show cards with content, sorted by sortOrder
  const cards = (cat.cards || [])
    .filter(c => c.title || c.description)
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    .slice(0, 3);

  if (cards.length === 0) return null;

  // Grid cols: 3-up on desktop, adapt to card count on mobile
  const colCount = isMobile ? 1 : Math.min(cards.length, 3);

  return (
    <section style={{ marginBottom: 56 }}>
      <SectionHeading title="Catering & Dining" subtitle={venue?.sectionIntros?.dining || "Professional catering services and dining options"} />
      <div
        className="vp-catering-grid"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${colCount}, 1fr)`,
          gap: 20,
          marginBottom: 28,
        }}
      >
        {cards.map(c => (
          <div
            key={c.id || c.title}
            style={{ padding: 24, border: `1px solid ${C.border}`, background: C.surface }}
          >
            <div style={{ marginBottom: 12 }}>
              <Icon name={c.icon || 'dining'} size={28} color={C.gold} />
            </div>
            <div style={{ fontFamily: FD, fontSize: 18, color: C.text, marginBottom: 8 }}>{c.title}</div>
            <div style={{ fontFamily: FB, fontSize: 13, color: C.textLight, lineHeight: 1.7 }}>{c.description}</div>
            {c.subtext && (
              <div style={{ fontFamily: FB, fontSize: 12, color: C.textMuted, marginTop: 8 }}>{c.subtext}</div>
            )}
          </div>
        ))}
      </div>

      {/* Dining styles + dietary pills */}
      <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
        {cat.styles?.length > 0 && (
          <div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 10 }}>
              Dining styles
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {cat.styles.map(s => <Pill key={s} color="gold">{s}</Pill>)}
            </div>
          </div>
        )}
        {cat.dietary?.length > 0 && (
          <div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 10 }}>
              Dietary options
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {cat.dietary.map(d => (
                <Pill key={d} color="green"><Icon name="check" size={10} color={C.green} /> {d}</Pill>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── SPACES ───────────────────────────────────────────────────────────────────
// ─── VENUE SPACES SECTION ─────────────────────────────────────────────────────
function SpaceCapacityRow({ space, C }) {
  const caps = [
    space.capacityCeremony != null && { label: 'Ceremony', value: space.capacityCeremony },
    space.capacityReception != null && { label: 'Reception', value: space.capacityReception },
    space.capacityDining != null && { label: 'Dining', value: space.capacityDining },
    space.capacityStanding != null && { label: 'Standing', value: space.capacityStanding },
  ].filter(Boolean);

  if (!caps.length) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
      {caps.map(({ label, value }) => (
        <div key={label} style={{ textAlign: 'center', minWidth: 56 }}>
          <div style={{ fontFamily: FD, fontSize: 22, fontWeight: 400, color: C.text, lineHeight: 1 }}>{value}</div>
          <div style={{ fontFamily: FB, fontSize: 10, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

const SPACE_ATTR_ICONS = {
  indoor:     "M3 10l9-7 9 7M5 9v11h14V9M9 20v-6h6v6",
  outdoor:    "M12 2C9 6 8 8 8 10a4 4 0 008 0c0-2-1-4-4-8zM12 14v8M8 22h8",
  covered:    "M12 3a9 9 0 019 9H3a9 9 0 019-9zM12 12v7a2 2 0 004 0",
  openair:    "M12 3v2M12 19v2M4 12H2M22 12h-2M5.6 5.6L7 7M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4M12 8a4 4 0 100 8 4 4 0 000-8z",
  accessible: "M12 5a2 2 0 100-4 2 2 0 000 4zM12 7v6M8 10h8M12 13l-2 8M12 13l2 8",
};
function SpaceAttributeBadges({ space, C }) {
  const attrs = [];
  if (space.indoor != null) attrs.push(space.indoor ? { icon: SPACE_ATTR_ICONS.indoor, label: 'Indoor' } : { icon: SPACE_ATTR_ICONS.outdoor, label: 'Outdoor' });
  if (space.covered != null && !space.indoor) attrs.push(space.covered ? { icon: SPACE_ATTR_ICONS.covered, label: 'Covered' } : { icon: SPACE_ATTR_ICONS.openair, label: 'Open Air' });
  if (space.accessible) attrs.push({ icon: SPACE_ATTR_ICONS.accessible, label: 'Accessible' });
  if (!attrs.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
      {attrs.map(a => (
        <span key={a.label} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 11px', fontSize: 11, fontFamily: FB,
          border: `1px solid ${C.border}`, borderRadius: 20,
          color: C.textMid, backgroundColor: C.bgAlt || C.surface,
        }}>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={a.icon} /></svg>
          {a.label}
        </span>
      ))}
    </div>
  );
}

function SpacesSection({ spaces, venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [floorPlanModal, setFloorPlanModal] = useState(null); // { url, name }
  // Gallery images used as fallback when a space has no dedicated image
  const galleryImgs = (venue?.imgs || []).map(i => (typeof i === 'string' ? i : (i.src || i.url || '')).trim()).filter(Boolean);

  return (
    <section id="capacity" style={{ marginBottom: 56 }}>
      <SectionHeading
        title="Venue Spaces"
        subtitle={venue?.sectionIntros?.spaces || `${spaces.length} distinct event space${spaces.length !== 1 ? 's' : ''}, each with unique character and atmosphere`}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 48 : 64 }}>
        {spaces.map((s, i) => {
          const isEven = i % 2 === 0;
          const spaceImg = s.img === null ? null : (s.img || galleryImgs[i % galleryImgs.length] || null);
          return (
            <div key={s.id || s.name} className="vp-space-card" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : (isEven ? '1fr 1fr' : '1fr 1fr'),
              gap: isMobile ? 40 : 48,
              animation: `fadeUp 0.5s ease ${i * 0.08}s both`,
              alignItems: 'center',
            }}>
              {/* Image column, type pill overlaid bottom-left */}
              {spaceImg && (
                <div style={{
                  order: isMobile ? 0 : (isEven ? 0 : 1),
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  aspectRatio: '16 / 10',
                }}>
                  <img
                    src={vpImg(spaceImg)} alt={s.name} loading="lazy" decoding="async"
                    className="lwd-img-zoom"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      maxHeight: isMobile ? '380px' : '500px',
                    }}
                  />
                  {/* Type pill, overlaid on image */}
                  {s.type && (
                    <div style={{ position: 'absolute', bottom: 14, left: 16 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '5px 14px',
                        fontSize: 10,
                        fontFamily: FB,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        border: '1px solid rgba(255,255,255,0.35)',
                        borderRadius: 20,
                        color: '#fff',
                        fontWeight: 700,
                        background: 'rgba(0,0,0,0.38)',
                        backdropFilter: 'blur(8px)',
                      }}>{s.type}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Content column, editorial layout, more open spacing */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                order: isMobile ? 1 : (isEven ? 1 : 0),
              }}>

                {/* Space name, serif headline */}
                <div style={{
                  fontFamily: FD,
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: 400,
                  color: C.text,
                  lineHeight: 1.2,
                }}>
                  {s.name}
                </div>

                {/* Capacity numbers, clean row */}
                <SpaceCapacityRow space={s} C={C} />

                {/* Description, readable paragraph width */}
                {s.description && (
                  <p style={{
                    fontFamily: FB,
                    fontSize: 13,
                    color: C.textLight,
                    lineHeight: 1.8,
                    margin: 0,
                    maxWidth: '600px',
                  }}>
                    {s.description}
                  </p>
                )}

                {/* Attribute badges */}
                <SpaceAttributeBadges space={s} C={C} />

                {/* Floor plan link */}
                {s.floorPlanUrl && (
                  <button
                    type="button"
                    onClick={() => setFloorPlanModal({ url: s.floorPlanUrl, name: s.name })}
                    style={{
                      alignSelf: 'flex-start',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '9px 18px',
                      fontSize: 11,
                      fontFamily: FB,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      border: `1px solid ${C.textMuted}`,
                      borderRadius: 2,
                      backgroundColor: 'transparent',
                      color: C.textMid,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = C.bgAlt;
                      e.currentTarget.style.borderColor = C.textMid;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = C.textMuted;
                    }}
                  >
                    📐 View Floor Plan
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floor Plan Modal */}
      {floorPlanModal && (
        <div
          onClick={() => setFloorPlanModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: 'relative', maxWidth: 900, width: '100%', backgroundColor: '#fff', borderRadius: 2 }}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5ddd0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: FB, fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Floor Plan, {floorPlanModal.name}</span>
              <button
                type="button"
                onClick={() => setFloorPlanModal(null)}
                style={{ border: 'none', background: 'none', fontSize: 22, color: '#888', cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>
            <img src={vpImg(floorPlanModal.url)} alt={`Floor plan, ${floorPlanModal.name}`} style={{ width: '100%', display: 'block', borderRadius: '0 0 2px 2px' }} />
          </div>
        </div>
      )}
    </section>
  );
}

// Per-room image slider, primary + gallery images, arrows + dots. Used inside
// each accommodation entity block.
function RoomImageSlider({ images, alt, isMobile }) {
  const C = useT();
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;
  const clamped = Math.min(idx, images.length - 1);
  const go = (d, e) => { e && e.stopPropagation(); setIdx((i) => (i + d + images.length) % images.length); };
  const arrow = (dir) => (
    <button onClick={(e) => go(dir === "prev" ? -1 : 1, e)} aria-label={dir === "prev" ? "Previous image" : "Next image"}
      style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", [dir === "prev" ? "left" : "right"]: 10,
        width: 34, height: 34, borderRadius: "50%", border: "none", cursor: "pointer",
        background: "rgba(0,0,0,0.42)", color: "#fff", fontSize: 18, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {dir === "prev" ? "‹" : "›"}
    </button>
  );
  return (
    <div style={{ position: "relative", width: isMobile ? "100%" : "42%", flexShrink: 0, aspectRatio: "4/3", borderRadius: 2, overflow: "hidden", background: C.bgAlt || C.surface }}>
      <img src={vpImg(images[clamped])} alt={alt || ""} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      {images.length > 1 && (
        <>
          {arrow("prev")}
          {arrow("next")}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 10, display: "flex", justifyContent: "center", gap: 6 }}>
            {images.map((_, i) => (
              <span key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                style={{ width: i === clamped ? 18 : 6, height: 6, borderRadius: 6, background: i === clamped ? "#fff" : "rgba(255,255,255,0.6)", cursor: "pointer", transition: "width .25s ease" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── ROOMS & ACCOMMODATION ────────────────────────────────────────────────────
function RoomsSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const acc = venue.accommodation;
  const entities = Array.isArray(acc?.entities) ? acc.entities.filter(e => e && (e.name || e.primaryImageUrl)) : [];
  if (!acc || (!acc.totalRooms && !acc.description && entities.length === 0)) return null;
  const [roomsLightboxIdx, setRoomsLightboxIdx] = useState(null);
  // Fall back to gallery images when no dedicated room images are assigned.
  // Normalise to { src, title } so room names can overlay the dedicated images.
  const rawRoomImgs = acc.images?.length > 0 ? acc.images : (venue.imgs || []).slice(0, 6);
  const roomImgs = rawRoomImgs
    .map(it => typeof it === 'string'
      ? { src: it.trim(), title: '' }
      : { src: (it.src || it.url || '').trim(), title: it.title || '' })
    .filter(it => it.src);

  return (
    <section id="rooms" style={{ marginBottom: 56 }}>
      <SectionHeading title="Rooms & Accommodation" subtitle={venue?.sectionIntros?.rooms || (acc.totalRooms ? `${acc.totalRooms} rooms${acc.totalSuites ? ` & ${acc.totalSuites} suites` : ''} for your guests` : "Accommodation for your guests")} />
      <>
        {/* Stats bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
          {acc.type && (
            <span style={{ padding: '5px 14px', border: `1px solid ${C.gold}`, borderRadius: 20, fontSize: 12, fontWeight: 600, color: C.gold, fontFamily: 'var(--font-body, inherit)' }}>
              {acc.type}
            </span>
          )}
          {acc.totalRooms > 0 && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid, fontFamily: 'var(--font-body, inherit)' }}>
              {acc.totalRooms} Rooms
            </span>
          )}
          {acc.totalSuites > 0 && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid, fontFamily: 'var(--font-body, inherit)' }}>
              {acc.totalSuites} Suites
            </span>
          )}
          {acc.maxOvernightGuests > 0 && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid, fontFamily: 'var(--font-body, inherit)' }}>
              Up to {acc.maxOvernightGuests} guests
            </span>
          )}
          {acc.minNightStay > 0 && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid, fontFamily: 'var(--font-body, inherit)' }}>
              Min {acc.minNightStay} nights
            </span>
          )}
          {acc.exclusiveUse && (
            <span style={{ padding: '5px 14px', backgroundColor: 'rgba(211,154,33,0.1)', border: `1px solid ${C.gold}`, borderRadius: 20, fontSize: 12, fontWeight: 700, color: C.gold, fontFamily: 'var(--font-body, inherit)' }}>
              ✦ Exclusive Use Available
            </span>
          )}
        </div>

        {/* Description */}
        {acc.description && (
          <div style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.8, marginBottom: 28 }}>
            {splitDescriptionBlocks(acc.description).map((block, i) =>
              block.html
                ? <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }} dangerouslySetInnerHTML={safeHtml(block.content)} />
                : <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }}>{block.content}</p>
            )}
          </div>
        )}

        {/* Entity blocks, each accommodation type as its own image-and-text block */}
        {entities.length > 0 && (
          <div style={{ display: 'grid', gap: isMobile ? 26 : 36 }}>
            {entities.map((ent, i) => {
              const chips = [
                ent.accommodationType || null,
                ent.roomCount ? `${ent.roomCount} ${Number(ent.roomCount) === 1 ? 'room' : 'rooms'}` : null,
                ent.sleepsMax ? `Sleeps ${ent.sleepsMax}` : null,
                ent.minStay ? `Min ${ent.minStay} night${Number(ent.minStay) === 1 ? '' : 's'}` : null,
                ent.exclusiveUse ? '✦ Exclusive use' : null,
              ].filter(Boolean);
              const gal = Array.isArray(ent.gallery) ? ent.gallery.filter(Boolean) : [];
              const slides = [ent.primaryImageUrl, ...gal].filter(Boolean).slice(0, 5);
              return (
                <div key={ent.id || i} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 14 : 30, alignItems: 'flex-start' }}>
                  <RoomImageSlider images={slides} alt={ent.name || `Accommodation ${i + 1}`} isMobile={isMobile} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {ent.name && <h3 style={{ fontFamily: 'var(--font-heading-primary, inherit)', fontSize: 21, fontWeight: 500, color: C.textMid, margin: '0 0 10px' }}>{ent.name}</h3>}
                    {chips.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {chips.map((c, ci) => (
                          <span key={ci} style={{ padding: '4px 12px', backgroundColor: c[0] === '✦' ? 'rgba(211,154,33,0.1)' : (C.bgAlt || C.surface), border: `1px solid ${c[0] === '✦' ? C.gold : C.border}`, borderRadius: 20, fontSize: 11.5, color: c[0] === '✦' ? C.gold : C.textMid, fontWeight: c[0] === '✦' ? 700 : 400, fontFamily: 'var(--font-body, inherit)' }}>{c}</span>
                        ))}
                      </div>
                    )}
                    {ent.description && <p style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>{ent.description}</p>}
                    {Array.isArray(ent.facilities) && ent.facilities.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginTop: 16 }}>
                        {ent.facilities.map((fk) => {
                          const f = ROOM_FACILITY_MAP[fk]; if (!f) return null;
                          return (
                            <span key={fk} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-body, inherit)', fontSize: 12.5, color: C.textMid }}>
                              <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={f.icon} /></svg>
                              {f.label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Room images grid (max 6), legacy fallback when there are no entities */}
        {entities.length === 0 && roomImgs.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 8,
          }}>
            {roomImgs.slice(0, 6).map((img, i) => (
              <div
                key={i}
                onClick={() => setRoomsLightboxIdx(i)}
                style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'pointer' }}
              >
                <img
                  src={vpImg(img.src)} alt={img.title || `Room ${i + 1}`}
                  loading="lazy"
                  style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                />
                {img.title && (
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    padding: '20px 12px 9px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.62), rgba(0,0,0,0))',
                    color: '#fff', fontSize: 12.5, fontWeight: 600,
                    letterSpacing: '0.02em', fontFamily: 'var(--font-body, inherit)',
                    pointerEvents: 'none',
                  }}>
                    {img.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {roomsLightboxIdx !== null && (() => {
          const lightboxImages = roomImgs.slice(0, 6).map(img => ({ src: img.src, title: img.title || '' }));
          return (
            <MenuImageModal
              images={lightboxImages}
              idx={roomsLightboxIdx}
              onClose={() => setRoomsLightboxIdx(null)}
              onPrev={() => setRoomsLightboxIdx(i => Math.max(0, i - 1))}
              onNext={() => setRoomsLightboxIdx(i => Math.min(lightboxImages.length - 1, i + 1))}
            />
          );
        })()}
      </>
    </section>
  );
}

// ─── MENU IMAGE MODAL ─────────────────────────────────────────────────────────
function MenuImageModal({ images, idx, onClose, onPrev, onNext }) {
  const C = useT();
  if (idx === null || idx === undefined || !images?.[idx]) return null;
  const img = images[idx];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: '100%', position: 'relative' }}>
        <img
          src={vpImg(img.src)} alt={img.title}
          style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 2 }}
        />
        {img.title && (
          <p style={{
            textAlign: 'center', marginTop: 14,
            fontFamily: 'var(--font-body, inherit)', fontSize: 14,
            color: 'rgba(255,255,255,0.85)', letterSpacing: '0.02em',
          }}>
            {img.title}
          </p>
        )}
        {/* Close */}
        <button onClick={onClose} style={{ position: 'absolute', top: -40, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        {/* Prev */}
        {idx > 0 && (
          <button onClick={onPrev} style={{ position: 'absolute', top: '50%', left: -52, transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
        )}
        {/* Next */}
        {idx < images.length - 1 && (
          <button onClick={onNext} style={{ position: 'absolute', top: '50%', right: -52, transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
        )}
        {/* Thumbnail strip (max 4) */}
        {images.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            {images.map((img, i) => (
              <img key={i} src={vpImg(img.src)} alt="" onClick={() => { /* handled via onPrev/onNext */ }}
                style={{
                  width: 52, height: 36, objectFit: 'cover', borderRadius: 2,
                  cursor: 'pointer', opacity: i === idx ? 1 : 0.45,
                  border: i === idx ? '1px solid #d39a21' : '1px solid transparent',
                  transition: 'opacity 0.15s',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DINING SECTION ───────────────────────────────────────────────────────────
function DiningSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const dining = venue.dining;
  const [menuImgIdx, setMenuImgIdx] = useState(null);

  if (!dining || (!dining.description && !dining.style)) return null;

  // Gallery fallback: normalize venue.imgs to plain URL strings
  const galleryImgs = (venue.imgs || [])
    .map(i => (typeof i === 'string' ? i : (i.src || i.url || '')).trim())
    .filter(Boolean);

  const sideImg = dining.menuImages?.[0]?.src || null;

  // Check if dining section has dedicated images
  const hasDiningImages = dining.menuImages?.length > 0;

  const PillGroup = ({ items, color }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
      {items.map(item => (
        <span key={item} style={{
          padding: '4px 12px', borderRadius: 20, fontSize: 12,
          border: color === 'gold' ? `1px solid ${C.gold}` : `1px solid ${C.border}`,
          color: color === 'gold' ? C.gold : C.textMid,
          backgroundColor: color === 'gold' ? 'rgba(211,154,33,0.08)' : (C.bgAlt || C.surface),
          fontFamily: 'var(--font-body, inherit)',
          fontWeight: color === 'gold' ? 600 : 400,
        }}>
          {item}
        </span>
      ))}
    </div>
  );

  return (
    <section id="dining" style={{ marginBottom: 56 }}>
      <SectionHeading title="Dining" subtitle={venue?.sectionIntros?.dining || "World-class culinary experiences and menu options"} />
      <SectionLayout sideImg={sideImg} isMobile={isMobile}>

        {/* Style + chef */}
        {dining.style && (
          <p style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: isMobile ? 17 : 20, fontWeight: 400, color: C.text, lineHeight: 1.35, marginBottom: 20, letterSpacing: '-0.01em' }}>
            {dining.style}
            {dining.chefName && <span style={{ display: 'block', fontFamily: 'var(--font-body, inherit)', fontSize: 13, color: C.textLight, marginTop: 6, fontStyle: 'italic' }}>Chef {dining.chefName}</span>}
          </p>
        )}

        {/* Catering badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {dining.inHouseCatering && (
            <span style={{ padding: '5px 14px', backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.4)', borderRadius: 20, fontSize: 12, fontWeight: 600, color: C.green }}>✓ In-house Catering</span>
          )}
          {dining.externalCateringAllowed && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid }}>External Caterers Welcome</span>
          )}
        </div>

        {/* Menu styles */}
        {dining.menuStyles?.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 8 }}>Menu Style</p>
            <PillGroup items={dining.menuStyles} color="gold" />
          </div>
        )}

        {/* Dietary */}
        {dining.dietaryOptions?.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 8 }}>Dietary</p>
            <PillGroup items={dining.dietaryOptions} color="neutral" />
          </div>
        )}

        {/* Drinks */}
        {dining.drinksOptions?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 8 }}>Drinks</p>
            <PillGroup items={dining.drinksOptions} color="neutral" />
          </div>
        )}

        {/* Description */}
        {dining.description && (
          <div style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.8, marginBottom: 32 }}>
            {splitDescriptionBlocks(dining.description).map((block, i) =>
              block.html
                ? <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }} dangerouslySetInnerHTML={safeHtml(block.content)} />
                : <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }}>{block.content}</p>
            )}
          </div>
        )}

        {/* Menu Highlights, dedicated dining images */}
        {hasDiningImages && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 12 }}>Menu Highlights</p>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(dining.menuImages.length, 5)}, 1fr)`, gap: 8 }}>
              {dining.menuImages.slice(0, 5).map((img, i) => (
                <div key={i} style={{ cursor: 'pointer' }} onClick={() => setMenuImgIdx(i)}>
                  <img
                    src={vpImg(img.src)} alt={img.title}
                    loading="lazy"
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 2, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  />
                  {img.title && (
                    <p style={{ fontSize: 11, color: C.textLight, margin: '5px 0 0', lineHeight: 1.3, fontFamily: 'var(--font-body, inherit)' }}>{img.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </SectionLayout>

      {/* Menu image lightbox */}
      <MenuImageModal
        images={dining.menuImages || []}
        idx={menuImgIdx}
        onClose={() => setMenuImgIdx(null)}
        onPrev={() => setMenuImgIdx(i => Math.max(0, i - 1))}
        onNext={() => setMenuImgIdx(i => Math.min((dining.menuImages?.length || 1) - 1, i + 1))}
      />
    </section>
  );
}

// ─── WELLNESS & POOL SECTION ──────────────────────────────────────────────────
// Renders when the listing has wellness-tagged media_items and/or a wellness
// description (section_intros.wellnessBody). Mirrors the Dining section layout.
function WellnessSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [imgIdx, setImgIdx] = useState(null);
  const w = venue.wellness;
  if (!w || (!w.description && !(w.images?.length))) return null;

  const sideImg = w.images?.[0]?.src || null;
  const grid = (w.images || []).slice(0, 6);

  return (
    <section id="wellness" style={{ marginBottom: 56 }}>
      <SectionHeading title="Wellness & Pool" subtitle={w.subtitle || "Spa, pools and pampering for the couple"} />
      <SectionLayout sideImg={sideImg} isMobile={isMobile}>

        {w.description && (
          <div style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.8, marginBottom: 32 }}>
            {splitDescriptionBlocks(w.description).map((block, i) =>
              block.html
                ? <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }} dangerouslySetInnerHTML={safeHtml(block.content)} />
                : <p key={i} style={{ margin: i > 0 ? '1.35rem 0 0' : '0' }}>{block.content}</p>
            )}
          </div>
        )}

        {grid.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 12 }}>Wellness &amp; Pool</p>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(grid.length, 3)}, 1fr)`, gap: 8 }}>
              {grid.map((img, i) => (
                <div key={i} style={{ cursor: 'pointer' }} onClick={() => setImgIdx(i)}>
                  <img
                    src={vpImg(img.src)} alt={img.title || 'Wellness at ' + (venue.name || 'the venue')}
                    loading="lazy"
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 2, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  />
                  {img.title && (
                    <p style={{ fontSize: 11, color: C.textLight, margin: '5px 0 0', lineHeight: 1.3, fontFamily: 'var(--font-body, inherit)' }}>{img.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </SectionLayout>

      <MenuImageModal
        images={grid}
        idx={imgIdx}
        onClose={() => setImgIdx(null)}
        onPrev={() => setImgIdx(i => Math.max(0, i - 1))}
        onNext={() => setImgIdx(i => Math.min(grid.length - 1, i + 1))}
      />
    </section>
  );
}

// ─── VENUE TYPE SECTION ───────────────────────────────────────────────────────
function VenueTypeSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const vt = venue.venueType;
  if (!vt?.primaryType && !(venue.categories?.length > 0)) return null;

  const sideImg = venue.imgs?.[3] || venue.imgs?.[0];

  return (
    <section id="venue-type" style={{ marginBottom: 56 }}>
      <SectionHeading title="Venue Type" subtitle={vt?.description || "Unique venue with distinctive character and style"} />
      <SectionLayout sideImg={sideImg} isMobile={isMobile}>
        {/* Primary type + architecture */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
          {vt?.primaryType && (
            <span style={{ padding: '5px 16px', border: `1px solid ${C.gold}`, borderRadius: 20, fontSize: 13, fontWeight: 700, color: C.gold }}>
              {vt.primaryType}
            </span>
          )}
          {vt?.architecture && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid }}>
              {vt.architecture} Architecture
            </span>
          )}
          {vt?.built && (
            <span style={{ padding: '5px 14px', backgroundColor: C.bgAlt || C.surface, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid }}>
              Built {vt.built}
            </span>
          )}
        </div>

        {vt?.description && (
          <p style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 14, color: C.textMid, lineHeight: 1.75, marginBottom: 24 }}>
            {vt.description}
          </p>
        )}

        {/* Style tags */}
        {vt?.styles?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 10 }}>Style</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {vt.styles.map(s => (
                <span key={s} style={{ padding: '4px 12px', border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.textMid, backgroundColor: C.bgAlt || C.surface }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Features checklist */}
        {Array.isArray(vt?.features) && vt.features.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textLight, marginBottom: 10 }}>Features</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 8 }}>
              {vt.features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMid }}>
                  <span style={{ color: C.gold, fontSize: 11 }}>✦</span>
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionLayout>
    </section>
  );
}

// ─── BRIDAL BOUTIQUE: VIRTUAL TRY-ON ─────────────────────────────────────────
function BridalTryOnSection({ venue }) {
  const C       = useT();
  const isMobile = useIsMobile();

  const navigate = (path) => {
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const url = `/fashion-studio/${venue.slug || venue.id}`;

  return (
    <section id="virtual-tryon" style={{ marginBottom: 64 }}>
      <SectionHeading
        title="Virtual Try-On"
        subtitle="Discover how your dream gown looks, before you ever step inside."
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 32,
        alignItems: 'center',
      }}>
        {/* Left, description */}
        <div>
          <p style={{
            fontFamily: FB, fontSize: 15, color: C.textMid,
            lineHeight: 1.85, margin: '0 0 24px',
          }}>
            Our immersive virtual fitting room lets you browse gowns from{' '}
            <strong style={{ color: C.text, fontWeight: 600 }}>{venue.name}</strong>{' '}
            and see how each silhouette, fabric, and detail looks on your figure. All from home,
            at your own pace.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
            {[
              'Browse the full gown collection',
              'Visualise styles on different body types',
              'Save favourites to bring to your appointment',
            ].map((item) => (
              <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ color: C.gold, fontSize: 13, marginTop: 1, flexShrink: 0 }}>✦</span>
                <span style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.6 }}>
                  {item}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate(url)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '13px 28px',
              background: C.gold,
              color: '#ffffff',
              border: 'none',
              borderRadius: 3,
              fontFamily: FB, fontSize: 12, fontWeight: 700,
              letterSpacing: '1.4px', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            ✦ Try On Gowns
          </button>
        </div>

        {/* Right, cinematic dark card */}
        <div
          onClick={() => navigate(url)}
          style={{
            position: 'relative',
            // Warm near-black with very subtle top-centre warm gradient for materiality
            background: 'radial-gradient(ellipse 80% 50% at 50% 0%, #1a1208 0%, #0a0806 60%)',
            border: '1px solid rgba(211,154,33,0.3)',
            borderRadius: 8,
            padding: '48px 40px 40px',
            textAlign: 'center',
            overflow: 'hidden',
            cursor: 'pointer',
            // Soft vignette via box-shadow inset
            boxShadow: 'inset 0 0 80px rgba(0,0,0,0.55), 0 2px 24px rgba(0,0,0,0.35)',
          }}
        >
          {/* Top-centre warm gold halo, adds depth, implies candlelight */}
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: 320, height: 200,
            background: 'radial-gradient(ellipse at 50% 0%, rgba(211,154,33,0.14) 0%, transparent 68%)',
            pointerEvents: 'none',
          }} />
          {/* Bottom soft vignette */}
          <div aria-hidden="true" style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 120,
            background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
            pointerEvents: 'none',
          }} />

          {/* Gown silhouette SVG */}
          <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
            <svg width="52" height="72" viewBox="0 0 52 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M20 4 C20 4 17 8 16 14 L13 22 L39 22 L36 14 C35 8 32 4 32 4 Z" stroke="#d39a21" strokeWidth="1.2" fill="rgba(211,154,33,0.08)" strokeLinejoin="round"/>
              <path d="M22 4 Q26 9 30 4" stroke="#d39a21" strokeWidth="1" fill="none" strokeLinecap="round"/>
              <path d="M13 22 L4 68 L48 68 L39 22 Z" stroke="#d39a21" strokeWidth="1.2" fill="rgba(211,154,33,0.06)" strokeLinejoin="round"/>
              <line x1="13" y1="22" x2="39" y2="22" stroke="rgba(211,154,33,0.5)" strokeWidth="0.8"/>
              <path d="M4 68 Q26 63 48 68" stroke="#d39a21" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Eyebrow */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12,
            position: 'relative', zIndex: 1,
          }}>
            <div style={{ width: 20, height: 1, background: 'rgba(211,154,33,0.45)' }} />
            <span style={{
              fontFamily: FB, fontSize: 9, fontWeight: 700, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: 'rgba(211,154,33,0.75)',
            }}>Exclusive</span>
            <div style={{ width: 20, height: 1, background: 'rgba(211,154,33,0.45)' }} />
          </div>

          <p style={{
            fontFamily: FD, fontSize: 22, fontWeight: 400,
            color: '#f5f0e8', letterSpacing: '-0.3px', margin: '0 0 12px',
            position: 'relative', zIndex: 1,
          }}>
            Fashion Studio
          </p>
          {/* Body text: lifted from 0.5 → 0.68 opacity for readability without losing luxury feel */}
          <p style={{
            fontFamily: FB, fontSize: 12, color: 'rgba(245,240,232,0.68)',
            margin: '0 0 32px', lineHeight: 1.75,
            position: 'relative', zIndex: 1,
          }}>
            Browse every gown. See it on your figure.<br />Save favourites before your appointment.
          </p>

          {/* CTA, heavier vertical padding, slightly tighter tracking, candlelight hover glow */}
          <div style={{ position: 'relative', zIndex: 1, display: 'inline-block' }}>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(url); }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 28px rgba(211,154,33,0.45), 0 4px 16px rgba(211,154,33,0.2)';
                e.currentTarget.style.opacity = '0.92';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.opacity = '1';
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #d39a21, #e8c97a)',
                color: '#0a0806',
                border: 'none',
                borderRadius: 3,
                fontFamily: FB, fontSize: 11, fontWeight: 700,
                letterSpacing: '1.15px', textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'box-shadow 0.3s ease, opacity 0.2s ease',
                boxShadow: 'none',
              }}
            >
              ✦ Open Fitting Room
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── VENDOR SPECIALTIES & IDENTITY ───────────────────────────────────────────
const PLANNER_PRICING_LABELS = {
  flat_fee:   'Flat Fee',
  percentage: '% of Budget',
  tiered:     'Tiered Packages',
  hybrid:     'Flat Fee + %',
  day_rate:   'Day Rate',
};

function EntertainmentIntelligence({ venue }) {
  const subTypes           = Array.isArray(venue.entSubTypes)           ? venue.entSubTypes           : [];
  const genres             = Array.isArray(venue.entGenres)             ? venue.entGenres             : [];
  const coverage           = Array.isArray(venue.entCoverage)           ? venue.entCoverage           : [];
  const productionProvided = Array.isArray(venue.entProductionProvided) ? venue.entProductionProvided : [];
  const weddingTypes       = Array.isArray(venue.entWeddingTypes)       ? venue.entWeddingTypes       : [];
  const format             = venue.entFormat            || '';
  const performanceLength  = venue.entPerformanceLength || '';
  const startingFrom       = venue.entStartingFrom      || '';
  const learnFirstDance    = venue.entLearnFirstDance;
  const city               = venue.contact?.address?.city    || '';
  const country            = venue.contact?.address?.country || '';
  const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  const hasAny = subTypes.length || genres.length || coverage.length || format ||
    performanceLength || productionProvided.length || weddingTypes.length;
  if (!hasAny) return null;

  // Smart card headline derived from sub-types
  const hasMusic       = subTypes.some(t => /band|dj|string|quartet|trio|ensemble|musician|flute|piano/i.test(t));
  const hasPerformance = subTypes.some(t => /theatrical|performer|artist|dancer|acrobat|singer|vocalist/i.test(t));
  const cardHeadline = hasMusic && hasPerformance
    ? 'Live Music, Performance & Atmosphere'
    : hasMusic
      ? 'Live Music & Atmosphere'
      : hasPerformance
        ? 'Performance & Atmosphere'
        : 'Entertainment';

  // Experiential coverage line
  const coverageSentence = coverage.length
    ? coverage.slice(0, -1).join(', ') + (coverage.length > 1 ? ' and ' : '') + coverage[coverage.length - 1]
    : '';
  const experientialLine = coverageSentence
    ? `Ceremony music, aperitivo performances, dinner entertainment and late-night celebrations across ${coverageSentence}.`
    : '';

  // Hero stat row: format · coverage summary · first dance · tier
  const heroStats = [
    format,
    coverage.length > 0 ? (coverage.includes('Worldwide') ? 'Worldwide' : coverage[coverage.length - 1]) : null,
    learnFirstDance ? 'First Dance Included' : null,
    weddingTypes.length > 0 ? weddingTypes[0] : null,
  ].filter(Boolean);

  // Practical meta
  const metaParts = [
    format,
    performanceLength,
    learnFirstDance ? 'First Dance Included' : null,
    startingFrom ? `From ${startingFrom}` : null,
  ].filter(Boolean);

  // Dark card design tokens
  const BG        = '#1c1a17';
  const GOLD_D    = '#c9a84c';
  const WHITE_1   = 'rgba(255,255,255,0.95)';
  const WHITE_2   = 'rgba(255,255,255,0.55)';
  const WHITE_3   = 'rgba(255,255,255,0.35)';
  const DIV_COL   = 'rgba(255,255,255,0.10)';

  const Chip = ({ label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '6px 17px',
      border: `1px solid ${WHITE_3}`,
      borderRadius: 3,
      fontFamily: FB, fontSize: 12, fontWeight: 400,
      letterSpacing: '0.04em', color: WHITE_2,
      background: 'transparent', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );

  const ChipRow = ({ items }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
      {items.map(c => <Chip key={c} label={c} />)}
    </div>
  );

  const GroupLabel = ({ children }) => (
    <p style={{
      fontFamily: FB, fontSize: 9, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.18em',
      color: GOLD_D, margin: 0,
    }}>
      {children}
    </p>
  );

  const Divider = () => (
    <div style={{ borderTop: `1px solid ${DIV_COL}`, margin: '28px 0' }} />
  );

  return (
    <section id="ent-intelligence" style={{ marginBottom: 64 }}>
      <SectionHeading title="The Performance" subtitle="The act format, musical range and what the performance covers." mb={16} />

      <div style={{
        background: BG,
        borderRadius: 4,
        padding: '40px 36px 36px',
      }}>

        {/* Editorial headline */}
        <p style={{
          fontFamily: FD, fontSize: 26, fontWeight: 400,
          color: WHITE_1, letterSpacing: '-0.3px', lineHeight: 1.2,
          margin: '0 0 16px',
        }}>
          {cardHeadline}
        </p>

        {/* Sub-types line */}
        {subTypes.length > 0 && (
          <p style={{
            fontFamily: FB, fontSize: 12, color: WHITE_2,
            margin: 0, letterSpacing: '0.04em', lineHeight: 1.7,
          }}>
            {subTypes.join('  ·  ')}
          </p>
        )}

        {/* Hero stat row */}
        {heroStats.length > 0 && (
          <>
            <Divider />
            <p style={{
              fontFamily: FB, fontSize: 13, color: WHITE_1,
              margin: 0, letterSpacing: '0.03em', lineHeight: 1.6,
              fontWeight: 500,
            }}>
              {heroStats.join('  ·  ')}
            </p>
          </>
        )}

        {/* Experiential coverage line */}
        {experientialLine && (
          <p style={{
            fontFamily: FB, fontSize: 13, color: WHITE_2,
            margin: '10px 0 0', letterSpacing: '0.02em', lineHeight: 1.7,
          }}>
            {experientialLine}
          </p>
        )}

        {/* Music + Best For */}
        {(genres.length > 0 || weddingTypes.length > 0) && (
          <>
            <Divider />
            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
              {genres.length > 0 && (
                <div style={{ flex: '1 1 180px' }}>
                  <GroupLabel>Music &amp; Atmosphere</GroupLabel>
                  <ChipRow items={genres} />
                </div>
              )}
              {weddingTypes.length > 0 && (
                <div style={{ flex: '1 1 180px' }}>
                  <GroupLabel>Best For</GroupLabel>
                  <ChipRow items={weddingTypes} />
                </div>
              )}
            </div>
          </>
        )}

        {/* Production */}
        {productionProvided.length > 0 && (
          <>
            <Divider />
            <GroupLabel>Production Provided</GroupLabel>
            <ChipRow items={productionProvided} />
          </>
        )}

      </div>
    </section>
  );
}

// ── Photography / Film intelligence ──────────────────────────────────────────
// The photographer & videographer equivalent of "The Performance": a dark card
// surfacing style, the essentials, who they're perfect for, and the relationship
// graph (Featured At / Worked With). Reads the camelised media_/photo_/video_/rel_
// fields. Evidence-gated: hides anything with no data.
function PhotographyIntelligence({ venue }) {
  const isVideo = venue.categorySlug === 'videographers';
  const arr  = (x) => Array.isArray(x) ? x : [];
  const nm   = (v) => typeof v === 'string' ? v : (v?.name || v?.label || '');
  const styles       = arr(isVideo ? venue.videoFilmStyles : venue.photoStyles).map(nm).filter(Boolean);
  const personality  = arr(venue.videoFilmPersonality).map(nm).filter(Boolean);
  const perfectFor   = arr(venue.mediaPerfectFor).map(nm).filter(Boolean);
  const idealWeds    = arr(venue.mediaIdealWeddings).map(nm).filter(Boolean);
  const destinations = arr(venue.mediaDestinationsCovered).map(nm).filter(Boolean);
  const portfolio    = arr(venue.photoPortfolioCategories).map(nm).filter(Boolean);
  const deliverables = arr(venue.videoDeliverables).map(nm).filter(Boolean);
  const venuesWorked = arr(venue.relVenuesWorked).map(nm).filter(Boolean);
  const planners     = arr(venue.relPlannersWorkedWith).map(nm).filter(Boolean);
  const startingFrom = venue.mediaStartingFrom || '';
  const coverage     = venue.mediaCoverageHours || '';
  const team         = venue.mediaTeamSize || '';
  const turnaround   = venue.mediaTurnaround || '';
  const basedIn      = venue.mediaBasedIn || '';
  const travelsTo    = venue.mediaTravelsTo || '';
  const howIWork     = venue.mediaHowIWork || '';

  const hasAny = styles.length || perfectFor.length || startingFrom || venuesWorked.length ||
    planners.length || destinations.length || idealWeds.length || howIWork;
  if (!hasAny) return null;

  const BG = '#1c1a17', GOLD_D = '#c9a84c';
  const WHITE_1 = 'rgba(255,255,255,0.95)', WHITE_2 = 'rgba(255,255,255,0.55)';
  const DIV_COL = 'rgba(255,255,255,0.10)';

  const heroStats = [
    startingFrom && (/^from/i.test(startingFrom) ? startingFrom : `From ${startingFrom}`),
    coverage, team, turnaround,
  ].filter(Boolean);

  const Divider = () => <div style={{ borderTop: `1px solid ${DIV_COL}`, margin: '28px 0' }} />;
  const GroupLabel = ({ children }) => (
    <p style={{ fontFamily: FB, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', color: GOLD_D, margin: '0 0 12px' }}>{children}</p>
  );
  const ChipRow = ({ items }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', padding: '7px 14px', border: `1px solid ${DIV_COL}`, borderRadius: 3, fontFamily: FB, fontSize: 11, letterSpacing: '0.03em', color: WHITE_2, whiteSpace: 'nowrap' }}>{it}</span>
      ))}
    </div>
  );

  return (
    <section id="craft-intelligence" style={{ marginBottom: 64 }}>
      <SectionHeading
        title={isVideo ? 'The Films' : 'The Craft'}
        subtitle={isVideo ? 'The film style, what you receive, and who it is for.' : 'The signature style, the essentials, and who it is for.'}
        mb={16}
      />
      <div style={{ background: BG, borderRadius: 4, padding: '40px 36px 36px' }}>
        <p style={{ fontFamily: FD, fontSize: 26, fontWeight: 400, color: WHITE_1, letterSpacing: '-0.3px', lineHeight: 1.2, margin: '0 0 16px' }}>
          {styles.length ? styles.join('  ·  ') : (isVideo ? 'Wedding Films' : 'Wedding Photography')}
        </p>
        {(isVideo ? personality : portfolio).length > 0 && (
          <p style={{ fontFamily: FB, fontSize: 12, color: WHITE_2, margin: 0, letterSpacing: '0.04em', lineHeight: 1.7 }}>
            {(isVideo ? personality : portfolio).join('  ·  ')}
          </p>
        )}

        {heroStats.length > 0 && (<>
          <Divider />
          <p style={{ fontFamily: FB, fontSize: 13, color: WHITE_1, margin: 0, letterSpacing: '0.03em', lineHeight: 1.6, fontWeight: 500 }}>
            {heroStats.join('  ·  ')}
          </p>
        </>)}

        {(basedIn || travelsTo) && (
          <p style={{ fontFamily: FB, fontSize: 13, color: WHITE_2, margin: '10px 0 0', letterSpacing: '0.02em', lineHeight: 1.7 }}>
            {[basedIn && `Based in ${basedIn}`, travelsTo].filter(Boolean).join('. ')}.
          </p>
        )}

        {(perfectFor.length > 0 || idealWeds.length > 0) && (<>
          <Divider />
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {perfectFor.length > 0 && (
              <div style={{ flex: '1 1 220px' }}><GroupLabel>Perfect For</GroupLabel><ChipRow items={perfectFor} /></div>
            )}
            {idealWeds.length > 0 && (
              <div style={{ flex: '1 1 180px' }}><GroupLabel>Ideal Weddings</GroupLabel><ChipRow items={idealWeds} /></div>
            )}
          </div>
        </>)}

        {(deliverables.length > 0 || destinations.length > 0) && (<>
          <Divider />
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {deliverables.length > 0 && (
              <div style={{ flex: '1 1 220px' }}><GroupLabel>Deliverables</GroupLabel><ChipRow items={deliverables} /></div>
            )}
            {destinations.length > 0 && (
              <div style={{ flex: '1 1 220px' }}><GroupLabel>Destinations Covered</GroupLabel><ChipRow items={destinations} /></div>
            )}
          </div>
        </>)}

        {(venuesWorked.length > 0 || planners.length > 0) && (<>
          <Divider />
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {venuesWorked.length > 0 && (
              <div style={{ flex: '1 1 220px' }}><GroupLabel>Featured At</GroupLabel><ChipRow items={venuesWorked} /></div>
            )}
            {planners.length > 0 && (
              <div style={{ flex: '1 1 220px' }}><GroupLabel>Worked With Planners</GroupLabel><ChipRow items={planners} /></div>
            )}
          </div>
        </>)}

        {howIWork && (<>
          <Divider />
          <GroupLabel>How I Work</GroupLabel>
          <p style={{ fontFamily: FB, fontSize: 14, color: WHITE_2, lineHeight: 1.8, margin: 0, maxWidth: 640 }}>{howIWork}</p>
        </>)}
      </div>
    </section>
  );
}

function PlannerServicesSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();

  const services     = Array.isArray(venue.plannerServices)   ? venue.plannerServices   : [];
  const approaches   = Array.isArray(venue.plannerApproaches) ? venue.plannerApproaches : [];
  const pricingModel = PLANNER_PRICING_LABELS[venue.plannerPricingModel] || venue.plannerPricingModel || '';
  const startingFrom = venue.plannerStartingFrom || '';
  const teamSize     = venue.plannerTeamSize     || '';
  const yearsActive  = venue.plannerYearsActive  || '';
  const weddingsYear = venue.plannerWeddingsYear || '';
  const destinations = venue.plannerDestinations || '';
  const philosophy   = venue.plannerPhilosophy   || '';

  const hasStats   = !!(teamSize || yearsActive || weddingsYear || startingFrom || pricingModel);
  const hasContent = !!(services.length > 0 || approaches.length > 0 || destinations || philosophy || hasStats);
  if (!hasContent) return null;

  const fieldLabel = {
    fontFamily: FB, fontSize: 9, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.18em',
    color: C.gold, marginBottom: 12, marginTop: 0,
  };

  const ServiceChip = ({ label, gold }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '7px 15px',
      border: `1px solid ${gold ? C.goldBorder : C.border}`,
      borderRadius: 3,
      fontFamily: FB, fontSize: 11, fontWeight: gold ? 500 : 400,
      letterSpacing: '0.03em',
      color: gold ? C.gold : C.textMid,
      background: gold ? C.goldLight : 'transparent',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );

  return (
    <section id="planner-services" style={{ marginBottom: 64 }}>
      <SectionHeading
        title="Services &amp; Approach"
        subtitle="What this planner offers and how they work with couples."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>

        {/* Quick stats row */}
        {hasStats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 16,
            padding: '20px 24px',
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            background: C.cardBg || 'transparent',
          }}>
            {teamSize && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.text }}>{teamSize}</div>
                <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>Team Size</div>
              </div>
            )}
            {yearsActive && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.text }}>{yearsActive}</div>
                <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>Years Active</div>
              </div>
            )}
            {weddingsYear && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.text }}>{weddingsYear}</div>
                <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>Weddings / Year</div>
              </div>
            )}
            {startingFrom && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.text }}>{startingFrom}</div>
                <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>Starting From</div>
              </div>
            )}
            {pricingModel && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FB, fontSize: 15, fontWeight: 600, color: C.text }}>{pricingModel}</div>
                <div style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 4 }}>Pricing Model</div>
              </div>
            )}
          </div>
        )}

        {/* Services offered */}
        {services.length > 0 && (
          <div>
            <p style={fieldLabel}>Services Offered</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {services.map(s => <ServiceChip key={s} label={s} />)}
            </div>
          </div>
        )}

        {/* Planning approach */}
        {approaches.length > 0 && (
          <div>
            <p style={fieldLabel}>Planning Approach</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {approaches.map(a => <ServiceChip key={a} label={a} gold />)}
            </div>
          </div>
        )}

        {/* Planning philosophy */}
        {philosophy && (
          <div>
            <p style={fieldLabel}>Planning Philosophy</p>
            <p style={{ fontFamily: FB, fontSize: 15, color: C.textMid, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>
              {philosophy}
            </p>
          </div>
        )}

        {/* Destinations */}
        {destinations && (
          <div>
            <p style={fieldLabel}>Destinations</p>
            <p style={{ fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.7, margin: 0 }}>
              {destinations}
            </p>
          </div>
        )}

      </div>
    </section>
  );
}

function VendorSpecialtiesSection({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [lightbox, setLightbox] = useState({ imgs: [], idx: null });

  const isPlanner = venue.categorySlug === 'wedding-planners';

  const specialties      = Array.isArray(venue.specialties)          ? venue.specialties          : [];
  const secondaryArch    = Array.isArray(venue.secondaryArchetypes)   ? venue.secondaryArchetypes   : [];
  const moodProfile      = Array.isArray(venue.moodProfile)           ? venue.moodProfile           : [];
  const audienceFit      = Array.isArray(venue.audienceFit)           ? venue.audienceFit           : [];
  const serviceTier      = venue.serviceTier      || '';
  const budgetTier       = venue.budgetTier       || '';
  const primaryArchetype = venue.primaryArchetype || '';
  const luxuryTier       = venue.luxuryTier       || '';

  const specialtiesBodyP1  = venue.specialtiesBodyP1  || '';
  const specialtiesBodyP2  = venue.specialtiesBodyP2  || '';
  const specialtiesImages  = Array.isArray(venue.specialtiesImages) ? venue.specialtiesImages : [];
  const aestheticBodyP1    = venue.aestheticBodyP1    || '';
  const aestheticBodyP2    = venue.aestheticBodyP2    || '';
  const aestheticImages    = Array.isArray(venue.aestheticImages)   ? venue.aestheticImages   : [];

  // Normalise luxury tier to a display-ready label
  const LUXURY_TIER_LABELS = {
    'ultra-luxury': 'Ultra Luxury', 'ultra_luxury': 'Ultra Luxury', 'ultraluxury': 'Ultra Luxury',
    'luxury':       'Luxury',
    'premium':      'Premium',
    'boutique':     'Boutique',
  };
  const luxuryTierLabel = LUXURY_TIER_LABELS[luxuryTier?.toLowerCase?.().replace(/\s+/g, '-')] || luxuryTier;

  const hasServices          = !!(serviceTier || specialties.length > 0 || budgetTier);
  const hasIdentity          = !!(primaryArchetype || secondaryArch.length > 0 || moodProfile.length > 0 || luxuryTier || audienceFit.length > 0);
  const hasSpecialtiesContent = !!(specialtiesBodyP1 || specialtiesBodyP2 || specialtiesImages.length > 0);
  const hasAestheticContent   = !!(aestheticBodyP1 || aestheticBodyP2 || aestheticImages.length > 0);
  if (!hasServices && !hasIdentity && !hasSpecialtiesContent && !hasAestheticContent) return null;


  // Field label style
  const fieldLabel = {
    fontFamily: FB, fontSize: 9, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.18em',
    color: C.gold, marginBottom: 12, marginTop: 0,
  };

  // Standard specialty chip (outline, neutral)
  const SpecialtyChip = ({ label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '7px 15px',
      border: `1px solid ${C.border}`,
      borderRadius: 3,
      fontFamily: FB, fontSize: 11, fontWeight: 400,
      letterSpacing: '0.03em',
      color: C.textMid,
      background: 'transparent',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );

  // Highlighted tier chip (gold accent)
  const TierChip = ({ label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '7px 16px',
      border: `1px solid ${C.goldBorder}`,
      borderRadius: 3,
      fontFamily: FB, fontSize: 12, fontWeight: 600,
      letterSpacing: '0.06em',
      color: C.gold,
      background: C.goldLight,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );

  return (
    <section id="specialties" style={{ marginBottom: 64 }}>

      {/* ── Services & Specialties ─────────────────────────────────────── */}
      {(hasServices || hasSpecialtiesContent) && (
        <div style={{ marginBottom: (hasIdentity || hasAestheticContent) ? 56 : 0 }}>
          <SectionHeading
            title="Services &amp; Specialties"
            subtitle="The services this team offers and the areas they are known for."
          />

          {/* Service tier + budget side by side */}
          {(serviceTier || budgetTier) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: specialties.length > 0 ? 32 : 0 }}>
              {serviceTier && <TierChip label={serviceTier} />}
              {budgetTier  && <TierChip label={budgetTier}  />}
            </div>
          )}

          {/* Specialties */}
          {specialties.length > 0 && (
            <div style={{ marginBottom: (specialtiesBodyP1 || specialtiesBodyP2 || specialtiesImages.length > 0) ? 32 : 0 }}>
              <p style={fieldLabel}>Specialties</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {specialties.map(s => <SpecialtyChip key={s} label={s} />)}
              </div>
            </div>
          )}

          {/* Editorial prose */}
          {specialtiesBodyP1 && (
            <div style={{ marginBottom: specialtiesImages.length > 0 ? 32 : 0 }}>
              <p style={{ fontFamily: FB, fontSize: 15, color: C.textMid, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>
                {specialtiesBodyP1}
              </p>
            </div>
          )}

          {/* Editorial images, 3 × 2 grid, click to open lightbox */}
          {specialtiesImages.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: 8,
            }}>
              {specialtiesImages.slice(0, 6).map((url, i) => (
                <div
                  key={i}
                  style={{ aspectRatio: '3/2', borderRadius: 3, overflow: 'hidden', cursor: 'pointer' }}
                  onClick={() => setLightbox({ imgs: specialtiesImages, idx: i })}
                >
                  <img
                    src={vpImg(url, 800)}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Their Aesthetic ────────────────────────────────────────────── */}
      {(hasIdentity || hasAestheticContent) && (() => {
        const isEnt = venue.categorySlug === 'entertainment';
        const DARK     = '#1c1a17';
        const GOLD_D   = '#c9a84c';
        const WHITE_1  = 'rgba(255,255,255,0.95)';
        const WHITE_2  = 'rgba(255,255,255,0.55)';
        const WHITE_3  = 'rgba(255,255,255,0.35)';
        const DarkLabel = ({ children }) => (
          <p style={{ fontFamily: FB, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.18em', color: GOLD_D, margin: '0 0 0' }}>
            {children}
          </p>
        );
        const DarkChip = ({ label }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 17px', border: `1px solid ${WHITE_3}`, borderRadius: 3, fontFamily: FB, fontSize: 12, fontWeight: 400, letterSpacing: '0.04em', color: WHITE_2, background: 'transparent', whiteSpace: 'nowrap' }}>
            {label}
          </span>
        );
        const DarkDivider = () => <div style={{ borderTop: `1px solid rgba(255,255,255,0.10)`, margin: '24px 0' }} />;

        return (
        <div>
          <SectionHeading
            title={isPlanner ? "Design Philosophy" : isEnt ? "Style & Character" : "Their Aesthetic"}
            subtitle={isPlanner
              ? "How this planner shapes the look, feel, and experience of a wedding celebration."
              : isEnt
                ? "The mood, vision, and artistic identity behind the performance."
                : "The character, mood, and vision behind the work."
            }
            mb={isEnt ? 16 : undefined}
          />

          <div style={isEnt ? { background: DARK, borderRadius: 4, padding: '40px 36px 36px' } : { display: 'flex', flexDirection: 'column', gap: 36 }}>

            {/* primaryArchetype intentionally not shown publicly, kept in DB for internal filtering/Atlas only */}

            {/* Secondary archetypes, pills */}
            {secondaryArch.length > 0 && (
              isEnt ? (
                <div>
                  <DarkLabel>Also Known For</DarkLabel>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                    {secondaryArch.map(s => <DarkChip key={s} label={s} />)}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={fieldLabel}>Also Known For</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {secondaryArch.map(s => <SpecialtyChip key={s} label={s} />)}
                  </div>
                </div>
              )
            )}

            {/* Mood profile, pills */}
            {moodProfile.length > 0 && (
              isEnt ? (
                <div style={{ marginTop: secondaryArch.length > 0 ? 28 : 0 }}>
                  <DarkLabel>Mood</DarkLabel>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                    {moodProfile.map(m => <DarkChip key={m} label={m} />)}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={fieldLabel}>Mood</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {moodProfile.map(m => <SpecialtyChip key={m} label={m} />)}
                  </div>
                </div>
              )
            )}

            {/* Luxury tier, single gold chip; hidden for entertainment (shown in stats strip instead) */}
            {luxuryTierLabel && !isEnt && (
              <div>
                <p style={fieldLabel}>Prestige Level</p>
                <TierChip label={luxuryTierLabel} />
              </div>
            )}

            {/* Audience fit, pills */}
            {audienceFit.length > 0 && (
              isEnt ? (
                <>
                  <DarkDivider />
                  <div>
                    <DarkLabel>Made For</DarkLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                      {audienceFit.map(a => <DarkChip key={a} label={a} />)}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <p style={fieldLabel}>Made For</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {audienceFit.map(a => <SpecialtyChip key={a} label={a} />)}
                  </div>
                </div>
              )
            )}

            {/* Editorial prose */}
            {aestheticBodyP1 && (
              <div style={{ marginBottom: aestheticImages.length > 0 ? 8 : 0 }}>
                <p style={{ fontFamily: FB, fontSize: 15, color: isEnt ? WHITE_2 : C.textMid, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {aestheticBodyP1}
                </p>
              </div>
            )}

            {/* Editorial images, 3 × 2 */}
            {aestheticImages.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: 8,
              }}>
                {aestheticImages.slice(0, 6).map((url, i) => (
                  <div
                    key={i}
                    style={{ aspectRatio: '3/2', borderRadius: 3, overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => setLightbox({ imgs: aestheticImages, idx: i })}
                  >
                    <img
                      src={vpImg(url, 800)}
                      alt=""
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    />
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
        );
      })()}

      {/* ── Lightbox overlay ───────────────────────────────────────────── */}
      {lightbox.idx !== null && lightbox.imgs.length > 0 && (() => {
        const total = lightbox.imgs.length;
        const prev  = () => setLightbox(l => ({ ...l, idx: (l.idx - 1 + total) % total }));
        const next  = () => setLightbox(l => ({ ...l, idx: (l.idx + 1) % total }));
        const close = () => setLightbox({ imgs: [], idx: null });
        const btnBase = {
          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
          width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: '#fff', fontSize: 22, lineHeight: 1,
          backdropFilter: 'blur(6px)',
        };
        return (
          <div
            onClick={close}
            style={{
              position: 'fixed', inset: 0, zIndex: 9000,
              background: 'rgba(0,0,0,0.94)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {/* Close */}
            <button
              onClick={close}
              style={{ position: 'absolute', top: 18, right: 22, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1, opacity: 0.7 }}
            >×</button>

            {/* Counter */}
            <div style={{ position: 'absolute', top: 22, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: 'var(--font-body)', letterSpacing: '0.1em' }}>
              {lightbox.idx + 1} / {total}
            </div>

            {/* Image */}
            <img
              src={vpImg(lightbox.imgs[lightbox.idx], 1800)}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            />

            {/* Prev */}
            {total > 1 && (
              <button onClick={e => { e.stopPropagation(); prev(); }} style={{ ...btnBase, left: 20 }}>‹</button>
            )}
            {/* Next */}
            {total > 1 && (
              <button onClick={e => { e.stopPropagation(); next(); }} style={{ ...btnBase, right: 20 }}>›</button>
            )}
          </div>
        );
      })()}

    </section>
  );
}

// ─── WEDDING WEEKEND ─────────────────────────────────────────────────────────
function WeddingWeekend({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const ww = venue.weddingWeekend;
  if (!ww || ww.enabled === false) return null;
  const experiences = venue.experiences || [];
  const estate  = venue.estateEnabled !== false ? experiences.filter(e => e.category === "estate").slice(0, 6) : [];
  const nearby  = venue.nearbyEnabled !== false ? experiences.filter(e => e.category === "nearby").slice(0, 6) : [];
  const formatDistance = (mins) => {
    if (!mins) return null;
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
  };

  const experienceRow = (exp) => (
    <div key={exp.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: C.bgAlt || C.bg, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <Icon name={EXPERIENCE_KIND_SET.has(exp.kind) ? exp.kind : "nature"} size={18} color={C.textMid} />
        <span style={{ fontFamily: FB, fontSize: 13, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{exp.label}</span>
        {exp.isIncluded && (
          <span style={{ fontFamily: FB, fontSize: 9, color: C.gold, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 700, padding: "2px 6px", border: `1px solid ${C.goldBorder || C.gold}`, background: C.goldLight || "transparent", flexShrink: 0 }}>Included</span>
        )}
        {exp.isPrivate && (
          <span style={{ fontFamily: FB, fontSize: 9, color: C.textMuted, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 700, padding: "2px 6px", border: `1px solid ${C.border}`, flexShrink: 0 }}>Private</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {exp.season && exp.season !== "all-year" && (
          <span style={{ fontFamily: FB, fontSize: 10, color: C.textMuted, fontStyle: "italic" }}>{exp.season}</span>
        )}
        {exp.distanceMinutes && (
          <span style={{ fontFamily: FB, fontSize: 11, color: C.gold, fontWeight: 600 }}>{formatDistance(exp.distanceMinutes)}</span>
        )}
      </div>
    </div>
  );

  const days = (ww.days || [])
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    .slice(0, 4)
    .map(d => ({
      ...d,
      day:   String(d.day   || '').slice(0, 12),
      title: String(d.title || '').slice(0, 28),
      desc:  String(d.desc  || '').slice(0, 110),
    }));

  const dayCard = (d) => (
    <div key={d.day} style={{ padding: 20, border: `1px solid ${C.border}`, background: C.surface, flex: isMobile ? "0 0 220px" : undefined, scrollSnapAlign: isMobile ? "start" : undefined, minHeight: isMobile ? undefined : 130, overflow: 'hidden' }}>
      <div style={{ fontFamily: FB, fontSize: 10, color: C.gold, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>{d.day}</div>
      <div style={{ fontFamily: FD, fontSize: 18, color: C.text, marginBottom: 8 }}>{d.title}</div>
      <p style={{ fontFamily: FB, fontSize: 12, color: C.textLight, lineHeight: 1.65 }}>{d.desc}</p>
    </div>
  );

  return (
    <section id="things-to-do" style={{ marginBottom: 56 }}>
      <SectionHeading title="Your Wedding Weekend" subtitle={venue?.sectionIntros?.weddings || ww.subtitle || ''} />
      {/* Days */}
      {isMobile ? (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch", marginBottom: 36, scrollbarWidth: "none", msOverflowStyle: "none" }} className="vp-weekend-slider">
          {days.map(dayCard)}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(days.length, 4)}, 1fr)`, gap: 12, marginBottom: 36 }}>
          {days.map(dayCard)}
        </div>
      )}
      {/* Experiences */}
      {(estate.length > 0 || nearby.length > 0) && (
        <div className="vp-experiences-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : (estate.length > 0 && nearby.length > 0 ? "1fr 1fr" : "1fr"), gap: isMobile ? 24 : 32 }}>
          {[
            estate.length > 0 && { title: "On the Estate", items: estate },
            nearby.length > 0 && { title: "Nearby Experiences", items: nearby },
          ].filter(Boolean).map(g => (
            <div key={g.title}>
              <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 14 }}>{g.title}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {g.items.map(experienceRow)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── GETTING HERE ─────────────────────────────────────────────────────────────
function GettingHere({ access }) {
  const C = useT();

  // Only render if access data exists with airports
  if (!access || !Array.isArray(access.airports) || access.airports.length === 0) return null;

  const formatDrive = (mins) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
  };
  const driveColor = (mins) => {
    if (mins < 60) return C.green;
    if (mins < 100) return C.gold;
    return C.textLight;
  };

  return (
    <section id="availability" style={{ marginBottom: 56 }}>
      <SectionHeading title="Getting Here" subtitle="Transportation options and airport proximity for guest convenience" />

      {/* Helicopter callout */}
      {access.helicopterTransferAvailable && (
        <div style={{ marginBottom: 20, padding: "14px 20px", background: C.goldLight, border: `1px solid ${C.goldBorder}`, display: "flex", alignItems: "center", gap: 12 }}>
          <Icon name="helicopter" size={22} color={C.gold} />
          <div>
            <span style={{ fontFamily: FB, fontSize: 14, fontWeight: 700, color: C.gold }}>Helicopter transfers available</span>
            {access.helicopterTransferMinutesFromAirport && (
              <span style={{ fontFamily: FB, fontSize: 13, color: C.textLight, marginLeft: 8 }}>
                {access.helicopterTransferMinutesFromAirport} min from {access.primaryAirport.code}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Primary airport highlight */}
      {access.primaryAirport && (
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="airport" size={14} color={C.gold} />
          <span style={{ fontFamily: FB, fontSize: 13, color: C.textMid }}>
            Closest international airport: <strong style={{ color: C.text, fontWeight: 600 }}>{access.primaryAirport.name}</strong>, {formatDrive(access.primaryAirport.driveTimeMinutes)}
          </span>
        </div>
      )}

      {/* Airport table */}
      <div style={{ border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", background: C.bgAlt || C.bg, borderBottom: `1px solid ${C.border}`, padding: "10px 20px" }}>
          {["Airport", "Drive time", "Distance"].map(h => (
            <div key={h} style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>
        {access.airports.map((a, i) => (
          <div key={a.code} style={{
            display: "grid", gridTemplateColumns: "2fr 1fr 1fr",
            padding: "16px 20px", alignItems: "center",
            borderBottom: i < access.airports.length - 1 ? `1px solid ${C.border}` : "none",
            background: C.surface,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name="airport" size={16} color={C.textMuted} />
              <div>
                <span style={{ fontFamily: FD, fontSize: 17, color: C.text }}>{a.name}</span>
                <span style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, marginLeft: 8, padding: "2px 6px", border: `1px solid ${C.border2}` }}>{a.code}</span>
              </div>
            </div>
            <div style={{ fontFamily: FB, fontSize: 14, fontWeight: 700, color: driveColor(a.driveTimeMinutes) }}>{formatDrive(a.driveTimeMinutes)}</div>
            <div style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>{a.distanceKm ? `${a.distanceKm} km` : ""}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── REVIEWS ─────────────────────────────────────────────────────────────────
function Reviews({ testimonials, venue, venueSlug }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [expandedFirst, setExpandedFirst] = useState(false);

  if (!testimonials || !Array.isArray(testimonials) || testimonials.length === 0) return null;

  const featured = testimonials[0];
  const rest = testimonials.slice(1);
  const LONG_THRESHOLD = 300;
  const featuredIsLong = (featured.text || '').length > LONG_THRESHOLD;

  // Avatar initials helper
  const getInitials = (names) => {
    if (!names) return '?';
    const parts = names.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Compact card for horizontal slider
  const compactCard = (r, idx) => (
    <div
      key={r.id || idx}
      style={{
        flex: '0 0 340px',
        padding: 20,
        border: `1px solid ${C.border}`,
        background: C.surface,
        scrollSnapAlign: 'start',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* Stars */}
      <Stars rating={r.rating} size={11} />

      {/* Title */}
      {r.title && (
        <div style={{ fontFamily: FD, fontSize: 14, color: C.text, lineHeight: 1.4 }}>{r.title}</div>
      )}

      {/* Body – 4 line clamp */}
      <p style={{
        fontFamily: FB, fontSize: 13, color: C.textMid, lineHeight: 1.75, margin: 0,
        display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        flex: 1,
      }}>{r.text}</p>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <div style={{
          width: 28, height: 28, background: C.goldLight, border: `1px solid ${C.goldBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FD, fontSize: 11, color: C.gold, flexShrink: 0,
        }}>{getInitials(r.names)}</div>
        <div>
          <div style={{ fontFamily: FB, fontSize: 12, color: C.text, fontWeight: 600 }}>{r.names}</div>
          <div style={{ fontFamily: FB, fontSize: 10, color: C.textMuted }}>
            {[r.location, r.date].filter(Boolean).join(' · ')}
          </div>
        </div>
        {r.verified && (
          <span style={{ marginLeft: 'auto', fontFamily: FB, fontSize: 10, color: C.green, fontWeight: 700, whiteSpace: 'nowrap' }}>✓ Verified</span>
        )}
      </div>
    </div>
  );

  return (
    <section id="reviews" style={{ marginBottom: 56 }}>
      <SectionHeading title="What Couples Say" subtitle={`Rated ${venue?.rating || '5.0'} by ${venue?.reviews || testimonials.length} verified couple${(venue?.reviews || testimonials.length) !== 1 ? 's' : ''}`} />

      {/* ── Featured review ───────────────────────────────────────────────── */}
      <div style={{
        padding: isMobile ? 20 : 32,
        borderLeft: `3px solid ${C.gold}`,
        border: `1px solid ${C.goldBorder}`,
        borderLeftWidth: 3,
        borderLeftColor: C.gold,
        background: C.goldLight,
        marginBottom: rest.length > 0 ? 28 : 0,
        position: 'relative',
      }}>
        {/* Large quote mark */}
        <div style={{
          fontFamily: FD, fontSize: 72, color: C.gold, lineHeight: 0.6,
          marginBottom: 16, opacity: 0.6, userSelect: 'none',
        }}>"</div>

        {/* Title */}
        {featured.title && (
          <div style={{ fontFamily: FD, fontSize: 18, color: C.text, marginBottom: 10 }}>{featured.title}</div>
        )}

        {/* Body */}
        <p style={{
          fontFamily: FB, fontSize: 14, color: C.textMid, lineHeight: 1.8, margin: 0,
          ...(!expandedFirst && featuredIsLong ? {
            display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          } : {}),
        }}>{featured.text}</p>

        {featuredIsLong && (
          <button
            onClick={() => setExpandedFirst(e => !e)}
            style={{
              marginTop: 8, fontFamily: FB, fontSize: 12, color: C.gold,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontWeight: 600,
            }}
          >{expandedFirst ? 'Show less' : 'Read more'}</button>
        )}

        {/* Footer row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <div style={{
            width: 40, height: 40, background: C.goldLight, border: `1px solid ${C.goldBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: FD, fontSize: 15, color: C.gold, flexShrink: 0,
          }}>{getInitials(featured.names)}</div>
          <div>
            <div style={{ fontFamily: FD, fontSize: 15, color: C.text }}>{featured.names}</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted }}>
              {[featured.location, featured.date].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ marginLeft: 4 }}>
            <Stars rating={featured.rating} size={12} />
          </div>
          {featured.verified !== false && (
            <span style={{ fontFamily: FB, fontSize: 11, color: C.green, fontWeight: 700, padding: '2px 8px', border: `1px solid ${C.green}`, borderRadius: 2 }}>✓ Verified</span>
          )}
        </div>
      </div>

      {/* ── More from guests ──────────────────────────────────────────────── */}
      {rest.length > 0 && (
        <>
          <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 12 }}>More from guests</div>
          <div style={{
            display: 'flex', gap: 16, overflowX: 'auto', scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none',
            paddingBottom: 4,
          }} className="vp-reviews-more-slider">
            {rest.map((r, i) => compactCard(r, i))}
          </div>
        </>
      )}

      {/* ── Bottom action row ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 24, flexWrap: 'wrap', gap: 12,
      }}>
        {/* Aggregate */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: FD, fontSize: 36, color: C.gold, lineHeight: 1 }}>{venue?.rating || '5.0'}</span>
          <div>
            <Stars rating={venue?.rating || 5} size={14} />
            <div style={{ fontFamily: FB, fontSize: 12, color: C.textMuted, marginTop: 2 }}>
              {venue?.reviews || testimonials.length} verified review{(venue?.reviews || testimonials.length) !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        {/* Read all link */}
        <a
          /* Gated 2026-08-15: a /reviews page only exists when the listing has at least
             one APPROVED review (the sitemap edge function decides), so linking it from
             any listing showing testimonials produced dead links. Mons Calpe Suite was
             the live case. Falls back to '#' exactly as the no-slug branch already did. */
          href={(() => { const u = venueSlug ? `${window.location.pathname.replace(/\/$/, '')}/reviews` : null; return u && isPubliclyRoutable(u) ? u : '#'; })()}
          style={{
            fontFamily: FB, fontSize: 13, color: C.gold, fontWeight: 600,
            textDecoration: 'none', letterSpacing: '0.2px',
          }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
        >Read all reviews →</a>
      </div>
    </section>
  );
}

// ─── ENQUIRY MODAL (shared: FAQ CTA, hero CTA, compare bar) ──────────────────
function EnquiryModal({ venue, onClose }) {
  const C = useT();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    hasDate: null, date: "", year: "", season: "",
    guests: "", budget: "",
    name1: "", name2: "", email: "", phone: "",
  });
  const [sent, setSent] = useState(false);

  const STEPS = 3;
  const years   = ["2025", "2026", "2027", "2028", "Not sure"];
  const seasons = ["Spring  Mar–May", "Summer  Jun–Aug", "Autumn  Sep–Nov", "Winter  Dec–Feb"];
  const guestTiers  = ["Up to 50", "50–80", "80–120", "120–180", "180+"];
  const budgetTiers = ["Under £15k", "£15–25k", "£25–50k", "£50k+"];

  const pill = (val, active, onClick) => (
    <button key={val} onClick={onClick} style={{
      padding: "8px 14px", border: `1px solid ${active ? C.gold : C.border2}`, borderRadius: "var(--lwd-radius-input)",
      background: active ? C.goldLight : "none",
      color: active ? C.gold : C.textMid,
      fontFamily: FB, fontSize: 12, fontWeight: active ? 700 : 400,
      cursor: "pointer", transition: "all 0.15s", letterSpacing: "0.2px",
    }}>{val}</button>
  );

  const canNext = () => {
    if (step === 1) return form.hasDate !== null && (form.hasDate ? !!form.date : (!!form.year && !!form.season));
    if (step === 2) return !!form.guests;
    if (step === 3) return !!(form.name1 && form.email);
    return false;
  };

  const prefilledMsg = () =>
    `Hi, we're ${form.name1}${form.name2 ? " & " + form.name2 : ""}. We're planning a ${form.hasDate ? `wedding on ${form.date}` : `${form.season?.split("  ")[0].toLowerCase()} ${form.year} wedding`} for ${form.guests} guests${form.budget ? " with a budget of " + form.budget : ""}. We'd love to learn more about ${venue.name}, could you share availability and pricing?`;

  const echoSummary = () => {
    const datePart = form.hasDate
      ? new Date(form.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
      : [form.season?.split("  ")[0], form.year].filter(Boolean).join(" ");
    return [datePart, form.guests ? `${form.guests} guests` : "", form.budget].filter(Boolean).join("  ·  ");
  };

  if (sent) return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, maxWidth: 480, width: "90vw",
        border: `1px solid ${C.border}`, boxShadow: C.shadowLg,
        animation: "lwd-modal-in 0.3s ease", overflow: "hidden",
      }}>
        {/* Venue photo banner */}
        <div style={{ height: 130, position: "relative", overflow: "hidden" }}>
          <img src={imgSrc(venue.imgs[0])} alt={venue.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 100%)" }} />
          <div style={{ position: "absolute", bottom: 14, left: 24, fontFamily: FD, fontSize: 13, color: "rgba(255,255,255,0.8)", letterSpacing: "0.4px" }}>{venue.name} · {venue.location}</div>
        </div>

        {/* Gold ✓ circle, floats on the seam */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: -28 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: C.gold, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, color: "#0f0d0a", fontWeight: 700,
            border: `3px solid ${C.surface}`, boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          }}>✓</div>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 32px 32px", textAlign: "center" }}>
          <h3 style={{ fontFamily: FD, fontSize: 26, color: C.text, marginBottom: 10, lineHeight: 1.2 }}>Enquiry received</h3>
          <p style={{ fontFamily: FB, fontSize: 14, color: C.textLight, lineHeight: 1.75, marginBottom: 20 }}>
            <strong style={{ color: C.text, fontWeight: 600 }}>{venue.owner.name.split(" ")[0]}</strong> will personally review your enquiry and respond within{" "}
            <strong style={{ color: C.text, fontWeight: 600 }}>{venue.responseTime}</strong>.
          </p>

          {/* Echo card, reflects their selections back */}
          <div style={{
            background: C.goldLight, border: `1px solid ${C.goldBorder}`,
            borderRadius: "var(--lwd-radius-card)", padding: "14px 18px", marginBottom: 24, textAlign: "left",
          }}>
            <div style={{ fontFamily: FB, fontSize: 10, color: C.gold, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>Your enquiry summary</div>
            <div style={{ fontFamily: FD, fontSize: 14, color: C.text, lineHeight: 1.65 }}>{echoSummary()}</div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, marginTop: 6 }}>Confirmation sent to {form.email}</div>
          </div>

          <button onClick={onClose} style={{
            padding: "12px 36px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
            color: "#ffffff", fontFamily: FB, fontSize: 12, fontWeight: 800,
            letterSpacing: "1.2px", textTransform: "uppercase", cursor: "pointer",
          }}>Done</button>
        </div>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.58)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, width: "100%", maxWidth: 520, border: `1px solid ${C.border}`, boxShadow: C.shadowLg, position: "relative" }}>

        {/* Gold top bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${C.gold}, ${C.green})` }} />

        {/* Header */}
        <div style={{ padding: "24px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: FB, fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 4 }}>Enquire about {venue.name}</div>
            <h3 style={{ fontFamily: FD, fontSize: 22, color: C.text, margin: 0 }}>
              {step === 1 ? "When is your celebration?" : step === 2 ? "Tell us about your plans" : "Your details"}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: C.textMuted, cursor: "pointer", lineHeight: 1, padding: "0 0 0 16px" }}>✕</button>
        </div>

        {/* Progress dots */}
        <div style={{ display: "flex", gap: 6, padding: "16px 28px 0" }}>
          {[1,2,3].map(s => (
            <div key={s} style={{ flex: 1, height: 3, background: s <= step ? C.gold : C.border, borderRadius: 2, transition: "background 0.3s" }} />
          ))}
        </div>
        <div style={{ padding: "6px 28px 0", fontFamily: FB, fontSize: 11, color: C.textMuted }}>Step {step} of {STEPS}</div>

        {/* Step content */}
        <div style={{ padding: "20px 28px 0" }}>

          {/* Step 1, Date */}
          {step === 1 && (
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[{v: true, l: "I have a date"}, {v: false, l: "Not yet"}].map(o => (
                  <button key={o.l} onClick={() => setForm(f => ({ ...f, hasDate: o.v }))} style={{
                    flex: 1, padding: "11px", border: `1px solid ${form.hasDate === o.v ? C.gold : C.border2}`,
                    borderRadius: "var(--lwd-radius-input)",
                    background: form.hasDate === o.v ? C.goldLight : "none",
                    color: form.hasDate === o.v ? C.gold : C.textMid,
                    fontFamily: FB, fontSize: 13, fontWeight: form.hasDate === o.v ? 700 : 400, cursor: "pointer",
                  }}>{o.l}</button>
                ))}
              </div>
              {form.hasDate === true && (
                <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
                  style={{ width: "100%", padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt, color: C.text, fontFamily: FB, fontSize: 14, outline: "none" }} />
              )}
              {form.hasDate === false && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>Year</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {years.map(y => pill(y, form.year === y, () => setForm(f => ({...f, year: y}))))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>Season</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {seasons.map(s => pill(s, form.season === s, () => setForm(f => ({...f, season: s}))))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2, Guests + Budget */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>Approx. guest count</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {guestTiers.map(g => pill(g, form.guests === g, () => setForm(f => ({...f, guests: g}))))}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted, letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 8 }}>Approximate budget <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {budgetTiers.map(b => pill(b, form.budget === b, () => setForm(f => ({...f, budget: b}))))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3, Contact */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[{k:"name1",ph:"Your name *"},{k:"name2",ph:"Partner's name"}].map(f => (
                  <input key={f.k} placeholder={f.ph} value={form[f.k]} onChange={e => setForm(fm => ({...fm, [f.k]: e.target.value}))}
                    style={{ padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt, color: C.text, fontFamily: FB, fontSize: 13, outline: "none" }} />
                ))}
              </div>
              <input placeholder="Email address *" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                style={{ padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt, color: C.text, fontFamily: FB, fontSize: 13, outline: "none", width: "100%" }} />
              <input placeholder="Phone number (optional)" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                style={{ padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt, color: C.text, fontFamily: FB, fontSize: 13, outline: "none", width: "100%" }} />
              <textarea rows={3} defaultValue={prefilledMsg()}
                style={{ padding: "11px 14px", border: `1px solid ${C.border2}`, background: C.bgAlt, color: C.text, fontFamily: FB, fontSize: 12, outline: "none", resize: "none", lineHeight: 1.6 }} />
              <div style={{ fontFamily: FB, fontSize: 11, color: C.textMuted }}>✓ You'll receive a copy of this enquiry by email</div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div style={{ padding: "20px 28px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {step > 1
            ? <button onClick={() => setStep(s => s - 1)} style={{ background: "none", border: "none", fontFamily: FB, fontSize: 13, color: C.textLight, cursor: "pointer", padding: 0 }}>← Back</button>
            : <div />
          }
          {step < 3
            ? <button onClick={() => setStep(s => s + 1)} disabled={!canNext()} style={{
                padding: "11px 28px", background: canNext() ? C.gold : C.border, border: "none", borderRadius: "var(--lwd-radius-input)",
                color: canNext() ? "#fff" : C.textMuted, fontFamily: FB, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.8px", textTransform: "uppercase", cursor: canNext() ? "pointer" : "default", transition: "all 0.2s",
              }}>Continue →</button>
            : <button onClick={() => setSent(true)} disabled={!canNext()} style={{
                padding: "11px 28px", background: canNext() ? C.gold : C.border, border: "none", borderRadius: "var(--lwd-radius-input)",
                color: canNext() ? "#fff" : C.textMuted, fontFamily: FB, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.8px", textTransform: "uppercase", cursor: canNext() ? "pointer" : "default",
              }}>✦ Send enquiry</button>
          }
        </div>

        {/* Social proof */}
        <div style={{ padding: "0 28px 20px", display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontFamily: FB, fontSize: 11, color: C.green, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="zap" size={12} color={C.green} /> Responds in {venue.responseTime}</span>
          <span style={{ width: 1, height: 10, background: C.border }} />
          <span style={{ fontFamily: FB, fontSize: 11, color: C.textMuted }}>{venue.responseRate}% response rate</span>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
const FAQ_DATA = [
  {
    category: "The Venue",
    icon: "I",
    questions: [
      { q: "Is Villa Rosanova available for exclusive use?", a: "Yes, Villa Rosanova is available for exclusive hire from Thursday to Sunday. Exclusive use includes all 24 bedrooms and 6 suites, full use of the grounds, gardens, pool pavilion and all venue spaces. Pricing from £28,000 for the full weekend." },
      { q: "What is the maximum guest capacity?", a: "The estate accommodates up to 200 guests for a ceremony, 160 for a seated dinner and 180 for a standing reception. For intimate celebrations, we welcome parties from 20 guests." },
      { q: "Can we hold both the ceremony and reception here?", a: "Absolutely. The Cypress Garden seats 200 for outdoor ceremonies, while the Grand Salon accommodates 160 for indoor ceremonies. All reception spaces are on the same estate." },
    ],
  },
  {
    category: "Catering & Drink",
    icon: "II",
    questions: [
      { q: "Do you work with an in-house caterer or can we bring our own?", a: "We have an award-winning in-house culinary team led by Chef Marco Bellini. External caterers are permitted with prior approval and a corkage arrangement. Our sommelier curates a bespoke wine list featuring our own estate Chianti Classico." },
      { q: "Can you accommodate dietary requirements?", a: "Yes, our kitchen is fully equipped to cater for vegan, vegetarian, halal, kosher and gluten-free guests. Please advise your dedicated event planner of any requirements when confirming your booking." },
      { q: "Is there a corkage fee if we bring our own wine?", a: "External wine and spirits are welcome at £18 per bottle. We recommend our estate wine list as a first choice, our Chianti Classico is particularly popular with guests." },
    ],
  },
  {
    category: "Accommodation",
    icon: "III",
    questions: [
      { q: "How many guests can stay on the estate overnight?", a: "Villa Rosanova sleeps 58 guests across 24 bedrooms and 6 suites. All rooms are uniquely decorated and include en-suite bathrooms. Bridal and groom suites are available with dedicated dressing areas." },
      { q: "What are the check-in and check-out times?", a: "Check-in is from 3pm on your arrival day. Check-out is by 11am on your departure day. For exclusive use bookings, we are flexible around your schedule, please discuss timing with your event planner." },
    ],
  },
  {
    category: "Getting Here",
    icon: "IV",
    questions: [
      { q: "What is the closest airport?", a: "Please see our Getting Here section for detailed airport information, driving times, and transfer options." },
      { q: "Is there parking on the estate?", a: "Parking information will be provided during your enquiry consultation." },
    ],
  },
  {
    category: "Planning & Suppliers",
    icon: "V",
    questions: [
      { q: "Do we need to use your recommended suppliers?", a: "We have a curated list of preferred suppliers, florists, photographers, bands and planners, who know the estate well. However, you are welcome to bring your own suppliers subject to prior approval from our events team." },
      { q: "How far in advance should we book?", a: "Peak summer dates (June–September) book 18–24 months in advance. Spring and autumn dates are often available with 12 months' notice. We recommend securing your date as early as possible to avoid disappointment." },
      { q: "Is a wedding planner included in the venue hire?", a: "Our dedicated event coordinator will support you from enquiry through to your wedding day. For comprehensive planning services, we can recommend preferred planning partners." },
    ],
  },
];

function FAQSection({ venue, onAsk }) {
  const C = useT();
  const [openItems, setOpenItems] = useState({});
  const faqData = venue?.faq;
  if (!faqData || faqData.enabled === false) return null;
  const categories = (faqData.categories || []).filter(c => c.questions?.length > 0).slice(0, 4);
  if (categories.length === 0) return null;

  const toggle = (catIdx, qIdx) => {
    const key = `${catIdx}-${qIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <section id="faqs" style={{ marginBottom: 56 }}>
      <SectionHeading
        title={faqData.title || "FAQs"}
        subtitle={faqData.subtitle || ""}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
        {categories.map((cat, catIdx) => (
          <div key={cat.category} style={{
            background: C.bgAlt,
            border: `1px solid ${C.border}`,
            padding: "24px 28px",
          }}>
            {/* Category label */}
            <div style={{
              display: "flex", alignItems: "center", gap: 14,
              marginBottom: 16, paddingBottom: 14,
              borderBottom: `1px solid ${C.goldBorder}`,
            }}>
              <span style={{
                fontFamily: FD,
                fontSize: 11,
                color: C.gold,
                letterSpacing: "2px",
                minWidth: 24,
                textAlign: "center",
                opacity: 0.9,
              }}>{cat.icon}</span>
              <span style={{ fontFamily: FB, fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "1.8px", textTransform: "uppercase" }}>{cat.category}</span>
            </div>

            {/* Questions */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              {cat.questions.map((item, qIdx) => {
                const isOpen = openItems[`${catIdx}-${qIdx}`];
                return (
                  <div key={qIdx} style={{ borderBottom: `1px solid ${C.border}` }}>
                    {/* Question row */}
                    <button
                      onClick={() => toggle(catIdx, qIdx)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: 16,
                        padding: "21px 0", background: "none", border: "none",
                        cursor: "pointer", textAlign: "left",
                        transition: "opacity 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
                      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                    >
                      <span style={{
                        fontFamily: FB, fontSize: 14, fontWeight: 600,
                        color: isOpen ? C.gold : C.text,
                        lineHeight: 1.4, flex: 1,
                        transition: "color 0.2s",
                      }}>{item.q}</span>
                      <span style={{
                        fontSize: 18, color: isOpen ? C.gold : C.textMuted,
                        flexShrink: 0, transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease, color 0.2s",
                        display: "inline-block", fontWeight: 300,
                        lineHeight: 1,
                      }}>+</span>
                    </button>

                    {/* Answer, smooth height transition */}
                    <div style={{
                      overflow: "hidden",
                      maxHeight: isOpen ? 300 : 0,
                      transition: "max-height 0.4s ease",
                    }}>
                      <p style={{
                        fontFamily: FB, fontSize: 14, color: C.textLight,
                        lineHeight: 1.8, paddingBottom: 18,
                        margin: 0, paddingRight: 32,
                      }}>{item.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {faqData.ctaEnabled !== false && (
        <div style={{
          marginTop: 32, padding: "20px 24px",
          background: C.goldLight, border: `1px solid ${C.goldBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        }}>
          <div>
            <div style={{ fontFamily: FD, fontSize: 17, color: C.text, marginBottom: 4 }}>{faqData.ctaHeadline || "Still have a question?"}</div>
            <div style={{ fontFamily: FB, fontSize: 13, color: C.textLight }}>{faqData.ctaSubtext || "Our team is here to help."}</div>
          </div>
          <button style={{
            padding: "11px 24px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
            color: "#fff", fontFamily: FB, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.8px", textTransform: "uppercase", cursor: "pointer",
            flexShrink: 0, transition: "opacity 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          onClick={onAsk}>{faqData.ctaButtonText || "Ask a question"} →</button>
        </div>
      )}
    </section>
  );
}

// ─── SIMILAR VENUES ───────────────────────────────────────────────────────────
// Recommendation logic (production): query venues WHERE country = venue.country
// AND region = venue.region AND venueType = venue.venueType, ordered by
// price proximity and capacity overlap. Max 3 results.
// Admin can override via venue.similarVenuesManualOverride (array of venue objects).
function SimilarVenues({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();

  // Manual admin override / pre-computed list takes priority. When neither
  // exists (the common case for DB venues), fall back to the embedding-similarity
  // engine (match_similar_venues), nearest venues in Character space.
  const manual = (
    venue.similarVenuesManualOverride?.length
      ? venue.similarVenuesManualOverride
      : venue.similar || []
  );
  const usingManual = manual.length > 0;

  const [fetched, setFetched] = useState([]);
  useEffect(() => {
    if (usingManual || !venue.id || venue.similarVenuesEnabled === false) return;
    let alive = true;
    fetchSimilarVenues(venue.id, 6)
      .then((rows) => {
        if (!alive) return;
        setFetched(rows.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          countrySlug: r.countrySlug,
          regionSlug: r.regionSlug,
          categorySlug: r.categorySlug,
          location: r.region || r.city || r.country || '',
          img: r.heroImage,
          price: r.priceFrom,
          currency: r.priceCurrency,
          similarity: r.similarity,
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [venue.id, usingManual, venue.similarVenuesEnabled]);

  // Admin toggle guard (after hooks so hook order stays stable)
  if (venue.similarVenuesEnabled === false) return null;

  const venues = (usingManual ? manual : fetched).slice(0, isMobile ? 6 : 3);
  if (venues.length === 0) return null;

  // Each similar-venue click is an edge in the taste graph (the learning loop):
  // "people viewing X also lean toward Y". Fire-and-forget, then navigate.
  const go = (v) => {
    trackTasteSignal('venue', v.id, 'similar_click', {
      weight: 1.5,
      context: {
        anchor: venue.id,
        anchor_name: venue.name,
        surface: 'similar_rail',
        similarity: v.similarity ?? null,
      },
    });
    if (!v.slug) return;
    navigateTo(buildListingUrl(v));
  };

  return (
    <section id="continue-exploring" style={{ marginBottom: 56 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <SectionHeading title="Continue Exploring" />
      </div>
      <div style={{ fontFamily: FB, fontSize: 12, color: C.gold, marginBottom: 24, marginTop: -20 }}>
        ✦ {usingManual ? 'Curated based on location, venue type & capacity' : `Venues with a similar character to ${venue.name}`}
      </div>
      {isMobile ? (
        <SliderNav className="venue-similar-slider" cardWidth={300} gap={12}>
          {venues.map(({ location: loc, slug, ...rest }) => (
            <div key={rest.id} style={{ flex: "0 0 300px", scrollSnapAlign: "start" }} onClick={() => go({ slug, location: loc, ...rest })}>
              <GCardMobile
                v={{ ...rest, region: loc, image: rest.img, priceFrom: rest.price }}
                saved={false}
                onSave={() => {}}
                onView={() => {}}
              />
            </div>
          ))}
        </SliderNav>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${venues.length}, 1fr)`, gap: 16 }}>
          {venues.map(v => (
            <div key={v.id} style={{ border: `1px solid ${C.border}`, background: C.surface, overflow: "hidden", cursor: "pointer", borderRadius: 2 }}
              onClick={() => go(v)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = C.shadowMd}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
              <div style={{ overflow: "hidden", aspectRatio: "1/1" }}>
                <img src={vpImg(v.img)} alt={v.name} className="lwd-img-zoom" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ padding: 18 }}>
                <div style={{ fontFamily: FD, fontSize: 18, color: C.text, marginBottom: 4 }}>{v.name}</div>
                <div style={{ fontFamily: FB, fontSize: 12, color: C.textLight, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
                  <Icon name="pin" size={12} color={C.textLight} /> {v.location}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: v.rating ? "space-between" : "flex-end" }}>
                  {v.rating ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <Stars rating={v.rating} size={11} />
                      <span style={{ fontFamily: FB, fontSize: 12, color: C.textLight }}>{v.rating}</span>
                    </div>
                  ) : null}
                  {v.price ? (
                    <span style={{ fontFamily: FD, fontSize: 16, color: C.gold }}>From {fmtPrice(v.price, v.currency)}</span>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── RECENTLY VIEWED ──────────────────────────────────────────────────────────
// Reads from localStorage (ldw_recently_viewed_v2). Excludes current venue.
// Max 3 cards shown. Section hidden if empty or admin-disabled.
// ── FeaturedInEdit ── LWD Linking System (Tier 2, reverse direction): editorial
// articles that link to THIS venue (from mentions_json the auto-linker emits).
// Crawlable <a href> back into The Edit. Renders nothing if the venue isn't featured.
function FeaturedInEdit({ listingId }) {
  const C = useT();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let on = true;
    if (!listingId) { setPosts([]); return; }
    fetchEditsMentioningListing(listingId, 12).then(p => { if (on) setPosts(p || []); });
    return () => { on = false; };
  }, [listingId]);
  if (!posts.length) return null;
  // Show max 3 across; as more Edits connect to this listing, the row scrolls
  // horizontally (snap per card) rather than wrapping into an ever-taller grid.
  const scroll = posts.length > 3;
  return (
    <section style={{ marginBottom: 56 }}>
      <SectionHeading title="Featured in The LWD Edit" />
      <div style={{
        display: "flex", gap: 16, marginTop: 8,
        overflowX: scroll ? "auto" : "visible",
        scrollSnapType: scroll ? "x mandatory" : "none",
        paddingBottom: scroll ? 8 : 0,
        WebkitOverflowScrolling: "touch",
      }}>
        {posts.map(p => (
          <a key={p.id} href={`/lwd-edit/${p.slug}`} style={{ textDecoration: "none", border: `1px solid ${C.border}`, background: C.surface, overflow: "hidden", borderRadius: 2, display: "block", flex: "0 0 calc((100% - 32px) / 3)", minWidth: 240, scrollSnapAlign: "start" }}>
            <div style={{ overflow: "hidden", aspectRatio: "3/2", background: C.border }}>
              {p.cover_image && <img src={vpImg(p.cover_image)} alt={p.title} className="lwd-img-zoom" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: p.hero_focal_point || "center", display: "block" }} />}
            </div>
            <div style={{ padding: 16 }}>
              {p.category_label && <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: C.gold, marginBottom: 6 }}>{p.category_label}</div>}
              <div style={{ fontFamily: FD, fontSize: 17, lineHeight: 1.25, color: C.text }}>{p.title}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function RecentlyViewed({ venue }) {
  const C = useT();
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Read stored visits, exclude current venue, cap at 3.
    // Only `name` is structurally required, getRVList already enforces a
    // valid canonicalPath, and `location` is cosmetic (entries with empty
    // location used to be silently dropped, hiding the whole widget).
    const currentCat = venue.categorySlug || venue.category_slug || 'wedding-venues';
    const getCatFromPath = (p) => { const parts = (p || '').split('/').filter(Boolean); return parts.length >= 3 ? parts[parts.length - 2] : 'wedding-venues'; };
    const stored = getRVList()
      .filter(v => v.id !== venue.id && v.name && getCatFromPath(v.canonicalPath) === currentCat)
      .slice(0, 3);

    // Preflight each thumbnail so stale/broken URLs (old CDN paths, deleted
    // media) never show up as grey placeholder tiles. Entries whose image
    // fails to load are stripped from localStorage and from this view.
    let cancelled = false;
    const checkImage = (url) => new Promise((resolve) => {
      if (!url) { resolve(false); return; }
      const img = new Image();
      img.onload  = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src     = url;
    });
    Promise.all(stored.map(s => checkImage(s.img))).then((results) => {
      if (cancelled) return;
      const good = stored.filter((_, i) => results[i]);
      if (good.length !== stored.length) {
        try {
          const goodIds = new Set(good.map(g => g.id));
          // Only drop the entries that failed on THIS strip, keep any other
          // history the user has accumulated (the full list is longer than 3).
          const badIds = new Set(
            stored.filter((_, i) => !results[i]).map(s => s.id)
          );
          const pruned = getRVList().filter(x => !badIds.has(x.id) || goodIds.has(x.id));
          localStorage.setItem(RV_KEY, JSON.stringify(pruned));
        } catch { /* ignore */ }
      }
      setItems(good);
    });

    return () => { cancelled = true; };
  }, [venue.id]);

  // Admin toggle guard + empty guard
  // Also suppress in private preview sessions, prospects should not browse other venues
  const isPreviewSession = typeof window !== 'undefined' && !!sessionStorage.getItem('preview_session');
  if (venue.recentlyViewedEnabled === false || items.length === 0 || isPreviewSession) return null;

  return (
    <section id="recently-viewed" style={{ marginBottom: 56 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <SectionHeading title="Recently Viewed" />
      </div>
      <div style={{ fontFamily: FB, fontSize: 12, color: C.gold, marginBottom: 24, marginTop: -20 }}>
        ✦ Based on your browsing session
      </div>
      {isMobile ? (
        <SliderNav className="venue-recent-slider" cardWidth={300} gap={12}>
          {items.map(({ location: loc, img, price, slug, ...rest }) => (
            <div key={rest.id} style={{ flex: "0 0 300px", scrollSnapAlign: "start" }} onClick={() => {
              if (slug) navigateTo(buildListingUrl({ slug, ...rest }));
            }}>
              <GCardMobile
                v={{ ...rest, region: loc, image: img, priceFrom: price }}
                saved={false}
                onSave={() => {}}
                onView={() => {}}
              />
            </div>
          ))}
        </SliderNav>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, 240px)", justifyContent: "start", gap: 16 }}>
          {items.map(v => (
            <div key={v.id} style={{ border: `1px solid ${C.border}`, background: C.surface, overflow: "hidden", cursor: "pointer", borderRadius: 2 }}
              onClick={() => {
                if (v.slug) navigateTo(buildListingUrl(v));
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = C.shadowMd}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
              <div style={{ overflow: "hidden", aspectRatio: "1/1" }}>
                <img
                  src={vpImg(v.img)}
                  alt={v.name}
                  className="lwd-img-zoom"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={() => {
                    // Self-heal: a stored thumbnail that can't load (stale URL,
                    // deleted media, moved CDN) gets stripped from localStorage
                    // and dropped from the strip, so the user never sees a
                    // broken tile, and the entry rebuilds on the next visit.
                    try {
                      const pruned = getRVList().filter(x => x.id !== v.id);
                      localStorage.setItem(RV_KEY, JSON.stringify(pruned));
                    } catch { /* ignore */ }
                    setItems(prev => prev.filter(x => x.id !== v.id));
                  }}
                />
              </div>
              <div style={{ padding: 18 }}>
                <div style={{ fontFamily: FD, fontSize: 18, color: C.text, marginBottom: 4 }}>{v.name}</div>
                <div style={{ fontFamily: FB, fontSize: 12, color: C.textLight, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
                  <Icon name="pin" size={12} color={C.textLight} /> {v.location}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <Stars rating={v.rating} size={11} />
                    <span style={{ fontFamily: FB, fontSize: 12, color: C.textLight }}>{v.rating}</span>
                  </div>
                  <span style={{ fontFamily: FD, fontSize: 16, color: C.gold }}>From {fmtPrice(v.price, v.currency)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── MOBILE LEAD BAR ─────────────────────────────────────────────────────────
function MobileLeadBar({ venue, show }) {
  const C = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="lwd-mobile-bar" style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
        background: C.surface, borderTop: `1px solid ${C.border}`,
        padding: "12px 20px", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
        transform: show ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
      }}>
        <div>
          <div style={{ fontFamily: FD, fontSize: 18, color: C.gold }}>From {fmtPrice(venue.priceFrom, venue.priceCurrency)}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
            <Stars rating={venue.rating} size={11} />
            {venue.reviews != null && <span style={{ fontFamily: FB, fontSize: 11, color: C.textLight }}>{venue.reviews} reviews</span>}
          </div>
        </div>
        <button onClick={() => setOpen(true)} style={{
          padding: "12px 24px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)",
          color: "#fff", fontFamily: FB, fontSize: 13, fontWeight: 700,
          letterSpacing: "0.6px", textTransform: "uppercase", cursor: "pointer",
        }}>Send Enquiry</button>
      </div>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 950,
          display: "flex", alignItems: "flex-end",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: "100%", background: C.surface, padding: 24,
            borderTop: `3px solid ${C.gold}`,
            animation: "slideUp 0.3s ease",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: FD, fontSize: 22, color: C.text }}>Send Enquiry</div>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: 22, color: C.textMuted, cursor: "pointer" }}>×</button>
            </div>
            <LeadForm venue={venue} />
          </div>
        </div>
      )}
    </>
  );
}

// ─── COMPARE MODAL ───────────────────────────────────────────────────────────

// Venue objects in compare list come from multiple paths with different price shapes.
// This helper normalises them to a single display string.
function resolveDisplayPrice(venue) {
  const raw = venue?.priceFrom ?? venue?.price ?? null;
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/[£€$₹¥]/.test(s) || /^[A-Z]{2,}/.test(s)) return s;
  const n = Number(s.replace(/,/g, ''));
  if (isNaN(n) || n <= 0) return null;
  const sym = (venue?.priceCurrency || '£').trim();
  return `${sym}${n.toLocaleString('en-GB')}`;
}
function numericPrice(venue) {
  const raw = venue?.priceFrom ?? venue?.price ?? null;
  if (!raw) return Infinity;
  const n = Number(String(raw).replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? Infinity : n;
}

const VENUE_TYPE_LABELS = {
  venue:       'Venue',
  villa:       'Villa',
  castle:      'Castle',
  hotel:       'Hotel',
  manor:       'Manor House',
  chateau:     'Château',
  barn:        'Barn',
  garden:      'Garden',
  beach:       'Beach',
  vineyard:    'Vineyard',
  estate:      'Estate',
  palazzo:     'Palazzo',
  resort:      'Resort',
  farmhouse:   'Farmhouse',
  penthouse:   'Penthouse',
  lodge:       'Lodge',
  yacht:       'Yacht',
  island:      'Private Island',
  rooftop:     'Rooftop',
  loft:        'Loft',
  gallery:     'Gallery',
  spa:         'Spa & Retreat',
};

function CompareStat({ label, value, accent, C }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '14px 0', borderBottom: `1px solid rgba(255,255,255,0.07)`,
    }}>
      <span style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.38)', fontWeight: 600 }}>
        {label}
      </span>
      <span style={{ fontFamily: FB, fontSize: 13, color: accent || 'rgba(255,255,255,0.82)', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
        {value || <span style={{ color: 'rgba(255,255,255,0.22)', fontStyle: 'italic', fontWeight: 400 }}>-</span>}
      </span>
    </div>
  );
}

function CompareVenueColumn({ venue, isLast, highlight, C, onClose, onEnquire }) {
  const [hovered,    setHovered]    = useState(false);
  const [activeIdx,  setActiveIdx]  = useState(0);
  const [arrowHover, setArrowHover] = useState(null); // 'prev' | 'next' | null

  // ── Build full gallery from richest available source ──────────────────────
  const galleryImgs = (() => {
    if (Array.isArray(venue?.imgs) && venue.imgs.length > 0) {
      return venue.imgs
        .map(i => ({ src: i.src || i.url, alt: i.alt_text || venue.name }))
        .filter(i => i.src)
        .slice(0, 20);
    }
    if (Array.isArray(venue?.heroImages) && venue.heroImages.length > 0) {
      return venue.heroImages
        .filter(h => h?.url)
        .map(h => ({ src: h.url, alt: venue.name }));
    }
    const single = venue?.heroImage || venue?.cardImage || null;
    return single ? [{ src: single, alt: venue?.name || '' }] : [];
  })();

  const totalImgs  = galleryImgs.length;
  const currentImg = galleryImgs[activeIdx] || null;

  const goPrev = (e) => {
    e.stopPropagation();
    setActiveIdx(i => (i - 1 + totalImgs) % totalImgs);
  };
  const goNext = (e) => {
    e.stopPropagation();
    setActiveIdx(i => (i + 1) % totalImgs);
  };

  const locationParts = [venue?.city, venue?.region, venue?.country]
    .filter(Boolean)
    .map((p, i, arr) => {
      const norm = p.charAt(0).toUpperCase() + p.slice(1);
      return i > 0 && norm.toLowerCase() === arr[i - 1].toLowerCase() ? null : norm;
    })
    .filter(Boolean);
  const location = locationParts.join(', ');
  const flag = COUNTRY_FLAG[venue?.country] || '';
  const price = resolveDisplayPrice(venue);
  const capacity = venue?.capacity ? `Up to ${Number(venue.capacity).toLocaleString()} guests` : null;
  const type = VENUE_TYPE_LABELS[venue?.listingType?.toLowerCase?.()] ||
    (venue?.listingType ? venue.listingType.charAt(0).toUpperCase() + venue.listingType.slice(1) : null);
  const rating = venue?.rating ? Number(venue.rating).toFixed(1) : null;
  const reviews = venue?.reviewCount ?? venue?.reviews ?? null;
  const exclusiveUse = venue?.exclusiveUseEnabled ?? venue?.exclusiveUse?.enabled ?? null;
  const slug = venue?.slug || null;
  const profileUrl = listingProfileUrl({
    ...venue,
    category: venue?.categorySlug || venue?.category_slug || venue?.category || venue?.listingType,
  });

  if (!venue || (!venue.name && !currentImg)) {
    return (
      <div style={{
        borderRight: isLast ? 'none' : `1px solid rgba(184,160,90,0.12)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 500,
      }}>
        <div style={{ fontFamily: FB, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Unable to load</div>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRight: isLast ? 'none' : `1px solid rgba(184,160,90,0.12)`,
        display: 'flex', flexDirection: 'column',
        position: 'relative',
        background: hovered ? '#111110' : '#0f0f0d',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.22s cubic-bezier(0.25,0.46,0.45,0.94), background 0.2s ease',
        boxShadow: hovered ? '0 12px 40px rgba(0,0,0,0.5)' : 'none',
        zIndex: hovered ? 2 : 1,
      }}
    >

      {/* Highlight badge */}
      {highlight && (
        <div style={{
          position: 'absolute', top: 16, left: 16, zIndex: 4,
          background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)',
          border: `1px solid ${C.gold}`,
          padding: '3px 10px', borderRadius: 2,
          fontFamily: FB, fontSize: 9, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase', color: C.gold,
        }}>
          {highlight}
        </div>
      )}

      {/* ── Gallery ── */}
      <div style={{ position: 'relative', height: 340, flexShrink: 0, overflow: 'hidden', background: '#1a1a18' }}>

        {/* Slides, instant crossfade */}
        {galleryImgs.map((img, i) => (
          <img
            key={i}
            src={vpImg(img.src)}
            alt={img.alt}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%', objectFit: 'cover',
              opacity: i === activeIdx ? 1 : 0,
              transition: 'opacity 0.38s ease',
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Fallback placeholder */}
        {galleryImgs.length === 0 && (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a18 0%, #2a2a24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: FD, fontSize: 32, color: 'rgba(184,160,90,0.2)' }}>✦</span>
          </div>
        )}

        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)', zIndex: 1 }} />

        {/* Prev / Next arrows, shown only when >1 image and card is hovered */}
        {totalImgs > 1 && (
          <>
            <button
              onClick={goPrev}
              onMouseEnter={() => setArrowHover('prev')}
              onMouseLeave={() => setArrowHover(null)}
              aria-label="Previous photo"
              style={{
                position: 'absolute', left: 10, top: '50%',
                transform: `translateY(-50%) ${arrowHover === 'prev' ? 'scale(1.1)' : 'scale(1)'}`,
                zIndex: 3,
                width: 32, height: 32, borderRadius: '50%',
                background: arrowHover === 'prev' ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.48)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#fff', fontSize: 14, lineHeight: 1,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.2s ease, background 0.15s, transform 0.15s',
                backdropFilter: 'blur(8px)',
              }}
            >
              ‹
            </button>
            <button
              onClick={goNext}
              onMouseEnter={() => setArrowHover('next')}
              onMouseLeave={() => setArrowHover(null)}
              aria-label="Next photo"
              style={{
                position: 'absolute', right: 10, top: '50%',
                transform: `translateY(-50%) ${arrowHover === 'next' ? 'scale(1.1)' : 'scale(1)'}`,
                zIndex: 3,
                width: 32, height: 32, borderRadius: '50%',
                background: arrowHover === 'next' ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.48)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#fff', fontSize: 14, lineHeight: 1,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.2s ease, background 0.15s, transform 0.15s',
                backdropFilter: 'blur(8px)',
              }}
            >
              ›
            </button>
          </>
        )}

        {/* Photo count badge, top right */}
        {totalImgs > 1 && (
          <div style={{
            position: 'absolute', top: 14, right: 14, zIndex: 3,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)',
            padding: '3px 9px', borderRadius: 20,
            fontFamily: FB, fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.04em',
          }}>
            {activeIdx + 1} / {totalImgs}
          </div>
        )}

        {/* Dot indicators, sit just above the name/rating overlay */}
        {totalImgs > 1 && totalImgs <= 12 && (
          <div style={{
            position: 'absolute', bottom: 92, left: 0, right: 0,
            display: 'flex', justifyContent: 'center', gap: 5, zIndex: 3,
          }}>
            {galleryImgs.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                aria-label={`Photo ${i + 1}`}
                style={{
                  width: i === activeIdx ? 18 : 5,
                  height: 5, borderRadius: 3,
                  background: i === activeIdx ? C.gold : 'rgba(255,255,255,0.35)',
                  border: 'none', cursor: 'pointer', padding: 0,
                  transition: 'width 0.3s ease, background 0.2s ease',
                }}
              />
            ))}
          </div>
        )}

        {/* Name / rating overlay */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 24px 20px', zIndex: 2 }}>
          {rating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 7 }}>
              <span style={{ color: C.gold, fontSize: 10 }}>★</span>
              <span style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>
                {rating}
              </span>
              {reviews > 0 && (
                <span style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                  · {reviews} review{reviews !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
          <div style={{ fontFamily: FD, fontSize: 22, color: '#fff', fontWeight: 400, lineHeight: 1.2, marginBottom: 5 }}>
            {venue.name}
          </div>
          {location && (
            <div style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.52)', letterSpacing: '0.02em' }}>
              {flag && <span style={{ marginRight: 5 }}>{flag}</span>}
              {location}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ padding: '20px 24px 16px', flex: 1, background: 'transparent' }}>
        {/* Price, decision anchor, prominent */}
        <div style={{
          padding: '16px 0 14px',
          borderBottom: `1px solid rgba(255,255,255,0.07)`,
          marginBottom: 2,
        }}>
          <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.38)', fontWeight: 600, marginBottom: 6 }}>
            Starting From
          </div>
          <div style={{ fontFamily: FD, fontSize: 28, color: C.gold, fontWeight: 400, letterSpacing: '0.01em', lineHeight: 1 }}>
            {price || <span style={{ fontFamily: FB, fontSize: 13, color: 'rgba(255,255,255,0.22)', fontStyle: 'italic', fontWeight: 400 }}>Price on request</span>}
          </div>
        </div>

        <CompareStat label="Capacity" value={capacity} C={C} />
        <CompareStat label="Venue Type" value={type} C={C} />
        <CompareStat
          label="Exclusive Use"
          value={
            exclusiveUse === true  ? (
              <span style={{ color: '#6fcf67', fontWeight: 700 }}>✓ Available</span>
            ) : exclusiveUse === false ? (
              <span style={{ color: 'rgba(255,255,255,0.28)' }}>
                <span style={{ marginRight: 5, fontSize: 11 }}>×</span>Not offered
              </span>
            ) : null
          }
          C={C}
        />
      </div>

      {/* CTAs */}
      <div style={{ padding: '20px 24px 30px', display: 'flex', flexDirection: 'column', gap: 10, background: 'transparent' }}>
        {/* Primary, Send Enquiry */}
        <button
          onClick={() => onEnquire && onEnquire(venue)}
          style={{
            display: 'block', width: '100%', textAlign: 'center',
            padding: '13px 16px',
            background: C.gold,
            border: 'none',
            borderRadius: 'var(--lwd-radius-input)',
            color: '#0f0d0a', fontFamily: FB, fontSize: 12, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#b8922a';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(211,154,33,0.35)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = C.gold;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Send Enquiry →
        </button>

        {/* Secondary, View Profile */}
        {profileUrl && (
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block', textAlign: 'center',
              padding: '11px 16px',
              background: 'transparent',
              border: `1px solid rgba(255,255,255,0.14)`,
              borderRadius: 'var(--lwd-radius-input)',
              color: 'rgba(255,255,255,0.45)', fontFamily: FB, fontSize: 11, fontWeight: 500,
              letterSpacing: '0.04em',
              textDecoration: 'none', cursor: 'pointer',
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.32)'; e.currentTarget.style.color = 'rgba(255,255,255,0.72)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
          >
            View Full Profile
          </a>
        )}
      </div>
    </div>
  );
}

// ─── MINI CALENDAR ────────────────────────────────────────────────────────────
const CAL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAL_DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function MiniCalendar({ anchorRect, selected, onChange, onClose }) {
  const initMonth = () => {
    const base = selected || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  };
  const [month, setMonth] = useState(initMonth);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('[data-lwdcal]')) onClose(); };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  const prevMonth = (e) => { e.stopPropagation(); setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1)); };
  const nextMonth = (e) => { e.stopPropagation(); setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1)); };

  const today      = new Date();
  const firstDow   = (month.getDay() + 6) % 7; // Mon=0
  const daysInMo   = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells      = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMo }, (_, i) => i + 1)];

  const isSel  = (d) => d && selected && selected.getFullYear() === month.getFullYear() && selected.getMonth() === month.getMonth() && d === selected.getDate();
  const isToday = (d) => d && today.getFullYear() === month.getFullYear() && today.getMonth() === month.getMonth() && d === today.getDate();

  const pick = (d) => {
    if (!d) return;
    onChange(new Date(month.getFullYear(), month.getMonth(), d));
    onClose();
  };

  // Position: below the anchor, flip up if too close to bottom
  const viewH = window.innerHeight;
  const calH  = 280;
  const top   = anchorRect.bottom + 6 + calH > viewH ? anchorRect.top - calH - 6 : anchorRect.bottom + 6;
  const left  = Math.min(anchorRect.left, window.innerWidth - 268);

  const navBtn = {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
    fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '2px 6px',
    transition: 'color 0.15s',
  };

  return (
    <div
      data-lwdcal=""
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top, left, zIndex: 9999,
        width: 256,
        background: '#0d0d0b',
        border: '1px solid rgba(184,160,90,0.28)',
        borderRadius: 10,
        padding: '14px 14px 16px',
        boxShadow: '0 28px 70px rgba(0,0,0,0.85)',
        animation: 'lwd-modal-in 0.15s ease',
      }}
    >
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button style={navBtn} onClick={prevMonth}
          onMouseEnter={e => e.currentTarget.style.color = '#d39a21'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        >‹</button>
        <span style={{ fontFamily: FB, fontSize: 12, color: '#fff', fontWeight: 600, letterSpacing: '0.04em' }}>
          {CAL_MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        <button style={navBtn} onClick={nextMonth}
          onMouseEnter={e => e.currentTarget.style.color = '#d39a21'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        >›</button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {CAL_DAYS.map(d => (
          <div key={d} style={{ fontFamily: FB, fontSize: 9, color: 'rgba(255,255,255,0.28)', textAlign: 'center', padding: '2px 0', letterSpacing: '0.06em' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Date cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => (
          <button
            key={i}
            onClick={() => pick(d)}
            disabled={!d}
            style={{
              height: 30, borderRadius: 6, border: 'none',
              background: isSel(d)   ? '#d39a21'
                        : isToday(d) ? 'rgba(211,154,33,0.14)'
                        : 'transparent',
              outline: isToday(d) && !isSel(d) ? '1px solid rgba(211,154,33,0.32)' : 'none',
              color: isSel(d) ? '#0f0d0a' : d ? 'rgba(255,255,255,0.78)' : 'transparent',
              fontFamily: FB, fontSize: 12,
              fontWeight: isSel(d) ? 700 : 400,
              cursor: d ? 'pointer' : 'default',
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={e => { if (d && !isSel(d)) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={e => { if (d && !isSel(d)) e.currentTarget.style.background = isToday(d) ? 'rgba(211,154,33,0.14)' : 'transparent'; }}
          >
            {d || ''}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── COMPARE ENQUIRY FORM ─────────────────────────────────────────────────────
// Dark single-page form that floats above the compare modal.
// Compare grid stays visible in the background through the semi-opaque overlay.
const BASE_MSG = "We are currently exploring venues for our wedding and would love to learn more about your availability, pricing, and options.";

function buildMessage(date, guests) {
  const d = date?.trim();
  const g = guests?.trim();
  if (!d && !g) return BASE_MSG;
  const datePart   = d ? ` on ${d}` : '';
  const guestPart  = g ? ` for around ${g} guests` : '';
  return `We are currently exploring venues for our wedding${datePart}${guestPart} and would love to learn more about your availability, pricing, and options.`;
}

function CompareEnquiryForm({ venue, onClose }) {
  const openMiniBar = useAura((s) => s.openMiniBar);
  const sessionId   = useAura((s) => s.session.id);
  const [form,        setForm]        = useState({ name1: '', name2: '', email: '', phone: '', date: '', guests: '', message: BASE_MSG });
  const [sent,        setSent]        = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [msgEdited,   setMsgEdited]   = useState(false);
  const [calOpen,     setCalOpen]     = useState(false);
  const [calAnchor,   setCalAnchor]   = useState(null);
  const [selDate,     setSelDate]     = useState(null);
  const dateFieldRef = useRef(null);
  const textareaRef  = useRef(null);

  // Place cursor at end of prefilled message when form mounts
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const len = ta.value.length;
    ta.setSelectionRange(len, len);
    // don't auto-focus on mount, let user tab in naturally
  }, []);

  // Rebuild message whenever date/guests change, only if user hasn't manually edited
  const { date, guests } = form;
  useEffect(() => {
    if (msgEdited) return;
    setForm(f => ({ ...f, message: buildMessage(date, guests) }));
  }, [date, guests, msgEdited]);

  const openCal = () => {
    if (dateFieldRef.current) setCalAnchor(dateFieldRef.current.getBoundingClientRect());
    setCalOpen(true);
  };

  const pickDate = (d) => {
    setSelDate(d);
    const label = `${d.getDate()} ${CAL_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    setForm(f => ({ ...f, date: label }));
    setCalOpen(false);
  };

  const clearDate = (e) => {
    e.stopPropagation();
    setSelDate(null);
    setForm(f => ({ ...f, date: '' }));
  };

  const firstImg = venue?.imgs?.[0];
  const heroSrc = (typeof firstImg === 'string' ? firstImg : firstImg?.src) || venue?.heroImage || venue?.cardImage || venue?.gallery?.[0]?.src || venue?.gallery?.[0] || null;
  const loc     = [venue?.city, venue?.country].filter(Boolean).join(', ');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSubmit = !!(form.name1 && form.email) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createLead({
        leadSource:   'Compare Enquiry',
        leadChannel:  'compare_modal',
        leadType:     'venue_enquiry',
        listingId:    String(venue.id),
        venueId:      String(venue.id),
        firstName:    form.name1.trim() || undefined,
        lastName:     form.name2.trim() || undefined,
        fullName:     [form.name1.trim(), form.name2.trim()].filter(Boolean).join(' & ') || undefined,
        email:        form.email.trim(),
        phone:        form.phone.trim() || undefined,
        weddingDate:  form.date.trim()   || undefined,
        guestCount:   form.guests.trim() || undefined,
        message:      form.message.trim(),
        auraSessionId: sessionId || undefined,
        intentSummary: `Venue enquiry from Compare modal for ${venue.name}`,
        requirementsJson: { source: 'compare_modal', venue_name: venue.name },
        tagsJson:     ['compare_enquiry'],
        consentDataProcessing: true,
      });
      if (!result.success) {
        console.error('[CompareEnquiryForm] createLead failed:', result.error);
        throw new Error(`DB error: ${result.error?.message || result.error?.code || JSON.stringify(result.error)}`);
      }
      setSent(true);
    } catch (err) {
      console.error('[CompareEnquiryForm] submit failed:', err?.message, err);
      setSubmitError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Design tokens ─────────────────────────────────────────────────────────
  const GOLD        = '#d39a21';
  const GOLD_DIM    = 'rgba(211,154,33,0.55)';
  const GOLD_GLOW   = 'rgba(211,154,33,0.18)';

  const inputBase = {
    width: '100%', padding: '12px 16px',
    background: 'rgba(255,255,255,0.04)',         // darker resting, stays in the dark system
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 'var(--lwd-radius-input)',
    color: 'rgba(255,255,255,0.92)',              // text high-contrast, background not
    fontFamily: FB, fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.18s, background 0.18s, box-shadow 0.18s',
  };
  const FOCUS_SHADOW = `0 0 0 3px rgba(211,154,33,0.16), inset 0 1px 0 rgba(255,255,255,0.03)`;
  const focusStyle = (e) => {
    e.currentTarget.style.borderColor = 'rgba(211,154,33,0.52)';
    e.currentTarget.style.background  = 'rgba(255,255,255,0.06)';  // slight lift, not a flash
    e.currentTarget.style.boxShadow   = FOCUS_SHADOW;
  };
  const blurStyle = (e) => {
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
    e.currentTarget.style.background  = 'rgba(255,255,255,0.04)';
    e.currentTarget.style.boxShadow   = 'none';
  };

  const mkLabel = (text) => (
    <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 700, marginBottom: 7 }}>
      {text}
    </div>
  );
  const mkField = (k, label, type = 'text') => (
    <div>
      {mkLabel(label)}
      <input type={type} value={form[k]} onChange={e => set(k, e.target.value)}
        onFocus={focusStyle} onBlur={blurStyle}
        style={inputBase} />
    </div>
  );

  // ── Layout styles ──────────────────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 1100,
    background: 'rgba(0,0,0,0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(8px, 3vw, 24px)',
    backdropFilter: 'blur(6px)',
  };
  const panelStyle = {
    // Warm near-black with very subtle gold undertone
    background: 'linear-gradient(170deg, #111009 0%, #0c0c0a 50%, #0f0d08 100%)',
    border: '1px solid rgba(211,154,33,0.32)',
    borderRadius: 'clamp(10px, 2vw, 16px)',
    width: '100%', maxWidth: 500,
    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
    // Multi-layer glow: close bloom + wide ambient + depth shadow
    boxShadow: [
      `inset 0 1px 0 rgba(255,255,255,0.06)`,           // top inner light edge
      `0 0 0 1px rgba(211,154,33,0.08)`,                // halo ring, very quiet
      `0 0 28px 4px rgba(211,154,33,0.10)`,             // close bloom, softer than CTA
      `0 0 80px 24px rgba(211,154,33,0.05)`,            // mid ambient, wide & faint
      `0 0 160px 60px rgba(211,154,33,0.025)`,          // far ambient, just a hint
      `0 60px 160px rgba(0,0,0,0.97)`,                  // depth shadow
    ].join(', '),
    animation: 'lwd-modal-in 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
  };

  // ── Confirmation screen ───────────────────────────────────────────────────
  if (sent) return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={e => e.stopPropagation()} style={{ ...panelStyle, maxWidth: 460 }}>

        {/* Hero image carries through to confirmation */}
        {heroSrc && (
          <div style={{ position: 'relative', height: 120, overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
            <img src={vpImg(heroSrc)} alt={venue.name} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.6)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(12,12,10,1) 0%, rgba(0,0,0,0.1) 100%)' }} />
          </div>
        )}

        <div style={{ padding: heroSrc ? '0 44px 52px' : '60px 44px 52px', textAlign: 'center' }}>
          {/* Gold ring icon, floats on the hero seam */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD} 0%, #a87d2a 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, color: '#0f0d0a', fontWeight: 700,
            margin: heroSrc ? '-32px auto 28px' : '0 auto 28px',
            position: 'relative', zIndex: 1,
            boxShadow: `0 0 0 6px rgba(12,12,10,1), 0 0 0 10px rgba(211,154,33,0.14), 0 0 40px rgba(211,154,33,0.4)`,
          }}>✦</div>

          <div style={{ fontFamily: FD, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, fontWeight: 400, marginBottom: 14, opacity: 0.85 }}>
            One step closer
          </div>
          <div style={{ fontFamily: FD, fontSize: 28, color: '#fff', marginBottom: 16, lineHeight: 1.25, letterSpacing: '0.01em' }}>
            Your enquiry is on its way
          </div>
          <div style={{ fontFamily: FB, fontSize: 13, color: 'rgba(255,255,255,0.48)', lineHeight: 1.85, marginBottom: 36, maxWidth: 320, margin: '0 auto 36px' }}>
            You've taken a beautiful first step.<br />
            The team at <span style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 500 }}>{venue.name}</span> will reach out personally to discuss your wedding.
          </div>

          <button onClick={onClose} style={{
            padding: '15px 52px',
            background: `linear-gradient(135deg, #d4a93e 0%, ${GOLD} 45%, #b8882a 100%)`,
            border: 'none', borderRadius: 'var(--lwd-radius-input)',
            color: '#0a0800', fontFamily: FB, fontSize: 12, fontWeight: 800,
            letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
            boxShadow: `0 2px 0 rgba(0,0,0,0.2), 0 6px 28px rgba(211,154,33,0.38), inset 0 1px 0 rgba(255,255,255,0.18)`,
            transition: 'transform 0.18s, box-shadow 0.18s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 2px 0 rgba(0,0,0,0.2), 0 14px 36px rgba(211,154,33,0.52), inset 0 1px 0 rgba(255,255,255,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 0 rgba(0,0,0,0.2), 0 6px 28px rgba(211,154,33,0.38), inset 0 1px 0 rgba(255,255,255,0.18)'; }}
          >
            Back to comparison
          </button>

          <div style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 16, lineHeight: 1.7, letterSpacing: '0.01em' }}>
            You can continue exploring or send another enquiry
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={e => e.stopPropagation()} style={panelStyle}>

      {/* ── Hero banner, tall, cinematic ── */}
      <div style={{ position: 'relative', height: 150, overflow: 'hidden', borderRadius: '16px 16px 0 0', flexShrink: 0 }}>
        {heroSrc
          ? <img src={vpImg(heroSrc)} alt={venue.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a14 0%, #252518 100%)' }} />
        }
        {/* Cinematic gradient, deep at bottom, clear at top */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.3) 55%, rgba(0,0,0,0.1) 100%)' }} />
        {/* Venue info */}
        <div style={{ position: 'absolute', bottom: 16, left: 22, right: 52 }}>
          <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, fontWeight: 700, marginBottom: 5, opacity: 0.9 }}>
            {venue?.categorySlug === 'wedding-venues' ? 'Venue Enquiry' : venue?.categorySlug === 'wedding-planners' ? 'Planner Enquiry' : venue?.listingType === 'boutique' ? 'Boutique Enquiry' : 'Enquiry'}
          </div>
          <div style={{ fontFamily: FD, fontSize: 21, color: '#fff', lineHeight: 1.2, marginBottom: 3 }}>{venue.name}</div>
          {loc && <div style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.02em' }}>{loc}</div>}
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 10, right: 10,
          width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.12)',
          color: 'rgba(255,255,255,0.5)', fontSize: 15, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >×</button>
      </div>

      {/* Gold accent bar, full gradient, 3px */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${GOLD} 0%, rgba(184,160,90,0.6) 60%, rgba(184,160,90,0.05) 100%)` }} />

      {/* ── Form body ── */}
      <div style={{ padding: 'clamp(16px, 4vw, 28px) clamp(16px, 5vw, 28px) 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Section title */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, fontWeight: 700, opacity: 0.8, marginBottom: 7 }}>
            Begin your enquiry
          </div>
          <div style={{ fontFamily: FD, fontSize: 20, color: '#fff', lineHeight: 1.2, letterSpacing: '0.01em', marginBottom: 8 }}>
            Tell us about your wedding
          </div>
          <div style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.01em', lineHeight: 1.5 }}>
            Your enquiry will be sent directly to this venue
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {mkField('name1', 'Your name *')}
          {mkField('name2', "Partner's name")}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {mkField('email', 'Email *', 'email')}
          {mkField('phone', 'Phone', 'tel')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* Date, calendar picker */}
          <div>
            {mkLabel('Wedding date')}
            <div
              ref={dateFieldRef}
              onClick={openCal}
              style={{
                ...inputBase,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', userSelect: 'none',
                borderColor:  calOpen ? 'rgba(211,154,33,0.52)' : 'rgba(255,255,255,0.1)',
                background:   calOpen ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
                boxShadow:    calOpen ? FOCUS_SHADOW : 'none',
              }}
            >
              <span style={{ color: form.date ? '#fff' : 'rgba(255,255,255,0.32)', fontSize: 13 }}>
                {form.date || 'Select a date'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {form.date && (
                  <span onClick={clearDate}
                    style={{ color: 'rgba(255,255,255,0.3)', fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: '0 2px' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
                  >×</span>
                )}
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ color: GOLD, opacity: 0.65, flexShrink: 0 }}>
                  <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M5 1v4M11 1v4M1 7h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
            </div>
          </div>
          {mkField('guests', 'Estimated guests')}
        </div>

        {/* Calendar popup */}
        {calOpen && calAnchor && (
          <MiniCalendar anchorRect={calAnchor} selected={selDate} onChange={pickDate} onClose={() => setCalOpen(false)} />
        )}

        {/* Divider, transition to personal message */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0',
        }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
          <span style={{ fontFamily: FB, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>Your message</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
            {mkLabel('Message')}
            <span style={{ fontFamily: FB, fontSize: 9, color: 'rgba(255,255,255,0.24)', letterSpacing: '0.02em', fontStyle: 'italic' }}>
              Add any details to personalise
            </span>
          </div>
          <textarea
            ref={textareaRef}
            value={form.message}
            onChange={e => { setMsgEdited(true); set('message', e.target.value); }}
            onFocus={e => {
              focusStyle(e);
              if (!msgEdited) { const len = e.target.value.length; e.target.setSelectionRange(len, len); }
            }}
            onBlur={blurStyle}
            rows={4}
            style={{ ...inputBase, resize: 'vertical', lineHeight: 1.7, fontSize: 13 }}
          />
        </div>

      </div>

      {/* ── Sticky CTA ── */}
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'linear-gradient(to bottom, transparent 0%, #0c0c0a 28%)',
        padding: '28px 28px 20px',
        display: 'flex', flexDirection: 'column', gap: 11,
      }}>
        <button
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={{
            padding: '16px 24px', width: '100%',
            background: canSubmit
              ? `linear-gradient(135deg, #d4a93e 0%, ${GOLD} 45%, #b8882a 100%)`
              : 'rgba(211,154,33,0.12)',
            border: canSubmit ? 'none' : `1px solid rgba(211,154,33,0.18)`,
            borderRadius: 'var(--lwd-radius-input)',
            color: canSubmit ? '#0a0800' : 'rgba(255,255,255,0.25)',
            fontFamily: FB, fontSize: 13, fontWeight: 800,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: canSubmit ? 'pointer' : 'default',
            boxShadow: canSubmit
              ? `0 2px 0 rgba(0,0,0,0.2), 0 6px 28px rgba(211,154,33,0.38), inset 0 1px 0 rgba(255,255,255,0.18)`
              : 'none',
            transition: 'all 0.18s',
            position: 'relative',
          }}
          onMouseEnter={e => { if (canSubmit) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 2px 0 rgba(0,0,0,0.2), 0 14px 36px rgba(211,154,33,0.52), inset 0 1px 0 rgba(255,255,255,0.18)'; } }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = canSubmit ? '0 2px 0 rgba(0,0,0,0.2), 0 6px 28px rgba(211,154,33,0.38), inset 0 1px 0 rgba(255,255,255,0.18)' : 'none'; }}
        >
          {submitting ? 'Sending…' : 'Send Enquiry →'}
        </button>
        {submitError && (
          <div style={{ fontFamily: FB, fontSize: 11, color: '#e57373', textAlign: 'center', lineHeight: 1.5 }}>
            {submitError}
          </div>
        )}
        <div style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.55)', textAlign: 'center', lineHeight: 1.65, letterSpacing: '0.01em' }}>
          Your details are shared only with this venue and handled in confidence.
        </div>

        {/* Aura soft link */}
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button
            type="button"
            onClick={openMiniBar}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontFamily: FB, fontSize: 11, letterSpacing: '0.03em',
              color: 'rgba(211,154,33,0.55)',
              transition: 'color 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#d39a21'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(211,154,33,0.55)'; }}
          >
            ✦ Have questions before sending? Chat with Aura
          </button>
        </div>
      </div>

    </div>
    </div>
  );
}

function CompareModal({ items, onClose }) {
  const C = DARK; // modal is always dark
  const openWorkspace  = useAura((s) => s.openWorkspace);
  const sendMessage    = useAura((s) => s.sendMessage);
  const setChatContext = useAura((s) => s.setChatContext);
  const messages       = useAura((s) => s.messages);
  const [venues,       setVenues]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [enquiryVenue, setEnquiryVenue] = useState(null); // full venue object for in-modal enquiry

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape to close (only close compare if no enquiry form is open)
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') {
        if (enquiryVenue) setEnquiryVenue(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, enquiryVenue]);

  // Listen for Aura workspace "Enquire" button dispatches
  useEffect(() => {
    const handler = e => {
      const venue = venues.find(v => String(v.id) === String(e.detail?.venueId));
      if (venue) setEnquiryVenue(venue);
    };
    window.addEventListener('lwd:aura-enquire', handler);
    return () => window.removeEventListener('lwd:aura-enquire', handler);
  }, [venues]);

  // Fetch all venues
  useEffect(() => {
    if (!items.length) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      items.map(item =>
        fetchListingById(String(item.id)).catch(() => ({ id: item.id, name: item.name }))
      )
    ).then(results => {
      if (!cancelled) { setVenues(results); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [items]);

  // Compute highlight badges, best rating, best price
  const highlights = {};
  if (venues.length >= 2) {
    const ratings = venues.map((v, i) => ({ i, val: v?.rating ? Number(v.rating) : -1 }));
    const prices  = venues.map((v, i) => ({ i, val: numericPrice(v) }));
    const topRated  = ratings.reduce((a, b) => b.val > a.val ? b : a);
    const bestValue = prices.reduce((a, b) => b.val < a.val ? b : a);
    if (topRated.val  > 0)          highlights[topRated.i]  = 'Highest Rated';
    if (bestValue.val < Infinity && bestValue.i !== topRated.i) highlights[bestValue.i] = 'Most Affordable';
  }

  const count = items.length;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 960,
        background: 'rgba(0,0,0,0.97)',
        display: 'flex', flexDirection: 'column',
        animation: 'fadeIn 0.2s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* ── Header ── */}
      <div style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '28px 44px',
        borderBottom: `1px solid rgba(184,160,90,0.15)`,
        background: 'rgba(10,10,8,0.97)',
        backdropFilter: 'blur(20px)',
      }}>
        <div>
          <div style={{ fontFamily: FB, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: 8 }}>
            Venue Comparison
          </div>
          <div style={{ fontFamily: FD, fontSize: 26, color: '#fff', fontWeight: 400, letterSpacing: '0.01em' }}>
            {count === 1 ? 'Venue in Review' : `Comparing ${count} Venues`}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close comparison"
          style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.7)', fontSize: 20, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        >
          ×
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'clamp(16px, 4vw, 44px)' }}>
        {loading ? (
          <div style={{
            height: '100%', minHeight: 400,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
          }}>
            <div style={{ fontFamily: FD, fontSize: 20, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.04em' }}>
              Curating your comparison…
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {items.map((_, i) => (
                <div key={i} style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: 'rgba(184,160,90,0.5)',
                  animation: `dotPulse 1.4s ease-in-out ${i * 0.18}s infinite`,
                }} />
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${count}, minmax(260px, 1fr))`,
            border: `1px solid rgba(184,160,90,0.18)`,
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(0,0,0,0.75)',
          }}>
            {venues.map((venue, i) => (
              <CompareVenueColumn
                key={venue?.id || i}
                venue={venue}
                isLast={i === venues.length - 1}
                highlight={highlights[i] || null}
                C={C}
                onClose={onClose}
                onEnquire={(v) => setEnquiryVenue(v)}
              />
            ))}
          </div>
        )}

        {/* Footer note */}
        {!loading && (
          <div style={{ textAlign: 'center', marginTop: 28 }}>
            <span style={{ fontFamily: FB, fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
              Add up to 3 venues to compare · Click outside or press Esc to close
            </span>
          </div>
        )}
      </div>

      {/* ── Aura strip ── */}
      <div style={{
        flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.05)',
        padding: '13px 44px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(8,8,6,0.6)',
      }}>
        <button
          type="button"
          onClick={() => {
            // Set context, pass the full venue objects so the panel pins them
            setChatContext({ page: 'compare', country: venues[0]?.country || null, region: venues[0]?.destination || null, compareVenues: venues });
            // Open the full workspace, this is a decision environment, not a quick question
            openWorkspace();
            if (venues.length > 1) {
              // Only send the intro message once, don't repeat it every time the user reopens Aura
              const alreadySent = messages.some(m => m.from === 'user' && m.text?.startsWith("I'm comparing these venues:"));
              if (!alreadySent) {
                const venueSummaries = venues.map(v => {
                  const parts = [v.name];
                  if (v.type)                     parts.push(v.type);
                  if (v.destination || v.country) parts.push(v.destination || v.country);
                  if (v.capacity)                 parts.push(`up to ${v.capacity} guests`);
                  return parts.join(', ');
                });
                sendMessage(`I'm comparing these venues: ${venueSummaries.join(' · ')}. Can you help me understand which might be the better fit for our wedding?`);
              }
            }
          }}
          style={{
            background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'opacity 0.18s',
            opacity: 0.65,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.65'; }}
        >
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'rgba(211,154,33,0.12)',
            border: '1px solid rgba(211,154,33,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, color: '#d39a21', flexShrink: 0,
          }}>✦</span>
          <span style={{
            fontFamily: FB, fontSize: 12, color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.02em',
          }}>
            Not sure which to choose?{' '}
            <span style={{ color: '#d39a21' }}>Ask Aura →</span>
          </span>
        </button>
      </div>

      {/* ── Company footer ── */}
      <div style={{
        flexShrink: 0,
        borderTop: '1px solid rgba(184,160,90,0.1)',
        background: 'rgba(8,8,6,0.98)',
        backdropFilter: 'blur(12px)',
        padding: '16px 44px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 24,
      }}>
        {/* Left: wordmark + tagline */}
        <div>
          <div style={{ fontFamily: FD, fontSize: 14, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.04em', marginBottom: 3 }}>
            Luxury Wedding Directory
          </div>
          <div style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.02em' }}>
            The world's finest venues and vendors, carefully selected
          </div>
        </div>

        {/* Right: legal links + copyright */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Privacy', 'Terms', 'Cookies', 'Contact'].map(lbl => (
              <a
                key={lbl}
                href={`/${lbl.toLowerCase()}`}
                style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,0.28)', textDecoration: 'none', letterSpacing: '0.02em', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#d39a21'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.28)'}
              >
                {lbl}
              </a>
            ))}
          </div>
          <div style={{ fontFamily: FB, fontSize: 10, color: 'rgba(255,255,255,0.16)', letterSpacing: '0.01em' }}>
            &copy; {new Date().getFullYear()} Luxury Wedding Directory &middot; A brand of 5 Star Weddings Ltd
          </div>
        </div>
      </div>

      {/* In-modal enquiry form, dark single-page form, compare grid stays visible behind */}
      {enquiryVenue && (
        <CompareEnquiryForm
          venue={enquiryVenue}
          onClose={() => setEnquiryVenue(null)}
        />
      )}
    </div>
  );
}

// ─── COMPARE BAR ─────────────────────────────────────────────────────────────
function CompareBar({ items, onRemove, onClear, onCompare }) {
  const C = useT();
  if (!items.length) return null;
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 850,
      background: C.text, borderTop: `2px solid ${C.gold}`,
      padding: "12px 32px", display: "flex", alignItems: "center", gap: 12,
      animation: "slideUp 0.3s ease",
    }}>
      <span style={{ fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.6)", letterSpacing: "0.5px", textTransform: "uppercase", flexShrink: 0 }}>Compare:</span>
      {items.map(v => (
        <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
          <span style={{ fontFamily: FB, fontSize: 12, color: "#fff" }}>{v.name}</span>
          <button onClick={() => onRemove(v.id)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
        </div>
      ))}
      {items.length < 3 && (
        <div style={{ padding: "5px 12px", border: "1px dashed rgba(255,255,255,0.25)", fontFamily: FB, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>+ Add venue</div>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <button onClick={onClear} style={{ padding: "6px 14px", background: "none", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "var(--lwd-radius-input)", color: "rgba(255,255,255,0.6)", fontFamily: FB, fontSize: 12, cursor: "pointer" }}>Clear</button>
        <button onClick={onCompare} style={{ padding: "6px 20px", background: C.gold, border: "none", borderRadius: "var(--lwd-radius-input)", color: "#fff", fontFamily: FB, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Compare Now →</button>
      </div>
    </div>
  );
}

// ─── COOKIE BANNER (standalone, no ChatContext dependency) ──────────────────
const COOKIE_KEY = "lwd_cookies_accepted";
function VenueCookieBanner() {
  const C = useT();
  const [visible, setVisible]   = useState(false);
  const [entered, setEntered]   = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(COOKIE_KEY)) {
      const t = setTimeout(() => {
        setVisible(true);
        requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
      }, 900);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ all: true, ts: Date.now() }));
    setEntered(false);
    setTimeout(() => setVisible(false), 350);
  };

  const decline = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ essential: true, ts: Date.now() }));
    setEntered(false);
    setTimeout(() => setVisible(false), 350);
  };

  if (!visible) return null;

  return (
    <div style={{
      position:   "fixed",
      bottom:      0,
      left:        0,
      right:       0,
      zIndex:      1500,
      background:  "#0D0B09",
      borderTop:  "1px solid rgba(211,154,33,0.2)",
      padding:    "14px 28px",
      fontFamily:  FB,
      transform:   entered ? "translateY(0)" : "translateY(100%)",
      transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
      boxShadow:  "0 -4px 32px rgba(0,0,0,0.4)",
    }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 14,
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#d39a21", fontSize: 15 }}>✦</span> We use cookies
          </div>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", lineHeight: 1.55, margin: 0 }}>
            We use essential cookies to make our site work, and optional cookies to personalise your experience. By clicking{" "}
            <strong style={{ color: "rgba(255,255,255,0.65)" }}>Accept All</strong>, you agree to our use of cookies.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
          <button onClick={decline} style={{
            background: "none", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "var(--lwd-radius-input)",
            color: "rgba(255,255,255,0.55)", padding: "9px 18px",
            fontFamily: FB, fontSize: 11, cursor: "pointer", letterSpacing: "0.5px",
            transition: "all 0.2s", whiteSpace: "nowrap",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
          >Essential Only</button>
          <button onClick={accept} style={{
            background: "#d39a21", border: "none", borderRadius: "var(--lwd-radius-input)",
            color: "#0D0B09", padding: "9px 24px",
            fontFamily: FB, fontSize: 11, fontWeight: 700, cursor: "pointer",
            letterSpacing: "1.5px", textTransform: "uppercase", transition: "opacity 0.2s",
            whiteSpace: "nowrap",
          }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >Accept All</button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPARE LIST PERSISTENCE ─────────────────────────────────────────────────
// Stored in sessionStorage so it survives slug changes and page navigations
// within the same browser session.
const COMPARE_STORAGE_KEY = 'lwd_compare_list';

function loadCompareList() {
  try { return JSON.parse(sessionStorage.getItem(COMPARE_STORAGE_KEY)) || []; }
  catch { return []; }
}

function saveCompareList(list) {
  try { sessionStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(list)); }
  catch {}
}

// ─── SHARED LISTING PRESENTATION ─────────────────────────────────────────────
// VenueListingView, the visible listing content only (no nav, Aura, footer,
// modals, fetching or routing). ONE renderer, two data sources: the public page
// (VenueProfile) feeds it PUBLISHED data; Client Studio feeds it UNSAVED DRAFT.
// Reuses the exact same section components the public page uses, so there is no
// drift. `previewMode` = rendered inside the editor (no enquiry handlers).
// First version = the sections currently editable in Client Studio; extend the
// editor and this view together as new factual sections are added.
export function VenueListingView({ venue, previewMode = false, onEnquire = () => {}, onImageClick = null, onSectionClick = null }) {
  const [lightIdx, setLightIdx] = useState(null);
  if (!venue) return null;
  const isVenue = venue.categorySlug === "wedding-venues";
  const gallery = Array.isArray(venue.gallery) ? venue.gallery : [];
  const gLen = gallery.length || 1;
  // In the scaled side-preview (onImageClick set) a gallery click opens the full
  // popup, where the lightbox isn't trapped by the panel's CSS zoom.
  const openImage = (i) => (onImageClick ? onImageClick(i) : setLightIdx(i));
  // Wrap each section with a stable target id (preview-sync scroll target) and a
  // reverse-sync click that fires only on non-interactive whitespace.
  const sec = (id, node) => (
    <div data-preview-section={id} style={{ scrollMarginTop: 12, borderRadius: 8, transition: "box-shadow .35s ease, background-color .35s ease" }}
      onClick={onSectionClick ? (e) => { if (!e.target.closest("button,a,img,input,textarea,select,iframe,label")) onSectionClick(id); } : undefined}>
      {node}
    </div>
  );
  return (
    <div>
      {sec("hero", <><Hero venue={venue} heroStyle="cinematic" setHeroStyle={() => {}} onEnquire={onEnquire} /><StatsStrip venue={venue} nextEvent={null} onEventClick={() => {}} /></>)}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: previewMode ? "24px 22px 64px" : "48px 40px 120px" }}>
        {sec("about", <AboutSection venue={venue} isDbVenue={true} />)}
        {gallery.length > 0 && sec("gallery", <ImageGallery gallery={gallery} onOpenLight={openImage} />)}
        {Array.isArray(venue.videos) && venue.videos.length > 0 && sec("videos", <VideoGallery videos={venue.videos} venue={venue} />)}
        {isVenue && venue.exclusiveUse && sec("exclusiveUse", <ExclusiveUse venue={venue} onEnquire={onEnquire} />)}
        {isVenue && venue.catering && sec("catering", <CateringSection venue={venue} />)}
        {isVenue && Array.isArray(venue.spaces) && venue.spaces.length > 0 && sec("spaces", <SpacesSection spaces={venue.spaces} venue={venue} />)}
        {isVenue && sec("rooms", <RoomsSection venue={venue} />)}
        {isVenue && venue.dining && sec("dining", <DiningSection venue={venue} />)}
        {sec("contact", <ContactSection venue={venue} />)}
      </div>
      {gallery.length > 0 && (
        <Lightbox gallery={gallery} idx={lightIdx} setLightIdx={setLightIdx} onClose={() => setLightIdx(null)}
          onPrev={() => setLightIdx((i) => (i - 1 + gLen) % gLen)} onNext={() => setLightIdx((i) => (i + 1) % gLen)} />
      )}
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────
export default function VenueProfile({ onBack = null, slug = null, previewToken = null, countrySlug = null, regionSlug = null, categorySlug = null }) {
  // Always default to light mode on venue profiles, dark is opt-in via toggle
  const isMobile = useIsMobile();
  const [darkMode, setDarkMode] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lightIdx, setLightIdx] = useState(null);
  // Inside a private-review preview session the listing is sealed: the full nav
  // and breadcrumb (which link back out to the public site) are replaced with a
  // minimal review header, keeping the prospect inside the curated experience.
  const inPreviewSession = !!previewToken && typeof window !== 'undefined' && (() => {
    try { return !!sessionStorage.getItem('preview_session'); } catch { return false; }
  })();
  // Mobile sticky bar, revealed once user has scrolled 65% past the hero
  const heroRef = useRef(null);
  const [showMobileBar, setShowMobileBar] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const threshold = heroRef.current
        ? heroRef.current.offsetHeight * 0.65
        : window.innerHeight * 0.75;
      setShowMobileBar(window.scrollY > threshold);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [compareList, setCompareList] = useState(() => loadCompareList());
  const { isAuthenticated: isAdmin } = useAdminAuth();
  const setChatContext = useAura((s) => s.setChatContext);

  // Sync compareList to sessionStorage + notify GlobalCompare to update
  useEffect(() => {
    saveCompareList(compareList);
    window.dispatchEvent(new CustomEvent('lwd:compare-bar', { detail: { active: compareList.length > 0 } }));
  }, [compareList]);

  // Re-sync if GlobalCompare removes/clears items from the bar
  useEffect(() => {
    const handler = () => {
      const stored = loadCompareList();
      setCompareList(prev => {
        const prevIds = prev.map(v => String(v.id)).sort().join(',');
        const newIds  = stored.map(v => String(v.id)).sort().join(',');
        return prevIds === newIds ? prev : stored;
      });
    };
    window.addEventListener('lwd:compare-bar', handler);
    return () => window.removeEventListener('lwd:compare-bar', handler);
  }, []);
  const [heroStyle, setHeroStyle] = useState("cinematic");
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dbVenue, setDbVenue] = useState(null);
  const [rawListing, setRawListing] = useState(null);
  const [loading, setLoading] = useState(true);  // true until first DB load completes, prevents null flash
  const [notFound, setNotFound] = useState(false);
  const [viData, setViData] = useState(null); // venue_intelligence row
  const [venueEvents, setVenueEvents] = useState([]);
  const [drawerEvent, setDrawerEvent] = useState(null);
  usePrerenderReady(!loading && !!rawListing);

  // Sync heroStyle from saved hero_layout once DB data arrives.
  // Falls back to 'cinematic' when the chosen layout is 'video' but no
  // video URL was configured (prevents a blank hero).
  useEffect(() => {
    if (!dbVenue?.heroLayout) return;
    const saved = dbVenue.heroLayout;
    if (saved === 'video' && !dbVenue.video?.type) {
      setHeroStyle('cinematic');
    } else {
      setHeroStyle(saved);
    }
  }, [dbVenue?.heroLayout, dbVenue?.video?.type]);

  const C = darkMode ? DARK : LIGHT;
  // Ensure the canonical route slug is always on VV, dbVenue doesn't reliably
  // carry it, and the Try-On CTAs need a slug (not a UUID) for /fashion-studio/{slug}.
  const VV = dbVenue ? { ...VENUE, ...dbVenue, slug: dbVenue.slug || slug } : VENUE;

  // Record this venue visit for Recently Viewed tracking
  // Only record AFTER database has loaded with real data, not on mount with dummy data
  useEffect(() => {
    if (dbVenue && dbVenue.name && slug) {
      // Build the canonical URL from rawListing (not from window.location.pathname)
      //, the URL may not yet be canonicalized when this effect fires. Mirrors the
      // logic in the replaceState effect below so the stored path is always a
      // valid /<country>/<region>/<category>/<slug>.
      let canonicalPath = null;
      const cs = rawListing?.country_slug || rawListing?.countrySlug || countrySlug;
      let   rs = rawListing?.region_slug  || rawListing?.regionSlug  || regionSlug;
      const cat = rawListing?.category_slug || rawListing?.categorySlug || categorySlug || 'wedding-venues';
      if (!rs && rawListing?.region) rs = rawListing.region.toLowerCase().replace(/\s+/g, '-');
      if (cs && rs && cat) {
        // Same service as the canonical tag and the replaceState below — a stored path
        // built by hand would fail isCanonicalListingPath() for a region === country
        // listing and silently drop the recently-viewed entry.
        canonicalPath = listingProfileUrl({
          slug: slug.toLowerCase(),
          category_slug: cat.toLowerCase(),
          country_slug: cs.toLowerCase(),
          region_slug: rs.toLowerCase(),
        });
      }
      // Build a location fallback from URL-derived slugs so the entry is never
      // dropped by a downstream filter just because the DB lacks city/region.
      const titlecase = (s) => (s ? s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');
      const locationFallback = [titlecase(rs), titlecase(cs)].filter(Boolean).join(', ');
      recordVenueView(VV, slug, canonicalPath, locationFallback);
      // Track profile_view in unified event system (once per real data load)
      trackProfileView({
        entityType:    'venue',
        entityId:      dbVenue.id   || null,
        entityName:    dbVenue.name || null,
        slug,
        sourceSurface: 'venue_profile',
      });
      // Tag all subsequent tracker events with this listing context
      setListingContext(dbVenue.id || null, slug, 'venue');
      // Feed Aura with venue context + listingId so it can show this venue's images
      setChatContext({
        page:      'venue-profile',
        listingId: dbVenue.id   || null,
        country:   dbVenue.country   || countrySlug || null,
        region:    dbVenue.destination || dbVenue.region || regionSlug || null,
        venueInfo: [dbVenue.name, dbVenue.shortDescription || dbVenue.desc].filter(Boolean).join(', '),
      });
    }
    // Clear listing context when component unmounts or slug changes
    return () => clearListingContext();
  }, [slug, dbVenue, rawListing, countrySlug, regionSlug, categorySlug]);

  // Fetch venue intelligence, single source of truth for capacity + pricing
  useEffect(() => {
    if (!slug) return;
    fetchVenueIntelligence(slug).then(row => { if (row) setViData(row); });
  }, [slug]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        // Only update active tab from the topmost intersecting section
        const intersecting = entries.filter(e => e.isIntersecting);
        if (intersecting.length > 0) {
          // Pick the section closest to the top of the viewport
          const topmost = intersecting.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveTab(topmost.target.id);
        }
      },
      { rootMargin: '-137px 0px -60% 0px', threshold: 0 }
    );
    TABS.forEach(t => {
      const el = document.getElementById(t.key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let ignore = false;
    async function loadVenue() {
      if (!slug) return;
      setLoading(true);
      try {
        // Draft sales preview: a valid preview token renders the draft listing's
        // profile (token-gated) without publishing it; otherwise published-only.
        // The token comes from the `previewToken` prop (when VenueProfile is rendered
        // EMBEDDED inside /preview/<token>, where the token is in the path, not the
        // query) OR from a ?preview=<token> query param (public canonical URL).
        const pvToken = previewToken
          || (typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('preview')
            : null);
        const listing = pvToken
          ? await fetchListingForPreview(slug, pvToken)
          : await fetchListingBySlug(slug);
        if (ignore) return;
        if (!listing) { setNotFound(true); setLoading(false); return; }
        // All data is now camelCase from mapListingFromDb mapper
        const mapped = {
          // Wedding Intelligence capability signals, passed straight through
          // (camelCase from mapListingFromDb) so the public WeddingIntelligencePanel
          // and any capability-aware UI can read them off VV. The mapped object is a
          // hand-built reshape and does not spread ...listing, so these must be
          // forwarded explicitly or they are dropped.
          ceremonyCivil: listing.ceremonyCivil, ceremonyReligious: listing.ceremonyReligious, ceremonyOutdoor: listing.ceremonyOutdoor,
          exclusiveHire: listing.exclusiveHire, exclusiveUseEnabled: listing.exclusiveUseEnabled,
          accommodationOnSite: listing.accommodationOnSite, accommodationRoomsCount: listing.accommodationRoomsCount,
          cateringInHouse: listing.cateringInHouse, cateringExternalAllowed: listing.cateringExternalAllowed,
          cateringHalal: listing.cateringHalal, cateringKosher: listing.cateringKosher,
          corkagePolicy: listing.corkagePolicy, byoAlcoholAllowed: listing.byoAlcoholAllowed, barPolicy: listing.barPolicy,
          liveBandAllowed: listing.liveBandAllowed, djAllowed: listing.djAllowed, outdoorMusicAllowed: listing.outdoorMusicAllowed,
          musicCurfewIndoor: listing.musicCurfewIndoor, afterPartyAvailable: listing.afterPartyAvailable,
          fireworksPolicy: listing.fireworksPolicy, dronePolicy: listing.dronePolicy,
          inHousePlanner: listing.inHousePlanner, coordinatorOnSite: listing.coordinatorOnSite, externalPlannerAllowed: listing.externalPlannerAllowed,
          weddingFormat: listing.weddingFormat, welcomeEventCapability: listing.welcomeEventCapability,
          poolPartyCapability: listing.poolPartyCapability, beachPartyCapability: listing.beachPartyCapability,
          weddingBreakfastSpace: listing.weddingBreakfastSpace, farewellBrunchCapability: listing.farewellBrunchCapability,
          nearestAirport: listing.nearestAirport, airportTransferMinutes: listing.airportTransferMinutes,
          privateJetAirportNearby: listing.privateJetAirportNearby, helicopterTransferAvailable: listing.helicopterTransferAvailable,
          boatTransferAvailable: listing.boatTransferAvailable, shuttleServiceAvailable: listing.shuttleServiceAvailable, valetParking: listing.valetParking,
          id:        listing.id,
          name:      listing.name || VENUE.name,
          tagline:   listing.heroTagline || VENUE.tagline,
          location:  [listing.city, listing.region || listing.country]
            .filter(Boolean)
            .map(p => p.charAt(0).toUpperCase() + p.slice(1))
            .filter((v, i, a) => i === 0 || v.toLowerCase() !== a[i - 1].toLowerCase())
            .join(', ') || VENUE.location,
          country:   listing.country
            ? listing.country.charAt(0).toUpperCase() + listing.country.slice(1)
            : VENUE.country,
          priceFrom: listing.priceFrom || VENUE.priceFrom,
          priceCurrency: listing.priceCurrency || '£',
          capacity:  {
            ...VENUE.capacity,
            max:      listing.capacityMax || VENUE.capacity?.max,
            ceremony: listing.capacityMax || VENUE.capacity?.ceremony || null,
          },
          imgs:      (listing.imgs || []).map(i => i.src).filter(Boolean).slice(0, 10).map(u => imgUrl(u, { width: 1400, resize: 'contain' })),
          // Hero carousel: dedicated hero_images ONLY (never media_items/gallery).
          // Falls back to imgs[0] only as a single image if listing has no hero_images.
          heroImgs:  (() => {
            // Reject bare bucket paths (truthy but no filename → broken image)
            const isUsableUrl = (u) => u && !u.split('?')[0].endsWith('/') && (u.split('?')[0].split('/').pop() || '').length > 0;
            const fromHero = Array.isArray(listing.heroImages)
              ? listing.heroImages.map(h => (typeof h === 'string' ? h : (h?.url || h?.src))).filter(isUsableUrl)
              : [];
            if (fromHero.length) return fromHero.slice(0, 5);
            // Fallback 2: first image from gallery/media_items
            const firstImg = (listing.imgs || []).map(i => i.src || i.url).filter(Boolean)[0];
            if (firstImg) return [firstImg];
            // Fallback 3: card-level image fields
            if (listing.cardImage) return [listing.cardImage];
            if (listing.heroImage) return [listing.heroImage];
            return [];
          })().map(u => imgUrl(u, { width: 1600, resize: 'contain' })),
          verified:  !!listing.isVerified,
          venueType: { ...(VENUE.venueType || {}), features: listing.amenities || [], styles: listing.styles || [] },
          description: listing.shortDescription || listing.cardSummary || null,
          gallery: (listing.mediaItems || [])
            .filter(i => i.type !== 'video' && i.type !== 'virtual_tour' && i.visibility !== 'private')
            .sort((a, b) => {
              if (a.is_featured && !b.is_featured) return -1;
              if (!a.is_featured && b.is_featured) return 1;
              return (a.sort_order ?? 999) - (b.sort_order ?? 999);
            })
            .map(item => mapMediaItemToGalleryPhoto(item))
            .filter(item => item.src)
            .map(p => ({ ...p, src: imgUrl(p.src, { width: 1200, resize: 'contain' }) })) || VENUE.gallery,
          rating:  listing.rating ?? VENUE.rating,
          reviews: listing.reviewCount ?? VENUE.reviews,
          flag:    COUNTRY_FLAG[listing.country] || VENUE.flag,
          awards:  Array.isArray(listing.awards)
            ? listing.awards.map(a => typeof a === 'string' ? a : (a.award || a.title || a.issuer || '')).filter(Boolean)
            : [],
          // Full award objects preserved for the sidebar widget (year, issuer, icon, description)
          awardsList: Array.isArray(listing.awards)
            ? listing.awards
                .map(a => {
                  if (!a) return null;
                  if (typeof a === 'string') return { award: a };
                  return {
                    award:       a.award || a.title || '',
                    year:        a.year || '',
                    issuer:      a.issuer || '',
                    icon:        a.icon || '🏆',
                    description: a.description || '',
                  };
                })
                .filter(a => a && a.award)
            : [],
          press:   Array.isArray(listing.pressFeatures)
            ? listing.pressFeatures.map(p => typeof p === 'string' ? p : (p.outlet || p.title || '')).filter(Boolean)
            : [],
          // Full press objects preserved for the sidebar widget (outlet, year, title, url, logo_url)
          pressFeatures: Array.isArray(listing.pressFeatures)
            ? listing.pressFeatures
                .map(p => {
                  if (!p) return null;
                  if (typeof p === 'string') return { outlet: p };
                  return {
                    outlet:   p.outlet || '',
                    year:     p.year || '',
                    title:    p.title || '',
                    url:      p.url || '',
                    logo_url: p.logo_url || p.logoUrl || '',
                  };
                })
                .filter(p => p && (p.outlet || p.title))
            : [],
          videos:  Array.isArray(listing.mediaItems) ? buildVenueVideos(listing.mediaItems) : [],
          accommodation: (listing.roomsMaxGuests || listing.roomsTotal || listing.roomsDescription || listing.accommodationTypes?.length)
            ? {
                type:              listing.roomsAccommodationType || null,
                totalRooms:        listing.roomsTotal || null,
                totalSuites:       listing.roomsSuites || null,
                maxOvernightGuests:listing.roomsMaxGuests || null,
                maxGuests:         listing.roomsMaxGuests || null,
                minNightStay:      listing.roomsMinStay || null,
                exclusiveUse:      !!listing.roomsExclusiveUse,
                description:       listing.roomsDescription || null,
                images:            Array.isArray(listing.roomsImages)
                  ? listing.roomsImages
                      .map(img => typeof img === 'string'
                        ? { src: img, title: '' }
                        : { src: img.url || img.src || '', title: img.title || '' })
                      .filter(img => img.src)
                  : [],
                // Relational accommodation entities (public, active rows).
                entities: Array.isArray(listing.accommodationTypes)
                  ? listing.accommodationTypes.map(t => ({
                      id: t.id, name: t.name, accommodationType: t.accommodation_type, description: t.description,
                      roomCount: t.room_count, sleepsMax: t.sleeps_max, minStay: t.minimum_stay_nights,
                      exclusiveUse: !!t.exclusive_use_available, primaryImageUrl: t.primary_image_url,
                      facilities: Array.isArray(t.facilities) ? t.facilities : [],
                      gallery: (t.listing_accommodation_type_media || [])
                        .slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                        .map(m => m.image_url).filter(Boolean),
                    }))
                  : [],
              }
            : null,
          // Wellness & Pool, images from wellness-tagged media_items, copy from
          // section_intros.wellnessBody (no dedicated column needed).
          wellness: (() => {
            const imgs = (listing.mediaItems || [])
              .filter(m => (m.type === 'image' || m.image_type === 'image') && Array.isArray(m.tags) && m.tags.includes('wellness'))
              .map(m => ({ src: m.url, title: (m.caption && m.caption !== listing.name) ? m.caption : '' }))
              .filter(m => m.src);
            const si = (listing.sectionIntros && typeof listing.sectionIntros === 'object') ? listing.sectionIntros : {};
            const body = si.wellnessBody || '';
            return (imgs.length || body) ? { description: body, images: imgs, subtitle: si.wellness || '' } : null;
          })(),
          exclusiveUse: (listing.exclusiveUsePrice || listing.exclusiveUseDescription || listing.exclusiveUseEnabled != null)
            ? {
                enabled:     listing.exclusiveUseEnabled !== false,
                title:       listing.exclusiveUseTitle || 'Exclusive Use',
                subtitle:    listing.exclusiveUseSubtitle || '',
                from:        listing.exclusiveUsePrice || null,
                subline:     listing.exclusiveUseSubline || null,
                description: listing.exclusiveUseDescription || null,
                ctaText:     listing.exclusiveUseCtaText || 'Enquire About Exclusive Use',
                includes:    Array.isArray(listing.exclusiveUseIncludes) ? listing.exclusiveUseIncludes : [],
              }
            : null,
          // categorySlug falls back to the URL prop so /bridal-boutiques/tdr-bridal
          // always renders bridal-boutique-specific sections even if the DB row
          // has an empty category_slug field.
          categorySlug:          listing.categorySlug || listing.category_slug || categorySlug || null,
          countrySlug:           listing.countrySlug  || listing.country_slug  || countrySlug  || null,
          regionSlug:            listing.regionSlug   || listing.region_slug   || regionSlug   || null,
          // Bridal boutique feature flags, must be explicitly mapped because
          // snakeToCamel converts bridal_virtual_tryon → bridalVirtualTryon but
          // the section condition reads VV.bridal_virtual_tryon (snake_case key).
          bridal_virtual_tryon:  listing.bridalVirtualTryon  ?? false,
          bridal_starting_from:  listing.bridalStartingFrom  || '',
          // showcase_enabled (on the listing) and the showcase's own publish status can
          // disagree — see fetchListingBySlug (services/listings.ts), which is the one
          // place that now checks the real status. showcasePublished is only ever set
          // (true or false) when showcaseEnabled is true, so its absence here (undefined,
          // e.g. a preview-token fetch that doesn't run that check) falls back to the
          // flag alone rather than silently hiding a legitimate showcase link.
          /* showcaseUrl gated 2026-08-15: `showcaseEnabled` is the LISTING's flag and says nothing about whether the showcase row is published. Villa Nascosta had the flag on and a DRAFT showcase, so this expression emitted /showcase/villa-nascosta from the homepage, /italy and every card that rendered it, and every one 404'd. This exact expression existed in FIVE files; all five now ask the one gate, which consults the showcase's own state. */ 
          showcaseUrl:      slug && isPubliclyRoutable(`/showcase/${slug}`) ? `/showcase/${slug}` : null,
          specialties:         Array.isArray(listing.specialties) ? listing.specialties : [],
          serviceTier:         listing.serviceTier || listing.service_tier || '',
          budgetTier:          listing.priceRange  || listing.price_range  || '',
          primaryArchetype:    listing.primaryArchetype || listing.primary_archetype || '',
          secondaryArchetypes: Array.isArray(listing.secondaryArchetypes)
            ? listing.secondaryArchetypes
            : (Array.isArray(listing.secondary_archetypes) ? listing.secondary_archetypes : []),
          moodProfile:         Array.isArray(listing.moodProfile)  ? listing.moodProfile  : [],
          luxuryTier:          listing.luxuryTier  || listing.luxury_tier  || '',
          audienceFit:         Array.isArray(listing.audienceFit)  ? listing.audienceFit  : [],
          specialtiesBodyP1:   listing.specialtiesBodyP1 || '',
          specialtiesBodyP2:   listing.specialtiesBodyP2 || '',
          specialtiesImages:   Array.isArray(listing.specialtiesImages) ? listing.specialtiesImages : [],
          aestheticBodyP1:     listing.aestheticBodyP1 || '',
          aestheticBodyP2:     listing.aestheticBodyP2 || '',
          aestheticImages:     Array.isArray(listing.aestheticImages) ? listing.aestheticImages : [],
          // Planner-specific fields
          plannerServices:      Array.isArray(listing.plannerServices)   ? listing.plannerServices   : [],
          plannerApproaches:    Array.isArray(listing.plannerApproaches) ? listing.plannerApproaches : [],
          plannerPricingModel:  listing.plannerPricingModel  || '',
          plannerStartingFrom:  listing.plannerStartingFrom  || '',
          plannerTeamSize:      listing.plannerTeamSize      || '',
          plannerYearsActive:   listing.plannerYearsActive   || '',
          plannerWeddingsYear:  listing.plannerWeddingsYear  || '',
          plannerDestinations:  listing.plannerDestinations  || '',
          plannerPhilosophy:    listing.plannerPhilosophy    || '',
          // Entertainment Intelligence fields
          entSubTypes:           Array.isArray(listing.entSubTypes)           ? listing.entSubTypes           : [],
          entGenres:             Array.isArray(listing.entGenres)             ? listing.entGenres             : [],
          entCoverage:           Array.isArray(listing.entCoverage)           ? listing.entCoverage           : [],
          entFormat:             listing.entFormat             || '',
          entPerformanceLength:  listing.entPerformanceLength  || '',
          entStartingFrom:       listing.entStartingFrom       || '',
          entProductionProvided: Array.isArray(listing.entProductionProvided) ? listing.entProductionProvided : [],
          entWeddingTypes:       Array.isArray(listing.entWeddingTypes)       ? listing.entWeddingTypes       : [],
          entLearnFirstDance:    listing.entLearnFirstDance    ?? null,
          entRider:              listing.entRider              || '',
          entTeamSize:           listing.entTeamSize           || '',
          entStatement:          listing.entStatement          || '',
          // Photo & Film + universal relationship graph (camelised photo_/video_/media_/rel_).
          // dbVenue is hand-built (no ...listing spread), so these must be mapped explicitly.
          photoStyles:              Array.isArray(listing.photoStyles)              ? listing.photoStyles              : [],
          photoPortfolioCategories: Array.isArray(listing.photoPortfolioCategories) ? listing.photoPortfolioCategories : [],
          photoArtistStatement:     listing.photoArtistStatement   || '',
          videoFilmStyles:          Array.isArray(listing.videoFilmStyles)          ? listing.videoFilmStyles          : [],
          videoFilmPersonality:     Array.isArray(listing.videoFilmPersonality)     ? listing.videoFilmPersonality     : [],
          videoDeliverables:        Array.isArray(listing.videoDeliverables)        ? listing.videoDeliverables        : [],
          mediaStartingFrom:        listing.mediaStartingFrom      || '',
          mediaCoverageHours:       listing.mediaCoverageHours     || '',
          mediaTeamSize:            listing.mediaTeamSize          || '',
          mediaBasedIn:             listing.mediaBasedIn           || '',
          mediaTravelsTo:           listing.mediaTravelsTo         || '',
          mediaTurnaround:          listing.mediaTurnaround        || '',
          mediaDestinationsCovered: Array.isArray(listing.mediaDestinationsCovered) ? listing.mediaDestinationsCovered : [],
          mediaIdealWeddings:       Array.isArray(listing.mediaIdealWeddings)       ? listing.mediaIdealWeddings       : [],
          mediaPerfectFor:          Array.isArray(listing.mediaPerfectFor)          ? listing.mediaPerfectFor          : [],
          mediaHowIWork:            listing.mediaHowIWork          || '',
          relVenuesWorked:          Array.isArray(listing.relVenuesWorked)          ? listing.relVenuesWorked          : [],
          relPlannersWorkedWith:    Array.isArray(listing.relPlannersWorkedWith)    ? listing.relPlannersWorkedWith    : [],
          relPreferredPartners:     Array.isArray(listing.relPreferredPartners)     ? listing.relPreferredPartners     : [],
          relCollaborationStyle:    listing.relCollaborationStyle  || '',
          vendorStory:           listing.vendorStory            || '',
          vendorStoryTitle:      listing.vendorStoryTitle       || '',
          availabilityBody:      listing.availabilityBody       || '',
          pricingBody:           listing.pricingBody            || '',
          vendorStoryPhoto:      listing.vendorStoryPhoto       || '',
          vendorStoryPhotoSide:  listing.vendorStoryPhotoSide   || 'left',
          pricingPhoto:          listing.pricingPhoto           || '',
          pricingPhotoSide:      listing.pricingPhotoSide       || 'left',
          availabilityPhoto:     listing.availabilityPhoto      || '',
          availabilityPhotoSide: listing.availabilityPhotoSide  || 'left',
          fullDescription:  listing.description || null,
          readmoreEnabled:  !!listing.readmoreEnabled,
          openingHours: listing.openingHoursEnabled
            ? {
                enabled: true,
                hours:   listing.openingHours || {},
                // `note` removed: no editor surface writes opening_hours_note,
                // so this read was permanently null. Re-add when/if the editor
                // gains a Notes field for opening hours.
              }
            : null,
          responseTime: listing.contactProfile?.responseTime || null,
          responseRate: (() => {
            const rate = listing.contactProfile?.responseRate;
            return rate ? String(rate).replace('%', '') : null;
          })(),
          weddingsHosted: listing.weddingsHosted || null,
          // Owner card falls back to the venue's own name + contact details when no
          // dedicated contact_profile.name has been entered. This guarantees the
          // sidebar profile card always renders something useful.
          owner: (() => {
            const cp = listing.contactProfile || {};
            const hasNamedContact = !!cp.name;
            const fallbackName = listing.name || listing.venueName || null;
            const displayName  = cp.name || fallbackName;
            if (!displayName) return null;
            return {
              name:        displayName,
              title:       cp.title || (hasNamedContact ? null : 'Venue Team'),
              bio:         cp.bio || cp.about || null,
              photo:       cp.photo_url || cp.photoUrl || cp.photo || null,
              memberSince: cp.member_since || listing.memberSince || null,
              weddings:        cp.weddings_hosted   || null,
              yearsExperience: cp.years_experience  || null,
              languages:       cp.languages         || null,
              responseTime:    cp.response_time     || listing.responseTime || null,
              responseRate:    cp.response_rate     || null,
              verified:        cp.verified ?? listing.verified ?? true,
              // Contact-channel fallbacks so the card can show email/phone even
              // when the AI lookup didn't return a named contact.
              email:       cp.email   || listing.email   || null,
              phone:       cp.phone   || listing.phone   || null,
              whatsapp:    cp.whatsapp || null,
              website:     cp.website  || listing.website || null,
              social:      cp.social   || (() => {
                const s = {};
                ['instagram','facebook','linkedin','tiktok','twitter','pinterest','youtube'].forEach(k => {
                  if (cp[k]) s[k] = cp[k];
                });
                return Object.keys(s).length ? s : null;
              })(),
              isFallback:  !hasNamedContact,
            };
          })(),
          contact: {
            address: {
              line1:   listing.address  || '',
              city:    listing.city     || '',
              region:  listing.region   || '',
              postcode:listing.postcode || '',
              country: listing.country  || '',
            },
            phone:   listing.phone   || listing.contactProfile?.phone   || null,
            email:   listing.email   || listing.contactProfile?.email   || null,
            website: listing.website || listing.contactProfile?.website || null,
            social:  listing.contactProfile?.social || (() => {
              // Fallback: build social from flat keys on contact_profile (legacy data)
              const cp = listing.contactProfile;
              if (!cp) return null;
              const s = {};
              ['instagram','facebook','linkedin','tiktok','twitter','pinterest','youtube'].forEach(k => {
                if (cp[k]) s[k] = cp[k];
              });
              return Object.keys(s).length ? s : null;
            })(),
            responseMetrics: {
              averageResponseHours: null,
              responseRatePercent:  null,
              sameDayTypical: false,
            },
            addressFormatted: [listing.address, listing.city, [listing.postcode, listing.region].filter(Boolean).join(' '), listing.country].filter(Boolean).join(', '),
            mapQuery: [listing.city, listing.region, listing.country].filter(Boolean).join(',+').replace(/ /g, '+'),
          },
          catering: (() => {
            const cateringCards = listing.cateringCards || [];
            const cateringEnabled = listing.cateringEnabled;
            const hasCards = Array.isArray(cateringCards) && cateringCards.length > 0;
            if (!hasCards && cateringEnabled == null) return null;
            if (cateringEnabled === false) return null;
            return {
              enabled: true,
              cards:   Array.isArray(cateringCards) ? cateringCards : [],
              styles:  Array.isArray(listing.diningMenuStyles) ? listing.diningMenuStyles : [],
              dietary: Array.isArray(listing.diningDietary) ? listing.diningDietary : [],
            };
          })(),
          dining: (listing.diningStyle || listing.diningDescription || listing.diningChefName)
            ? {
                style:                  listing.diningStyle || null,
                chefName:               listing.diningChefName || null,
                inHouseCatering:        !!listing.diningInHouse,
                externalCateringAllowed:!!listing.diningExternal,
                menuStyles:             Array.isArray(listing.diningMenuStyles) ? listing.diningMenuStyles : [],
                dietaryOptions:         Array.isArray(listing.diningDietary) ? listing.diningDietary : [],
                drinksOptions:          Array.isArray(listing.diningDrinks) ? listing.diningDrinks : [],
                description:            listing.diningDescription || null,
                menuImages:             Array.isArray(listing.diningMenuImages)
                  ? listing.diningMenuImages.map(img =>
                      typeof img === 'string'
                        ? { src: img, title: '' }
                        : { src: img.url || img.src || '', title: img.title || '' }
                    ).filter(img => img.src)
                  : [],
              }
            : null,
          spaces: Array.isArray(listing.spaces) && listing.spaces.length > 0
            ? listing.spaces.map(s => ({
                id:          s.id          || s.name,
                name:        s.name        || '',
                type:        s.type        || '',
                description: s.description || '',
                img:         s.img         ? (s.img.startsWith('/') || s.img.startsWith('http') ? s.img : '/' + s.img) : null,
                capacityCeremony: s.capacityCeremony || null,
                capacityReception: s.capacityReception || null,
                capacityDining:   s.capacityDining   || null,
                capacityStanding: s.capacityStanding || null,
                indoor:     s.indoor    ?? false,
                covered:    s.covered   ?? false,
                accessible: s.accessible ?? false,
                floorPlanUrl: s.floorPlanUrl || null,
              }))
            : null,
          faq: (listing.faqCategories && Array.isArray(listing.faqCategories) && listing.faqCategories.length > 0)
            ? {
                enabled:       listing.faqEnabled !== false,
                title:         listing.faqTitle    || 'FAQs',
                subtitle:      listing.faqSubtitle || '',
                ctaEnabled:    listing.faqCtaEnabled !== false,
                ctaHeadline:   listing.faqCtaHeadline   || null,
                ctaSubtext:    listing.faqCtaSubtext     || null,
                ctaButtonText: listing.faqCtaButtonText || null,
                categories:    listing.faqCategories,
              }
            : null,
          // Editorial Content Layer, section intros + hero summary saved from
          // the Listing Studio's Editorial Content section. Each section header
          // below prefers `sectionIntros[key]` over its hardcoded fallback.
          sectionIntros: (listing.sectionIntros && typeof listing.sectionIntros === 'object')
            ? listing.sectionIntros
            : {},
          heroSummary: listing.heroSummary || null,
          // Quality & approval, drives tier badge + approval indicators on profile
          contentScore:         listing.contentQualityScore ?? null,
          editorialApproved:    listing.editorialApproved   ?? false,
          editorialFactChecked: listing.editorialFactChecked ?? false,
          editorialLastReviewedAt: listing.editorialLastReviewedAt || null,
          // Hero layout style chosen in Listing Studio (cinematic | split | magazine | video)
          heroLayout: listing.heroLayout || 'cinematic',
          // Parsed video embed for HeroVideo component ({ type, heroId, credit } or null)
          video: (() => {
            const v = parseHeroVideoUrl(listing.heroVideoUrl);
            if (!v) return v;
            const vids = Array.isArray(listing.videos) ? listing.videos : [];
            const media = Array.isArray(listing.mediaItems) ? listing.mediaItems
                        : Array.isArray(listing.media_items) ? listing.media_items : [];
            const mv = media.find(m => m && m.type === 'video');
            const credit = vids[0]?.credit || vids[0]?.videographer
                        || (mv && mv.show_credit !== false ? (mv.credit_name || mv.credit) : null)
                        || null;
            return { ...v, credit };
          })(),
          // Wedding Packages, structured multi-day offerings (max 5) saved by
          // the Listing Studio's PackagesSection. Without this read the
          // dedicated section on the public listing would silently stay empty.
          weddingPackages: Array.isArray(listing.weddingPackages)
            ? listing.weddingPackages.filter(p => p && (p.name || p.description || (Array.isArray(p.inclusions) && p.inclusions.length > 0)))
            : [],
          // Wedding Weekend, `weddingWeekendDays` from the editor maps onto the
          // existing `weddingWeekend.days` shape consumed by the WeddingWeekend
          // component. Without this block the entire section silently rendered
          // hardcoded GT_VENUE demo data instead of the saved listing data.
          weddingWeekend: (listing.weddingWeekendEnabled || (Array.isArray(listing.weddingWeekendDays) && listing.weddingWeekendDays.length > 0))
            ? {
                enabled:  listing.weddingWeekendEnabled !== false,
                subtitle: listing.weddingWeekendSubtitle || null,
                days:     Array.isArray(listing.weddingWeekendDays) ? listing.weddingWeekendDays : [],
              }
            : null,
          // Estate + Nearby experiences, saved as two separate arrays in the
          // editor (`estate_items`, `nearby_items`), but the front-end component
          // expects a single `experiences` array tagged with `category`. We map
          // editor item shape `{id, icon, title, status, note}` → component
          // shape `{id, kind, label, isIncluded, isPrivate, season}`. `status`
          // values "included" / "private" become the boolean flags.
          experiences: (() => {
            const mapItem = (it, category) => ({
              id:         it.id,
              category,
              kind:       it.icon || 'nature',
              label:      it.title || '',
              isIncluded: (it.status || '').toLowerCase() === 'included',
              isPrivate:  (it.status || '').toLowerCase() === 'private',
              season:     it.note || undefined,
            });
            const estate = Array.isArray(listing.estateItems)
              ? listing.estateItems.filter(i => i?.title).map(it => mapItem(it, 'estate'))
              : [];
            const nearby = Array.isArray(listing.nearbyItems)
              ? listing.nearbyItems.filter(i => i?.title).map(it => mapItem(it, 'nearby'))
              : [];
            return [...estate, ...nearby];
          })(),
          estateEnabled: listing.estateEnabled !== false,
          nearbyEnabled: listing.nearbyEnabled !== false,
          // Publishing dates, WordPress-style editorial timestamp
          publishedAt: listing.publishedAt || null,
          updatedAt:   listing.updatedAt   || null,
        };
        // ── Fetch approved reviews and map to testimonials format ─────────────
        let testimonials = [];
        try {
          const reviews = await fetchApprovedReviews('venue', listing.id);
          testimonials = (reviews || []).map(r => ({
            id:       r.id,
            names:    r.reviewer_name,
            avatar:   r.reviewer_name
                        .split(' ')
                        .filter(w => /^[A-Za-z]/.test(w))
                        .map(w => w[0].toUpperCase())
                        .slice(0, 2)
                        .join(''),
            date:     r.event_date
                        ? new Date(r.event_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
                        : new Date(r.published_at || r.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
            location: r.reviewer_location || '',
            rating:   Number(r.overall_rating),
            text:     r.review_text,
            verified: r.is_verified,
          }));
        } catch (reviewErr) {
          console.warn('[VenueProfile] Reviews fetch failed silently:', reviewErr);
        }
        mapped.testimonials = testimonials;

        if (!ignore) {
          setDbVenue(mapped);
          setRawListing(listing);
        }
      } catch (err) {
        console.error('Failed to load venue by slug', err);
        if (!ignore) setNotFound(true);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    loadVenue();
    return () => { ignore = true; };
  }, [slug, previewToken]);

  // Fetch upcoming events linked to this listing
  useEffect(() => {
    if (!VV.id) return;
    fetchUpcomingEventsForVenue(VV.id, 6).then(evts => setVenueEvents(evts || []));
  }, [VV.id]);

  // Sync URL bar to canonical format (no reload, no redirect).
  // Skipped inside a preview session: the listing renders within the sealed
  // /preview/<token> experience and the public URL must never be shown.
  useEffect(() => {
    if (inPreviewSession) return;
    if (!rawListing || !slug) return;
    const cs  = rawListing.country_slug  || rawListing.countrySlug  || countrySlug;
    let rs  = rawListing.region_slug   || rawListing.regionSlug   || regionSlug;
    const cat = rawListing.category_slug || rawListing.categorySlug || categorySlug || 'wedding-venues';
    if (!rs && rawListing.region) rs = rawListing.region.toLowerCase().replace(/\s+/g, '-');
    if (!cs || !rs || !cat) return;
    // Same service as the canonical tag, so the address bar and the <link> can never
    // disagree. Hand-rolling it here is what pushed /gibraltar/gibraltar/… into the URL.
    const canonical = listingProfileUrl({
      slug: slug.toLowerCase(),
      category_slug: cat.toLowerCase(),
      country_slug: cs.toLowerCase(),
      region_slug: rs.toLowerCase(),
    });
    if (canonical && window.location.pathname !== canonical) {
      window.history.replaceState(null, '', canonical);
    }
  }, [rawListing, slug, countrySlug, regionSlug, categorySlug]);

  const scrollToSection = (key) => {
    setActiveTab(key);
    const el = document.getElementById(key);
    if (!el) return;
    // Measure the tab nav bar height directly (it carries the combined sticky offset)
    // The tab nav sits at top:56, so its bottom = 56 + its own height
    const tabNavEl = document.querySelector('[data-tab-nav]');
    const tabNavBottom = tabNavEl ? tabNavEl.getBoundingClientRect().bottom : 116;
    const offset = Math.max(tabNavBottom, 137) + 16;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const addCompare = () => {
    if (compareList.find(v => v.id === VV.id)) return; // already added
    if (compareList.length >= 3) {
      // At max, open the global compare modal
      window.dispatchEvent(new CustomEvent('lwd:open-compare-modal'));
      return;
    }
    setCompareList(l => {
      const updated = [...l, { id: VV.id, name: VV.name }];
      trackCompareAdd({
        venueId:       VV.id,
        venueName:     VV.name,
        compareList:   l,
        sourceSurface: 'venue_profile',
      });
      return updated;
    });
  };

  if (slug && loading) return (
    <Theme.Provider value={C}>
      <GlobalStyles />
      <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontFamily: FB, fontSize: 14 }}>
        Loading venue…
      </div>
    </Theme.Provider>
  );

  if (slug && notFound) return (
    <Theme.Provider value={C}>
      <GlobalStyles />
      <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: C.text, fontFamily: FB }}>
        <div style={{ fontSize: 22, fontFamily: FD }}>Venue Not Found</div>
        {onBack && <button onClick={onBack} style={{ border: 'none', background: 'none', color: C.gold, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>← Back</button>}
      </div>
    </Theme.Provider>
  );

  return (
    <Theme.Provider value={C}>
      <GlobalStyles />
      {/* SEO head - only when real listing data is loaded */}
      {rawListing && (() => {
        const cs  = rawListing.country_slug  || rawListing.countrySlug  || countrySlug;
        const rs  = rawListing.region_slug   || rawListing.regionSlug   || regionSlug;
        const cat = rawListing.category_slug || rawListing.categorySlug || categorySlug || 'wedding-venues';
        // Hand-rolled this path and so missed the region === country collapse that the
        // sitemap, the edge 301 and listingUrl.js all apply — Gibraltar and an
        // "italy/italy" data slip therefore served a canonical of /gibraltar/gibraltar/…
        // which 301s straight back to this page. THE canonical-URL service owns the
        // shape; never rebuild it inline (see utils/listingUrl.js).
        // Fallback (should never fire — Population B, a published wedding-venues listing
        // with no country_slug, was confirmed empty 2026-08-02): the category hub, a page
        // guaranteed to exist, not the bare-slug shape the router 301s away from.
        const canonicalPath = listingProfileUrl({
          slug, category_slug: cat, country_slug: cs, region_slug: rs,
        }) || (cs ? `/${cs}/${cat}` : `/${cat}`);
        const canonicalUrl = `${SITE_URL}${canonicalPath}`;
        // Breadcrumbs need the same collapse, or the region crumb points at
        // /gibraltar/gibraltar and the trail contradicts the canonical.
        const hasRealRegion = !!(cs && rs && rs !== cs);
        const countryUrl  = cs ? `/${cs}` : '/';
        const regionUrl   = hasRealRegion ? `/${cs}/${rs}` : countryUrl;
        const categoryUrl = cs ? (hasRealRegion ? `/${cs}/${rs}/${cat}` : `/${cs}/${cat}`) : '/';
        const catLabel = cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const locationLabel = [rawListing.region || rs, rawListing.country || cs]
          .filter(Boolean).join(', ');
        const fallbackTitle = rawListing.name
          + (locationLabel ? ` | ${catLabel} in ${locationLabel}` : ` | ${catLabel}`)
          + ' | LWD';
        const fallbackDesc = (() => {
          const base = rawListing.shortDescription || rawListing.cardSummary || rawListing.description || '';
          if (base) return base.replace(/<[^>]+>/g, '').slice(0, 155).trim();
          return `Discover ${rawListing.name}, a luxury ${catLabel.toLowerCase()} in ${locationLabel}. Enquire now via Luxury Wedding Directory.`;
        })();
        return (
          <>
            <SeoHead
              title={rawListing.seoTitle || fallbackTitle}
              description={rawListing.seoDescription || fallbackDesc}
              keywords={rawListing.seoKeywords}
              canonicalUrl={canonicalUrl}
              ogImage={rawListing.heroImage || rawListing.imgs?.[0]?.src || rawListing.imgs?.[0]}
            />
            <JsonLd schema={buildVenueSchema(rawListing)} />
            <JsonLd schema={buildBreadcrumbSchema([
              { name: 'Home', url: '/' },
              // titleCase the SLUG fallbacks. Without it a listing lacking a stored
              // country/region string rendered its raw slug as the crumb name, so
              // trails read "england" and "north-yorkshire" in structured data. Same
              // raw-slug-as-label defect as the editorial <h1>.
              { name: titleCaseSlug(rawListing.country) || titleCaseSlug(cs) || 'Venues', url: countryUrl },
              ...(rs ? [{ name: titleCaseSlug(rawListing.region) || titleCaseSlug(rs), url: regionUrl }] : []),
              { name: cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), url: categoryUrl },
              { name: rawListing.name, url: canonicalUrl },
            ])} />
            {rawListing.faqEnabled !== false && rawListing.faqCategories && rawListing.faqCategories.length > 0 && (
              <JsonLd schema={buildFaqSchema(rawListing.faqCategories)} />
            )}
          </>
        );
      })()}
      <div className="vp-root" style={{ background: C.bg, minHeight: "100vh", color: C.text }}>
        {inPreviewSession
          ? <SealedReviewHeader onBack={onBack} />
          : <HomeNav hasHero={true} darkMode={darkMode} onToggleDark={() => setDarkMode(d => !d)} />}
        <div ref={heroRef} style={{ position: 'relative' }}>
          <Hero venue={VV} heroStyle={heroStyle} setHeroStyle={setHeroStyle} onEnquire={() => setEnquiryOpen(true)} onBack={onBack} />
          {/* Breadcrumb links out to public country/region/category pages, so it
              is hidden inside a sealed preview session. */}
          {!inPreviewSession && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
              <BreadcrumbBar venue={VV} />
            </div>
          )}
        </div>
        <StatsStrip venue={VV} nextEvent={venueEvents[0] || null} onEventClick={setDrawerEvent} />
        {IS_VENUE(VV) && <WeddingIntelligencePanel venue={VV} />}
        <StickyTabNav venue={VV} activeTab={activeTab} onTabClick={scrollToSection} saved={saved} setSaved={setSaved} onAddCompare={addCompare} compareList={compareList} />

        {/* Main layout */}
        <div className="vp-main-wrapper" style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 40px 120px" }}>
          <div className="vp-main-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 56, alignItems: "start" }}>
            {/* Content */}
            <div style={{ minWidth: 0, overflow: "hidden" }}>
              <AboutSection venue={VV} isDbVenue={!!slug} />

              {/* ── About the Vendor (personal story) ────────────────── */}
              {VV.vendorStory && (
                <section id="about-vendor" style={{ marginBottom: 56 }}>
                  <SectionHeading title={VV.vendorStoryTitle || 'Meet the Team'} />
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile
                      ? 'column'
                      : VV.vendorStoryPhoto
                        ? (VV.vendorStoryPhotoSide === 'right' ? 'row-reverse' : 'row')
                        : 'column',
                    gap: VV.vendorStoryPhoto ? 32 : 0,
                    alignItems: 'flex-start',
                  }}>
                    {VV.vendorStoryPhoto && (
                      <img
                        src={vpImg(VV.vendorStoryPhoto, 800)}
                        alt={`About ${VV.name}`}
                        style={{
                          flexShrink: 0,
                          width: 'clamp(160px, 35%, 280px)',
                          aspectRatio: '3/4',
                          objectFit: 'cover',
                          borderRadius: 3,
                          display: 'block',
                        }}
                      />
                    )}
                    <p style={{ fontFamily: FB, fontSize: 15, color: C.textMid, lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap', flex: 1 }}>{VV.vendorStory}</p>
                  </div>
                </section>
              )}

              <ImageGallery gallery={VV.gallery} onOpenLight={i => setLightIdx(i)} />
              {VV.videos && VV.videos.length > 0 && <VideoGallery videos={VV.videos} venue={VV} />}

              {/* ── Pricing / Offers text ─────────────────────────────── */}
              {VV.pricingBody && (
                <section id="pricing" style={{ marginBottom: 56 }}>
                  <SectionHeading title="Pricing &amp; Offers" subtitle="Investment and what is included." />
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile
                      ? 'column'
                      : VV.pricingPhoto
                        ? (VV.pricingPhotoSide === 'right' ? 'row-reverse' : 'row')
                        : 'column',
                    gap: VV.pricingPhoto ? 32 : 0,
                    alignItems: 'flex-start',
                  }}>
                    {VV.pricingPhoto && (
                      <img
                        src={vpImg(VV.pricingPhoto, 800)}
                        alt="Pricing"
                        style={{
                          flexShrink: 0,
                          width: 'clamp(160px, 35%, 280px)',
                          aspectRatio: '3/4',
                          objectFit: 'cover',
                          borderRadius: 3,
                          display: 'block',
                        }}
                      />
                    )}
                    <p style={{ fontFamily: FB, fontSize: 15, color: C.textMid, lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap', flex: 1 }}>{VV.pricingBody}</p>
                  </div>
                </section>
              )}

              {/* ── Venue-only sections ───────────────────────────────── */}
              {VV.categorySlug === 'wedding-venues' && <ExclusiveUse venue={VV} onEnquire={() => setEnquiryOpen(true)} />}

              {/* ── Venue Intelligence (venue-only AI blocks) ─────────── */}
              {VV.categorySlug === 'wedding-venues' && viData && (
                <>
                  <ShowcaseVerified
                    data={viData}
                    accentColor={C.gold}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                  <ShowcaseAtAGlance
                    data={viData}
                    accentColor={C.gold}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                  <ShowcasePricing
                    data={viData}
                    venueName={VV.name}
                    accentColor={C.gold}
                    theme={darkMode ? 'dark' : 'light'}
                    bg={darkMode ? undefined : '#faf9f6'}
                  />
                </>
              )}

              {VV.categorySlug === 'wedding-venues' && <WeddingPackagesSection venue={VV} onEnquire={() => setEnquiryOpen(true)} />}
              {VV.categorySlug === 'wedding-venues' && <CateringSection venue={VV} />}
              {VV.categorySlug === 'wedding-venues' && VV.spaces && <SpacesSection spaces={VV.spaces} venue={VV} />}
              {VV.categorySlug === 'wedding-venues' && <RoomsSection venue={VV} />}
              {VV.categorySlug === 'wedding-venues' && VV.dining && <DiningSection venue={VV} />}
              {VV.categorySlug === 'wedding-venues' && VV.wellness && <WellnessSection venue={VV} />}

              {/* ── Vendor-only sections ──────────────────────────────── */}
              {VV.categorySlug === 'wedding-planners' && <PlannerServicesSection venue={VV} />}
              {VV.categorySlug === 'entertainment'    && <EntertainmentIntelligence venue={VV} />}
              {(VV.categorySlug === 'photographers' || VV.categorySlug === 'videographers') && <PhotographyIntelligence venue={VV} />}
              {VV.categorySlug === 'bridal-boutiques' && VV.bridal_virtual_tryon && <div id="virtual"><BridalTryOnSection venue={VV} /></div>}
              {VV.categorySlug !== 'wedding-venues' && <VendorSpecialtiesSection venue={VV} />}

              {/* ── Venue-only classification + weekend sections ──────── */}
              {VV.categorySlug === 'wedding-venues' && <VenueTypeSection venue={VV} />}
              {VV.categorySlug === 'wedding-venues' && <WeddingWeekend venue={VV} />}

              {/* ── Availability text ─────────────────────────────────── */}
              {VV.availabilityBody && (
                <section id="availability" style={{ marginBottom: 56 }}>
                  <SectionHeading title="Availability" subtitle="When we take bookings and how to secure your date." />
                  <div style={{
                    display: 'flex',
                    flexDirection: VV.availabilityPhoto
                      ? (VV.availabilityPhotoSide === 'right' ? 'row' : 'row-reverse')
                      : 'column',
                    gap: VV.availabilityPhoto ? 32 : 0,
                    alignItems: 'flex-start',
                  }}>
                    {VV.availabilityPhoto && (
                      <img
                        src={vpImg(VV.availabilityPhoto, 800)}
                        alt="Availability"
                        style={{
                          flexShrink: 0,
                          width: 'clamp(160px, 35%, 280px)',
                          aspectRatio: '3/4',
                          objectFit: 'cover',
                          borderRadius: 3,
                          display: 'block',
                        }}
                      />
                    )}
                    <p style={{ fontFamily: FB, fontSize: 15, color: C.textMid, lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap', flex: 1 }}>{VV.availabilityBody}</p>
                  </div>
                </section>
              )}

              {/* ── All listings ─────────────────────────────────────── */}
              <ContactSection venue={VV} />
              {VV.categorySlug === 'wedding-venues' && VV.access && Array.isArray(VV.access.airports) && VV.access.airports.length > 0 && <GettingHere access={VV.access} />}
              {venueEvents.length > 0 && (
                <div id="events" style={{ marginBottom: 48 }}>
                  <div style={{ fontFamily: 'var(--font-heading-primary)', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#d39a21', marginBottom: 8 }}>Open Days &amp; Events</div>
                  <h2 style={{ fontFamily: 'var(--font-heading-primary)', fontSize: 26, fontWeight: 400, margin: '0 0 6px', color: C.text }}>Join Us in Person</h2>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: C.muted, margin: '0 0 24px', lineHeight: 1.6 }}>
                    Experience {VV.name} first-hand. Register for an upcoming open day, private tour, or virtual showcase.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                    {venueEvents.map(ev => {
                      const dateStr = formatEventDate(ev.startDate);
                      const timeStr = ev.startTime ? formatEventTime(ev.startTime) : null;
                      // Urgency signal: capacity-based or default
                      const spotsLeft = ev.capacity
                        ? Math.max(0, ev.capacity - (ev.bookingCount || 0))
                        : null;
                      const isAlmostFull = spotsLeft !== null && spotsLeft <= Math.ceil(ev.capacity * 0.25);
                      const urgencyLabel = isAlmostFull
                        ? `${spotsLeft} place${spotsLeft === 1 ? '' : 's'} remaining`
                        : ev.capacity
                          ? `Limited to ${ev.capacity} guests`
                          : 'Limited availability';
                      return (
                        <div
                          key={ev.id}
                          onClick={() => setDrawerEvent(ev)}
                          style={{ cursor: 'pointer', background: '#1a1a18', border: '1px solid #2a2a28', borderRadius: 4, overflow: 'hidden', transition: 'border-color 0.2s, transform 0.2s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#d39a21'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a28'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                          {ev.coverImageUrl && (
                            <div style={{ height: 160, overflow: 'hidden', position: 'relative' }}>
                              <img src={vpImg(ev.coverImageUrl)} alt={ev.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              {/* Urgency badge overlay */}
                              <div style={{
                                position: 'absolute', bottom: 10, right: 10,
                                background: isAlmostFull ? 'rgba(239,68,68,0.88)' : 'rgba(0,0,0,0.62)',
                                border: isAlmostFull ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(4px)',
                                padding: '3px 8px', borderRadius: 2,
                                fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: '#fff',
                              }}>
                                {urgencyLabel}
                              </div>
                            </div>
                          )}
                          {!ev.coverImageUrl && (
                            <div style={{
                              height: 48, background: '#141412',
                              borderBottom: '1px solid #2a2a28',
                              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                              padding: '0 14px',
                            }}>
                              <div style={{
                                background: isAlmostFull ? 'rgba(239,68,68,0.2)' : 'rgba(211,154,33,0.12)',
                                border: `1px solid ${isAlmostFull ? 'rgba(239,68,68,0.4)' : 'rgba(211,154,33,0.3)'}`,
                                padding: '3px 8px', borderRadius: 2,
                                fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: isAlmostFull ? '#fca5a5' : '#d39a21',
                              }}>
                                {urgencyLabel}
                              </div>
                            </div>
                          )}
                          <div style={{ padding: '16px 18px' }}>
                            <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: '#d39a21', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                              {ev.eventType?.replace(/_/g, ' ') || 'Event'}
                              {ev.isVirtual && <span style={{ marginLeft: 8, color: '#93c5fd' }}>· Virtual</span>}
                            </div>
                            <div style={{ fontFamily: 'var(--font-heading-primary)', fontSize: 17, fontWeight: 400, color: '#f0ece4', marginBottom: 4, lineHeight: 1.3 }}>{ev.title}</div>
                            {ev.subtitle && <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#999', marginBottom: 8, lineHeight: 1.5 }}>{ev.subtitle}</div>}
                            <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#666', marginTop: 8 }}>
                              {dateStr}{timeStr ? ` · ${timeStr}` : ''}
                              {ev.isFree === false && ev.ticketPrice && (
                                <span style={{ marginLeft: 10, color: '#d39a21' }}>From £{ev.ticketPrice}</span>
                              )}
                            </div>
                            <div style={{
                              marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              paddingTop: 12, borderTop: '1px solid #2a2a28',
                            }}>
                              <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: '#d39a21', letterSpacing: '0.1em', textTransform: 'uppercase', borderBottom: '1px solid #d39a21', paddingBottom: 1 }}>
                                {ev.bookingMode === 'external' ? 'Book Now →' : 'Reserve Your Place →'}
                              </div>
                              {ev.isFree !== false && (
                                <div style={{ fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#555', background: '#242420', padding: '3px 7px', borderRadius: 2 }}>
                                  Complimentary
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {VV.testimonials && Array.isArray(VV.testimonials) && VV.testimonials.length > 0 && <Reviews testimonials={VV.testimonials} venue={VV} venueSlug={slug} />}
              {dbVenue && dbVenue.id && (() => {
                const reviewEntityType = VV.categorySlug === 'wedding-venues'
                  ? 'venue'
                  : VV.categorySlug === 'wedding-planners'
                    ? 'planner'
                    : 'vendor';
                return (
                  <ReviewsSection
                    entityType={reviewEntityType}
                    entityId={dbVenue.id}
                    onOpenReviewForm={() => setShowReviewForm(true)}
                  />
                );
              })()}
              <FAQSection venue={VV} onAsk={() => setEnquiryOpen(true)} />
              <FeaturedInEdit listingId={VV.id} />
              {/* Latest Publication, inline on mobile only (sidebar is hidden < 900px) */}
              <div className="lwd-pub-inline" style={{ display: 'none' }}>
                <PublicationModule listingId={VV.id} listingSlug={VV.slug} listingName={VV.name} source="listing_mobile_inline" C={C} />
              </div>
              <SimilarVenues venue={VV} />
              <RecentlyViewed venue={VV} />
            </div>
            {/* Sidebar, 4 zones, sticky on desktop */}
            <div className="lwd-sidebar" style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 108, alignSelf: "start" }}>
              {/* Zone 1, Owner card (always renders if any name available, falls back to venue name) */}
              {VV.owner && VV.owner.name && <OwnerCard owner={VV.owner} venue={VV} />}
              {/* Zone 1b, Press Features (As Featured In), sidebar widget */}
              {Array.isArray(VV.pressFeatures) && VV.pressFeatures.length > 0 && (
                <PressSidebarCard items={VV.pressFeatures} />
              )}
              {/* Zone 1c, Awards & Recognition, sidebar widget */}
              {Array.isArray(VV.awardsList) && VV.awardsList.length > 0 && (
                <AwardsSidebarCard items={VV.awardsList} />
              )}
              {/* Zone 2, Venue enquiry form (lead gen) */}
              <VenueEnquiryForm
                listingId={VV.id}
                venueId={VV.id}
                vendorId={VV.vendorId || null}
                vendorName={VV.name}
                vendorEmail={VV.contactProfile?.email || VV.contact?.email || null}
                venueName={VV.name}
                responseTime={VV.responseTime}
                sticky={false}
                entityType={VV.listingType || (VV.categorySlug === 'wedding-venues' ? 'venue' : VV.categorySlug === 'wedding-planners' ? 'planner' : 'vendor')}
              />
              {/* Zone 2b, Latest Publication, directly below the enquiry (self-hides if none) */}
              <PublicationModule listingId={VV.id} listingSlug={VV.slug} listingName={VV.name} source="listing_sidebar" C={C} />
              {/* Zone 3, Lead form (scrolls naturally) */}
              <LeadForm venue={VV} />
              {/* Zone 4, Mini map + quick contact */}
              <SidebarContact venue={VV} />
              {/* Zone 5, Venue notices (open days, offers, late availability, news) */}
              <SidebarNotices notices={VV.notices} venueName={VV.name} />
              {/* Zone 6, Instagram teaser (placeholder, to be connected to live feed) */}
              {/* <SidebarInstagram venue={VV} /> */}
            </div>
          </div>
        </div>

        <MobileLeadBar venue={VV} show={showMobileBar} />
        <Lightbox gallery={VV.gallery} idx={lightIdx} setLightIdx={setLightIdx} onClose={() => setLightIdx(null)} onPrev={() => setLightIdx(i => (i - 1 + (VV.gallery?.length || 1)) % (VV.gallery?.length || 1))} onNext={() => setLightIdx(i => (i + 1) % (VV.gallery?.length || 1))} engagement={VV.engagement?.photos} />
        {enquiryOpen && <LuxeEnquiryModal venue={VV} onClose={() => setEnquiryOpen(false)} entityType={VV.listingType || (VV.categorySlug === 'wedding-venues' ? 'venue' : VV.categorySlug === 'wedding-planners' ? 'planner' : 'vendor')} />}
        {showReviewForm && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 20,
            }}
            onClick={() => setShowReviewForm(false)}
          >
            <div
              style={{
                background: C.bg,
                borderRadius: 8,
                padding: 40,
                maxWidth: 600,
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
              }}
              onClick={e => e.stopPropagation()}
            >
              {dbVenue && dbVenue.id && (
                <ReviewSubmitForm
                  entityType={VV.categorySlug === 'wedding-venues' ? 'venue' : VV.categorySlug === 'wedding-planners' ? 'planner' : 'vendor'}
                  entityId={dbVenue.id}
                  onSubmitSuccess={() => {
                    setShowReviewForm(false);
                  }}
                  onCancel={() => setShowReviewForm(false)}
                />
              )}
            </div>
          </div>
        )}
        <VenueCookieBanner />
        <EventDrawer event={drawerEvent} onClose={() => setDrawerEvent(null)} darkMode={darkMode} />

      </div>
    </Theme.Provider>
  );
}
